import {v4 as uuid} from 'uuid';
import * as V2 from './v2_interfaces';
import {IBuildSession, ISession, OnBuildSessionUpdate, OnConflict, FinanceParameters, FinanceDetails, SummaryDetails, Item, Price as BasicPrice, Action, UpdateSummary, ConflictDetails, ConflictResolution, OnAttachChoice, Blacklist, processLogic, ZL_ACCESSORIES, ZL_OPTIONS, OnShortCode, SaveData, ManagableSaveData, formatPrice, ShareTarget, ZL_BUNDLES, ItemType, GetAuthFunc, GetSharingData} from './api';
import {save as GenerateShortCode} from './shortcode';

declare global{
	interface Window { overrideVehicles: any}
}
const noop = () => {};
const asyncnoop = async () => {}
const undefinednoop = () => undefined
const parseDate = (date: string) => {
	const [year, month, day] = date.includes("/") ? date.split("/") : date.split("-");
	return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).valueOf();
}

const parseBool = (value: string | null | undefined, defaultVal?: boolean) : boolean | undefined => {
	if(value === null || value === undefined) {
		return defaultVal;
	}
	if(value === "") {
		return !defaultVal;
	}
	return ["", "1", "true"].includes(value.toLowerCase());
}

const today = () => {
	const now = new Date();
	now.setHours(0,0,0,0);
	return now.valueOf();
}

let touchedAccessories = new Set<string>();

const IS_TEST_ENV = process.env.NODE_ENV==="test"
const ALLOW_FALLBACK = (process.env.NODE_ENV !== "production" || IS_TEST_ENV);
const OUTPUT_DATA_WARNINGS = (!IS_TEST_ENV);

const _SEARCH_PARAMS = new URLSearchParams(window?.location?.search);
const DEBUG = _SEARCH_PARAMS.has("debugapi") ? [null, "", "true"].includes(_SEARCH_PARAMS.get("debugapi")) : window.location.host.includes(":") || window.location.host.includes("-dev.") || window.location.host.includes("-test.") || window.location.host.includes("-prerelease.");
const ALLOW_OVERRIDES = DEBUG || IS_TEST_ENV;
const NOPRICES = (ALLOW_OVERRIDES && parseBool(_SEARCH_PARAMS.get("noprices"))) || process.env.REACT_APP_NO_PRICES === "true" || parseBool(_SEARCH_PARAMS.get("global")) || process.env.REACT_APP_HIDE_PRICES?.split(",");
const OVERRIDE_VALIDITY_DATE = _SEARCH_PARAMS.has("overridedate") ? _SEARCH_PARAMS.get("overridedate") as string : process.env.REACT_APP_OVERRIDE_DATE;
const OVERRIDE_VALIDITY = ALLOW_OVERRIDES && OVERRIDE_VALIDITY_DATE && parseDate(OVERRIDE_VALIDITY_DATE)
const DEBUG_IGNORE_2WAY = DEBUG && _SEARCH_PARAMS.has("ignore2way");
const DEBUG_IGNORE_3WAY = DEBUG && _SEARCH_PARAMS.has("ignore3way");

const PREORDER_YEAR : {[region: string]: {[modelyear: string]: number}} | string = _SEARCH_PARAMS.get("preorder") ||
	(process.env.REACT_APP_PREORDER_YEAR?.includes("=") ? 
		process.env.REACT_APP_PREORDER_YEAR.split(",")
			.map(p => p.split("="))
			.filter(c => c[0] && c[1])
			.reduce((p, c) => ({
				...p,
				[c[0]]: (c[1].includes(";") ?
							{[c[1].split(";")[0]]: parseDate(c[1].split(";")[1])}
							:
							{[c[1]]: Number.MAX_VALUE})
			}), {}) 
		: 
		(process.env.REACT_APP_PREORDER_YEAR || "")
	);

const IGNORE_KIT_BUNDLE_REMOVAL_ON_FEATURE_CHANGE  = process.env.REACT_APP_NO_BUNDLE_CONFLICTS_ON_FEATURE_CHANGE === "true" || _SEARCH_PARAMS.has("nobundleconflictonfeaturechange");
const IGNORE_INCLUDED_ACCESSORY_VALIDATION = parseBool(process.env.REACT_APP_NO_INCLUDED_ACCESSORY_VALIDATION) || parseBool(_SEARCH_PARAMS.get("noincludedaccessoryvalidation"), true);
const IGNORE_ALL_ACCESSORY_VALIDATION  = parseBool(process.env.REACT_APP_NO_ACCESSORY_VALIDATION) || parseBool(_SEARCH_PARAMS.get("noaccessoryvalidation"), false);

const DEFAULT_HOMOLOGATION : {[key:string]: string} | string = process.env.REACT_APP_DEFAULT_HOMOLOGATION?.includes("=") ? process.env.REACT_APP_DEFAULT_HOMOLOGATION.split(",").map(p => p.split("=")).filter(c => c[0] && c[1]).reduce((p, c) => ({...p, [c[0]]: c[1]}), {}) : (process.env.REACT_APP_DEFAULT_HOMOLOGATION || "NOH");
const DEFAULT_CURRENCY = process.env.REACT_APP_DEFAULT_CURRENCY || "CAD";
const PREFFERED_CURRENCY : {[key:string]: string} = (process.env.REACT_APP_PREFFERED_CURRENCY || "").split(",").map(p => p.split("=")).filter(c => c[0] && c[1]).reduce((p, c) => ({...p, [c[0]]: c[1]}), {});

const PREFERRED_ATTACH_POINTS = process.env.REACT_APP_PREFERRED_ATTACH_POINTS?.split(",") || [];
const ATTACH_POINT_ALIASES : {[key:string]: string} = (process.env.REACT_APP_ATTACH_POINT_ALIASES || "").split(",").map(p => p.split("=")).filter(c => c[0] && c[1]).reduce((p, c) => ({...p, [c[0]]: c[1]}), {});

const BLOCKED_OPTION_GROUPS = (process.env.REACT_APP_BLOCKED_OPTION_GROUPS || "").split(",");
const EXCLUDED_OPTION_GROUPS = (process.env.REACT_APP_HIDDEN_OPTION_GROUPS || "").split(",");
const CONDITIONAL_OPTION_GROUPS = (process.env.REACT_APP_CONDITIONALLY_HIDDEN_OPTION_GROUPS || "").split(",");
const CONDITIONAL_SUMMARY_GROUPS = (process.env.REACT_APP_CONDITIONALLY_HIDDEN_SUMMARY_GROUPS || "").split(",");
const OPTION_GROUP_REDIRECT : {[key:string]: string[]} = (process.env.REACT_APP_OPTION_GROUP_REDIRECT || "").split(",").map(p => p.split("=")).filter(c => c[0] && c[1]).reduce((p, c) => ({...p, [c[0]]: c[1]?.split(";")}), {});

const HIDDEN_ACCESSORY_GROUPS = (process.env.REACT_APP_HIDDEN_ACCESSORY_GROUPS || "").split(",");

const OPTION_SORT_KEYS : {[key:string]: number} = (process.env.REACT_APP_OPTION_SORT_KEYS || "").split(",").map(p => p.split("=")).filter(c => c[0] && c[1]).reduce((p, c) => ({...p, [c[0]]: parseFloat(c[1])}), {});
const SORT_CATEGORIES = (process.env.REACT_APP_SORT_CATEGORIES === "true") || _SEARCH_PARAMS.has("sortcategories");
const SORT_ACCESSORIES = (process.env.REACT_APP_SORT_ACCESSORIES === "true") || _SEARCH_PARAMS.has("sortaccessories");
const SORT_FEATURE_OPTIONS : {[key:string]: string} = (process.env.REACT_APP_SORT_FEATURE_OPTIONS || "").split(",").map(p => p.split("=")).filter(c => c[0] && c[1]).reduce((p, c) => ({...p, [c[0]]: c[1]}), {});

const REMAP_PRCODES : {[key:string]: string} = (process.env.REACT_APP_REMAP_PRCODES || "").split(",").map(p => p.split("=")).filter(c => c[0] && c[1]).reduce((p, c) => ({...p, [c[0]]: c[1]}), {});

const OPTIMISE_RESOLVE_EXISTING = !_SEARCH_PARAMS.has("optimiseresolveexisting") || _SEARCH_PARAMS.get("optimiseresolveexisting") !== "false";
const TEST_OPTIMISE_RESOLVE_EXISTING = _SEARCH_PARAMS.get("testoptimiseresolveexisting") === "true"
const OPTIMISE_BREADTH_FIRST = process.env.NODE_ENV==="test" || process.env.REACT_APP_OPTIMISE_BREADTH_FIRST !== "false" || _SEARCH_PARAMS.get("optimisebreadthfirst") !== "false";

const IGNORE_OUT_OF_STOCK = _SEARCH_PARAMS.get("ignoreoutofstock") === "true"

const UNGROUP_ACCESSORIES = _SEARCH_PARAMS.get("ungroupaccessories") === "true"

const DEBUG_SKUS : string[] = DEBUG ? _SEARCH_PARAMS.getAll("debuggsku") : [];

const USE_BACKGROUND_DOWNLOAD = _SEARCH_PARAMS.get("usebackgrounddownload") === "true"

const NONE = "NONE";

export const CountryCodeMap : {[country: string]: string} = {
	"ARE": "AE",
	"AUT": "AT",
	"AUS": "AU",
	"BEL": "BE",
	"BRA": "BR",
	"CAN": "CA",
	"DEU": "DE",
	"ESP": "ES",
	"FIN": "FI",
	"FRA": "FR",
	"GBR": "GB",
	"ITA": "IT",
	"JPN": "JP",
	"NLD": "NL",
	"NOR": "NO",
	"MEX": "MX",
	"NZL": "NZ",
	"RUS": "RU",
	"SWE": "SE",
	"USA": "US",
	"POL": "PL",
	"HRV": "HR",
	"ROU": "RO",
	"TUR": "TR",
}

// starting to reduce references to these strings by referencing them via 
// this instead (might want to extend that beyond the module)
enum FCI {
	Color,
	Engine
}
const _CONFIGURABLE_FEATURE_CATEGORIES = ["BRP_COLOR", "BRP_{segment}_ENGINE"];
const ZL_ACCESSORY_GROUP = "ZL_ACCESSORY_GROUP_";

const DEFAULT_CURRENCY_FOR_COUNTRY : {[country: string]: string} = {
	"AE": "AED",
	"AT": "EUR",
	"AU": "AUD",
	"BE": "EUR",
	"BR": "BRL",
	"CA": "CAD",
	"DE": "EUR",
	"ES": "EUR",
	"FI": "EUR",
	"FR": "EUR",
	"GB": "GBP",
	"IT": "EUR",
	"NL": "EUR",
	"NO": "NOK",
	"MX": "MXN",
	"NZ": "NZD",
	"RU": "EUR",
	"SE": "SEK",
	"US": "USD",
	"PL": "PLN",
	"HR": "HRK",
	"RO": "RON",
	"TR": "TRY",
}

type Selected = {
	featureid?: string
	accessorysku?: string
	category: string				//type.id
	locked: number					//is included in package
	quantity: number
	attached?: string[]				//attach point ids
	addedautomatically?: boolean	//item was added to as part of conflict resolution, can be removed automatically when no longer needed
}

type State = {
	uuid: string //Random value to uniquely identifiy the build
	model: string
	year: string
	package: string
	homologation: string
	unitsku: string
	created: string
	modified: string
	locked: boolean
	from?: string
	expiry: string
	locale: string
	region: string
	name: string
	price: BasicPrice
	selected: Selected[]
	segment: string
	shortcode?: string
	initial?: {
		unitsku: string,
		selected: Selected[]
	}
};

interface Solution {
	add: string[]
	remove: string[]
	splitBundles: string[]
	targetUnitId?: string // only applicable when we're juggling features
}

type KeyedSolutions = {[key: string]: Solution}

type Restrictions = {[characteristic: string]: string}

interface ResolverState {
	[sku: string]: number
}

interface ResolverStore {
	unit: V2.Unit
	accessoryCache: AccessoryCache
	initialState: ResolverState
	steps: {[solutionkey: string]: (()=>void) | null}[]
	solutions: KeyedSolutions
}

const postMessage = (type:string, data?:any) => {
	console.log("ZL PostMessage: " + type, data);
	window.parent.postMessage(JSON.stringify({event: type, params: data}), "*")
}

const isPreorderOnly = (year: string, country: string, segment: string) => {
	if(parseBool(_SEARCH_PARAMS.get("global"), false)) {
		//Never pre-order on BRP world right now, might need a separate PREORDER_YEAR setup for BRP World in future
		return false;
	}

	if(typeof(PREORDER_YEAR) === "string") {
		if(PREORDER_YEAR === "true") {
			return true;
		}
		else if(PREORDER_YEAR === year) {
			return true;
		}
	}
	else {
		for(const context of [`${country}_${segment}`, country, segment]) {
			if(PREORDER_YEAR[context] && PREORDER_YEAR[context][year]) {
				let now = today()
				if(OVERRIDE_VALIDITY && OVERRIDE_VALIDITY > now) {
					now = OVERRIDE_VALIDITY;
				}
				if(PREORDER_YEAR[context][year] > now) {
					return true;
				}
			}
		}
	}
	return false;
}

const makeManagable = (saveData: SaveData) : ManagableSaveData => {
	return {
		...saveData,
		delete: () => {
			postMessage("brp_delete_build", saveData)
			try {
				if(saveData.autosaved) {
					window.localStorage.removeItem(`SavedBuild.autosaved`);
				}
				else {
					window.localStorage.removeItem(`SavedBuild.${saveData.uuid}`);
				}
				
			}
			catch {
				console.log("local storage not available for saved builds")
			}
		},
		isPreorderOnly: () => {
			return isPreorderOnly(saveData.year, saveData.locale.substr(3), saveData.segment)
		},
		preorder: async () => {
			postMessage("brp_pre_order", saveData)
		},
		requestQuote: async () => {
			postMessage("brp_get_quote", saveData)
		},
		share: (channel:ShareTarget, image: string, lock: boolean = false) => {
			postMessage("brp_share", {...saveData, channel, image, locked: lock})
		},
		getPlatformId: (real: boolean = false) => real ? saveData.realplatform || saveData.model : saveData.model,
		getPackageId: () => saveData.package,
		getModelSku: () => saveData.unitsku,
		getModelYear: () => saveData.year,
		getModelSegment: () => saveData.segment,
		getPRCodes: (stripIncludedAccessories?: boolean) => [
			saveData.unitsku,
			saveData.model,
			saveData.package,
			...saveData.selected.filter(s => !stripIncludedAccessories || !s.accessorysku || !s.locked).map(s => (s.featureid || s.accessorysku) as string)
		]
	}
}

const checkValidityPeriod = (item: {validFrom?: string, validTo?: string}, now: number = today()) => {
	if(OVERRIDE_VALIDITY && OVERRIDE_VALIDITY > now) {
		now = OVERRIDE_VALIDITY;
	}
	return (!item.validFrom || parseDate(item.validFrom) <= now) && (!item.validTo || parseDate(item.validTo) >= now);
}

const sanitizeCountryList = (countries?: string[]) : string[] => {
	//Remap country codes the BRP are misusing
	return countries?.map(c => {
		switch(c) {
			case "SP":
				return "ES";
			default:
				return c;
		}
	}) || [];
}

type FamilyCache = {
	modelId: string
	validUnits: {unit: V2.Unit, price: number, matches: Selected[]}[]
	engineBrackets: {[key: string]: number}
	featureOutOfStock: {[key: string]: boolean}
} | undefined

type AccessoryCache = {
	all: {
		items: {[sku: string]: V2.Accessory}
		orderedItems: V2.Accessory[]
	}
	available: {
		items: {[sku: string]: V2.Accessory}
		orderedItems: V2.Accessory[]
	}
	categorised: {
		[key:string]: {
			items: {[sku: string]: V2.Accessory}
			orderedItems: V2.Accessory[]
		}
	}
	optimisableCategories?: string[]
};

type EnsureAccessoriesReady = (unitId: string) => Promise<void>

class Configuration {
	constructor(blacklist: Blacklist, priorityList: string[], ignoreAttachPoints: boolean | string[], optimise: boolean, inject?: {[id: string]: any}, hidePrices?: boolean, autosave?: boolean) {
		this.blacklist = blacklist;
		this.priorityList = priorityList;
		this.ignoreAttachPoints = ignoreAttachPoints;
		this.optimise = optimise;
		this.inject = inject;
		this.hidePrices = hidePrices || false;
		this.autosave = autosave ?? (process.env.NODE_ENV !== 'test');
	}
	public readonly blacklist: Blacklist;
	public readonly priorityList: string[];
	public readonly ignoreAttachPoints: boolean | string[];
	public readonly optimise: boolean;
	public readonly inject: {[id: string]: any} | undefined
	public readonly hidePrices: boolean;
	public readonly autosave: boolean;
}

const isKit = (accessory?: V2.Accessory) : boolean => {
	return (accessory?.accessoryPackage?.length || 0) > 0 && accessory?.category?.some(c => /brp_.*_kit_accy/.test(c.id ?? "")) === true
}

const isBundle = (accessory?: V2.Accessory) : boolean => {
	return (accessory?.accessoryPackage?.length || 0) > 0 && !isKit(accessory);
}


const isNewCutoffDate = OVERRIDE_VALIDITY ? new Date(OVERRIDE_VALIDITY_DATE) : new Date();
isNewCutoffDate.setMonth(isNewCutoffDate.getMonth() - 6);
const isNew = (accessory: V2.Accessory) : boolean => {
	if(accessory.isNew === undefined) {
		if(accessory.validFrom && new Date(accessory.validFrom) > isNewCutoffDate) {
			return true;
		}
		return false;
	}
	return accessory.isNew;
}

const getQuantityRange = (accessory: V2.Accessory) => {
	if(isKit(accessory)) {
		return {minimum: 1, maximum: 1}
	}
	if(accessory.type?.id && HIDDEN_ACCESSORY_GROUPS.includes(accessory.type.id)) {
		return {minimum: 0, maximum: 1} //Should ideally be maximum=numCurrentlySelected
	}
	//Uncap the limits, the only things that currently have values above 1 are things that we don't handle separately anyway (such as wheel trims)
	return {
		minimum: accessory.quantityRange && accessory.quantityRange.minvalue || 1,	//accessory.quantityRange && accessory.quantityRange.maxvalue,
		maximum: accessory.quantityRange && accessory.quantityRange.maxvalue 		//undefined
	}
}

const getIncludedCount = (accessory?: V2.IncludedAccessory) : number => {
	if(!accessory) {
		return 0
	}
	return accessory.quantity ?? getQuantityRange(accessory).minimum
}

export class BuildSessionV2 implements IBuildSession {
	readonly session: SessionV2;
	readonly data: V2.UnitModelFamily;
	readonly state: State;
	readonly onBuildSessionUpdate: OnBuildSessionUpdate;
	readonly onConflict: OnConflict;
	readonly onAttachChoice: OnAttachChoice;
	readonly onShortCode: OnShortCode;
	readonly getSharingData: GetSharingData;
	private readonly familyCache: FamilyCache;
	private readonly accessoryCache: AccessoryCache | undefined;
	private readonly config: Configuration | undefined;
	private saved: boolean;
	private shortcode?: string | null;
	private configurableFeatureCategories: Array<string>;
	private ensureAccessoriesReady : EnsureAccessoriesReady;

	constructor(session: ISession, data: Object, state: State, onBuildSessionUpdate?: OnBuildSessionUpdate, onConflict?: OnConflict, onAttachChoice?: OnAttachChoice, getSharingData?: GetSharingData, onShortCode?: OnShortCode, familyCache: FamilyCache = undefined, accessoryCache: AccessoryCache | undefined = undefined, config: Configuration | undefined = undefined, saved: boolean = false, shortcode?: string, ensureAccessoriesReady?: EnsureAccessoriesReady) {
		this.session = session as SessionV2;
		this.data = data as V2.UnitModelFamily;
		this.state = state;
		this.onBuildSessionUpdate = onBuildSessionUpdate || noop;
		this.onConflict = onConflict || noop;
		this.onAttachChoice = onAttachChoice || noop;
		this.onShortCode = onShortCode || noop;
		this.getSharingData = getSharingData ||undefinednoop;
		this.ensureAccessoriesReady = ensureAccessoriesReady || asyncnoop;
		this.familyCache = familyCache;
		this.accessoryCache = accessoryCache;
		this.config = config;
		this.saved = saved;
		this.shortcode = shortcode || state.shortcode;
		this.configurableFeatureCategories = _CONFIGURABLE_FEATURE_CATEGORIES;
		this.initConfigCats();
	}

	private getDataRoot() {
		let root : string | null = null
		try {
			const params = new URLSearchParams(window.location.search)
			root = params.get("apidatasource")
		}
		catch {}
		if(root === null || root === "auto") {
			if(window.location.host.includes("-live.")) {
				root = "https://cloud-api.brp.com/digital_model/v1/"
			}
			else if(window.location.host.includes("-test.")) {
				root = "https://qa-cloud-api.brp.com/digital_model/v1/"
			}
			else if(window.location.host.includes("-vip.")) {
				root = "https://model-api-vip.brpdigital.net/apidata/v1/unit/"
			}
			else {
				root = "https://dev-cloud-api.brp.com/digital_model/v1/"
			}
		}
		else if(root === "builtin") {
			root = "/"
		}
		return root.endsWith("/") ? root : root + "/"
	}

	async start(initialState: any, testDataSource: any) : Promise<IBuildSession> {
		let root = this.getDataRoot()
		if(testDataSource !== undefined) {
			if(process.env.NODE_ENV === "production") {
				console.warn("Blocked test data source", {testDataSource});
				testDataSource = undefined;
			}
			else if(typeof(testDataSource) === "string") {
				console.log("Overriding data root", {testDataSource});
				root = testDataSource;
				testDataSource = undefined;
			}
		}

		let data : V2.UnitModelFamily | undefined = undefined;
		const language = this.session.locale.slice(0, 2);
		const isZLSource = root === "/" || root.includes(".zlthunder.net") || root.includes("zl.limited") || root.includes("zerolight.io") || root.includes("localhost")
		const isBRPDigitalProductCatalog = root.includes("/digitalProductCatalog/")
		const getUrlWithOverrideDate = (url: string) => {
			if(OVERRIDE_VALIDITY && OVERRIDE_VALIDITY > now) {
				if(isBRPDigitalProductCatalog) {
					return url.replace("/v1/byo/", "/v1/preview/byo/") + `?overrideLiveDate=${OVERRIDE_VALIDITY_DATE}`
				}
				else if(root.search(/(model-api-.*\.brpdigital\.net|-cloud-api\.brp\.com)/gi) >= 0) {
					return `${url}?overwriteDate=${OVERRIDE_VALIDITY_DATE}`
				}
			}
			return url
		}
		
		const now = today()
		let dataloc = undefined;
		if(isZLSource) {
			dataloc = root + ["apidata", "v2", initialState.model.toLowerCase() + "_" + initialState.year, initialState.pkg.toLowerCase(), language].join("/") + ".json";
			console.debug("Loading ZL Data Source", dataloc)
		}
		else {
			let platformId = initialState.model.toLowerCase()
			if(!isBRPDigitalProductCatalog) {
				platformId += "_" + initialState.year
			}
			dataloc = root + ["units", this.session.brand, initialState.year, "family", platformId, "package", initialState.pkg.toLowerCase(), this.session.locale].join("/");
			dataloc = getUrlWithOverrideDate(dataloc)
			console.debug("Loading BRP Data Source", dataloc)
		}

		if(!testDataSource) {
			try {
				let headers : {[key: string]: string} = {}
				if(this.session.authFunc) {
					const token = await this.session.authFunc()
					if(token) {
						headers = {
							"Authorization": "Bearer " + token
						}
					}
				}

				const response  = await fetch(dataloc, {headers});
				if(response.ok) {
					if(isZLSource) {
						const json = await response.json() as V2.UnitModelFamilyResponse;
						if(json && json.unitModelFamily) {
							data = json.unitModelFamily
						}
						else {
							throw Error("Bad API data");
						}
					}
					else {
						let text = await response.text()
						let responseJson = JSON.parse(text, (key,value) => {console.log(key, value); return value})
						//let json = (await response.json())
						responseJson = responseJson.data
						if(!isBRPDigitalProductCatalog) {
							responseJson = responseJson[0]
						}

						//Bodges to fix inconsistent data seen on the model API
						if(!(responseJson.unitModels instanceof Array)) {
							console.warn("Unexpected format for unitModels, upgrading", responseJson.unitModels)
							responseJson.unitModels = [responseJson.unitModels]
						}
						responseJson.unitModels[0].units.forEach((unit: any) => {
							if(unit.unitId) {
								console.warn("Unexpected format for unit id, upgrading", unit.unitId)
								unit.id = unit.unitId;
								delete unit.unitId
							}
							if(unit.platform !== null && typeof(unit.platform) === "object") {
								console.warn("Unexpected format for unit platform, upgrading", unit.platform);
								if(Object.keys(unit.platform).length > 0) {
									console.error("Unable to upgrade unit platform", unit.platform)
								}
								else {
									delete unit.platform
								}
							}

							if(unit.prices === null) {
								console.warn("Unexpected format for unit price, upgrading", unit.prices)
								delete unit.prices
							}

							if(unit.keyFeatures !== undefined && !Array.isArray(unit.keyFeatures)) {
								console.warn("Unexpected format for unit key features, upgrading", unit.keyFeatures);
								unit.keyFeatures = [unit.keyFeatures]
							}

							if(unit.availableCountries) {
								unit.availableCountries = unit.availableCountries.map((country: string) => CountryCodeMap[country] ?? country)
							}
							
							const segment = responseJson.id.split("_")[0]
							this.initConfigCats(segment.toUpperCase());
							unit.bundledFeatures = unit.bundledFeatures.map((f : V2.UnitFeature) => {
								if(f.type?.id?.toUpperCase() === "COLOR") {
									f.type.id = this.configurableFeatureCategories[FCI.Color]
								}
								else if(f.type?.id?.toUpperCase() === "ENGINE") {
									f.type.id = this.configurableFeatureCategories[FCI.Engine]
								}
								return f
							}).sort((l: V2.UnitFeature, r: V2.UnitFeature) => {
								let lIndex = this.configurableFeatureCategories.indexOf(l.type?.id ?? "")
								let rIndex = this.configurableFeatureCategories.indexOf(r.type?.id ?? "")
								lIndex = lIndex === -1 ? this.configurableFeatureCategories.length : lIndex
								rIndex = rIndex === -1 ? this.configurableFeatureCategories.length : rIndex
								return lIndex - rIndex
							})
						})

						let backgroundProcessorStarted = false;
						const accessoryRequests : {[unitId: string]: Promise<Response>} = {}
						const accessoryResponses : {[unitId: string]: Promise<boolean>} = {}
						const accessoriesLoaded : string[] = []
						this.ensureAccessoriesReady = async (unitId: string) => {
							if(accessoriesLoaded.includes(unitId)) {
								return;
							}

							const beginRequest = async (unitId: string) : Promise<Promise<Response>> => {
								let headers : {[key: string]: string} = {}
								if(this.session.authFunc) {
									const token = await this.session.authFunc()
									if(token) {
										headers = {
											"Authorization": "Bearer " + token
										}
									}
								}

								let accessoryRoot = root.replace("/unit/", "/accessory/") //The brpdigital version of the API has this strange layer
								let accessoryDataLoc = accessoryRoot + ["accessories", this.session.brand, "unit", unitId, this.session.locale].join("/")
								accessoryDataLoc = getUrlWithOverrideDate(accessoryDataLoc)
								const request = fetch(accessoryDataLoc, {headers})
								accessoryRequests[unitId as string] = request
								return request
							}

							const processResponse =  async (accessoryResponse: Response) => {
								if(accessoryResponse.ok) {
									const json = await accessoryResponse.json()
									let responseUnits = (Array.isArray(json.data) ? json.data : [json.data]) as {includedAccessories: V2.IncludedAccessory[], availableAccessories: V2.Accessory[], unitId?: string}[]
									for(const responseUnit of responseUnits) {
										const fixAccessory = (accessory: V2.Accessory) => {
											if(typeof(accessory.category) === "string") {
												accessory.category = [{id: accessory.category}]
											}
											else if(accessory.category?.length) {
												accessory.category = accessory.category.map((c: string | V2.CategoryType) => {
													if(typeof(c) === "string") {
														return {id: c}
													}
													return c;
												})
											}
											else {
												accessory.category = [{id: "missing_category"}]
											}

											if(typeof(accessory.type) === "string") {
												accessory.type = {id: accessory.type, name: accessory.type}
											}

											if(accessory.prices === null) {
												delete accessory.prices
											}
											else if(Array.isArray(accessory.prices)) {
												accessory.prices = (accessory.prices as V2.PriceV2[])[0]
											}

											if(accessory.dependencies?.twoWay && Array.isArray(accessory.dependencies.twoWay)) {
												accessory.dependencies.twoWay = accessory.dependencies.twoWay.reduce((deps: Record<string, string[]>, dep: {characteristic: string, values: string[]}) => {
													deps[dep.characteristic] = dep.values
													return deps
												}, {} as Record<string, string[]>)
											}
											if(accessory.includedCountries) {
												accessory.includedCountries = accessory.includedCountries.map(country => CountryCodeMap[country] ?? country)
											}
										}
										responseUnit.availableAccessories.forEach(fixAccessory);
										responseUnit.includedAccessories.forEach(fixAccessory);
										const unit = data?.unitModels?.[0].units?.find(u => u.id === responseUnit.unitId);
										if(unit) {
											delete responseUnit.unitId;
											Object.assign(unit, responseUnit);
											accessoriesLoaded.push(unit.id as string);
											return true;
										}
									}
								}
								else {
									throw Error("Failed to fetch API accessory data");
								}
								return false;
							}

							//Queue the desired unit first
							let startedAt = performance.now()
							if(accessoryRequests[unitId] === undefined) {
								await beginRequest(unitId)
							}
							if(!backgroundProcessorStarted && USE_BACKGROUND_DOWNLOAD) {
								backgroundProcessorStarted = true;
								const MAX_BACKGROUND_REQUEST_PROCESSORS = 3; //If too high, the browser will throttle the requests
								const beginBackgroundRequest = (group: number) => {
									for(const unit of responseJson.unitModels[0].units) {
										if(accessoryRequests[unit.id as string] === undefined) {
											beginRequest(unit.id).then(request => request).then(async (response: Response) => {
												const responseProcessor = processResponse(response)
												accessoryResponses[unit.id] = responseProcessor
												return await responseProcessor
											}).then(() => beginBackgroundRequest(group))
											return;
										}
									}
									if(!IS_TEST_ENV) {
										console.log(`Terminating background accessory request group ${group}`)
									}
								}
								for(let i = 0; i < MAX_BACKGROUND_REQUEST_PROCESSORS; ++i) {
									console.log(`Beginning background accessory request group ${i}`)
									beginBackgroundRequest(i);
								}
							}
							
							if(accessoryResponses[unitId] !== undefined) {
								await accessoryResponses[unitId]
							}
							else {
								await processResponse(await accessoryRequests[unitId])
							}
							
							if(!IS_TEST_ENV) {
								console.log(`Waited ${(performance.now() - startedAt) / 1000}s for ${unitId} accessory data`)
							}
						}
						data = responseJson as V2.UnitModelFamily
					}
				}
				else {
					throw Error("Failed to fetch API data");
				}
			}
			catch (err){
				console.log(err)
				if (ALLOW_FALLBACK){
					console.error("Failed to load real data. Was looking in ", dataloc)
				}else{
					throw Error("Failed to load API data for "+[initialState.model.toLowerCase(),initialState.pkg.toLowerCase(), initialState.year, language].join(" "));
				}
			}
		}
		else if(ALLOW_FALLBACK) {
			console.log("Loading data from test data source")
			const response = (await testDataSource.v2[(initialState.model + "_" + initialState.year).toLowerCase()]?.[initialState.pkg.toLowerCase()]?.[language]?.()) as V2.UnitModelFamilyResponse;
			if(!response || !response.unitModelFamily) {
				throw Error("Failed to load fallback data:" + response)
			}
			data = response.unitModelFamily;
		}

		/*Should only do this in development, final should loudly fail*/
		if(!data) {
			throw Error("No API data");
		}

		this.session.currency = this.getAvailableCurrency(data, this.session.country, this.session.region, this.session.currency);
		
		let unit : V2.Unit | undefined = undefined;
		let unitModel : V2.UnitModel | undefined = undefined;
		let state = this.state;
		if(state === null || state.unitsku === undefined) {
			//Build initial state from data and initialState parameters, ideally this will be the same structure as saved
			if(!data.unitModels || data.unitModels.length === 0) {
				console.error(data.unitModels)
				throw new Error("No unit models for " + initialState.model + "_" + initialState.year + " " + initialState.pkg);
			}

			unitModel = data.unitModels.find((um) => um.id === (initialState.model)) || data.unitModels[0];
			if(!unitModel.units || unitModel.units.length === 0) {
				throw new Error("No units for " + unitModel.id)
			}
		
			let validUnits : V2.Unit[] = [];
			if(!initialState.unitsku) {
				//Pick the unit with the lowest price, currently available in the required country
				const desiredFeatures = initialState.desiredFeatures?.map((id:string) => ({featureid: id}));
				const availableUnits = this.getValidUnits(unitModel, initialState.requiredFeatures, desiredFeatures);
				//console.log("requiredFeatures", initialState.requiredFeatures);
				//console.log("desiredFeatures", initialState.desiredFeatures);
				//console.log("availableUnits", availableUnits);
				
				if(availableUnits.length === 0) {
					throw new Error("No available units for " + unitModel.id)
				}
				const sortedAvailableUnits = availableUnits.sort((lhs, rhs) => (rhs.matches.length - lhs.matches.length) || (lhs.price - rhs.price));
				validUnits = availableUnits.map(a => a.unit);
				initialState.unitsku = sortedAvailableUnits[0].unit.id;
			}
			else {
				validUnits = this.getValidUnits(unitModel, undefined, undefined, true).map(a => a.unit);
			}

			unit = validUnits.find((u:V2.Unit) => u.id?.toLowerCase() === initialState.unitsku?.toLowerCase());
			if(!unit) {
				if(data.unitModels.some((um) => um.units?.some(u => u.id === initialState.unitsku))) {
					throw new Error("No valid units for " + initialState.unitsku + ", valid units are " + validUnits.map(u => u.id))
				}
				throw new Error("No units for " + initialState.unitsku)
			}

			await this.ensureAccessoriesReady(unit.id as string)

			const features = (unit.bundledFeatures || []).map((feature) => (
				{
					featureid: feature.id,
					locked: initialState.requiredFeatures?.includes(feature.id) ? 1 : 0,
					category: feature.type && feature.type.id || "",
					quantity: 1,
					attached: undefined
				}
			));
			const accessories = this.getIncludedAccessories(unit).map((accessory) => (
				{
					accessorysku: accessory.id,
					locked: getIncludedCount(accessory),
					category: accessory.type && accessory.type.id || "",
					quantity: getIncludedCount(accessory),
					attached: this.getValidAttachPoints(accessory)
				}
			));
			state = {
				uuid: initialState.buildId,
				name: this.getMarketingName(unitModel, unit),
				model: initialState.model,
				year: initialState.year,
				package: initialState.pkg,
				homologation: this.getHomologationIdFromUnit(unit),
				unitsku: unit.id as string,
				created: new Date().toISOString(),
				modified: new Date().toISOString(),
				from: unit.validFrom,
				expiry: unit.validTo,
				locked: false,
				selected: [
					...features,
					...accessories
				],
				segment: this.getSegment(initialState.model, data.type),
				price: this.getPrice(unit.prices) || {amount: 0.00, currency: this.session.currency},
				locale: this.session.locale,
				region: this.session.region,
				initial: {
					unitsku: unit.id as string,
					selected: [
						...features,
						...accessories
					]
				}
			};

		}
		else {
			for(const um of data.unitModels || []) {
				unit = (um.units || []).find(u => u.id === state.unitsku);
				if(unit) {
					unitModel = um
					break;
				}
			}
			if(!unit) {
				throw new Error("Unit not found: " + state.unitsku);
			}
			
			if(this.session.locale) {
				const country = this.session.locale.split("-")[1]
				if(unit.availableCountries && !unit.availableCountries.includes(country)) {
					throw new Error(`Unit ${state.unitsku} not available in the current country (${country}), save originally from ${state.locale}`)
				}
			}

			await this.ensureAccessoriesReady(unit.id as string)
		}

		if(!unitModel) {
			throw new Error("Unit Model not found");
		}

		this.initConfigCats(state.model.split("_")[0]);
		
		const familyCache = this.buildFamilyCache(unitModel);
		const accessoryCache = this.buildAccessoriesList(unit);
		

		if(initialState.desiredAccessories?.length > 0) {
			const addDesiredAccessories = (accessories: ResolverState) => {
				for(const sku of Object.keys(accessories)) {
					const existing = state.selected.find(a => a.accessorysku === sku);
					if(existing) {
						existing.quantity += accessories[sku];
					}
					else {
						const accessory = accessoryCache?.all.items[sku];
						if(accessory) {
							state.selected.push({
								accessorysku: sku,
								locked: 0,
								category: isBundle(accessory) ? ZL_BUNDLES : accessory.type && accessory.type.id || "",
								quantity: 1,
								attached: this.getValidAttachPoints(accessory)
							})
							if(accessory.accessoryPackage) {
								addDesiredAccessories(accessory.accessoryPackage.reduce((p, c) => Object.assign(p, {[c.id]: c.quantity}), {} as ResolverState))
							}
						}
					}
				}
			}
			addDesiredAccessories(this.getResolverState(initialState.desiredAccessories))
		}
		
		const optimise = this.config?.optimise || ((data.modelYear || 0) >= (data.productLineUp?.id === "lynx" ? 2023 : 2022)) || (data.modelYear === 2021 && data.productLineUp?.id === "canam-offroad" && (data.id?.startsWith("ssv_defender") || data.id?.startsWith("ssv_traxter"))) || false;
		console.log("Checking if optimisations should be automatically enabled", {modelYear: data.modelYear, brand: data.productLineUp?.id, optimise, prevOptimise: this.config?.optimise})
		const config = new Configuration(this.config?.blacklist || [], this.config?.priorityList || [], this.config?.ignoreAttachPoints || false, optimise, this.config?.inject)
		const buildSession = new BuildSessionV2(this.session, data, state, this.onBuildSessionUpdate, this.onConflict, this.onAttachChoice, this.getSharingData, this.onShortCode, familyCache, accessoryCache, config, true, this.shortcode || undefined, this.ensureAccessoriesReady)
		this.onBuildSessionUpdate(buildSession)
		if(buildSession.shortcode && this.onShortCode) {
			this.onShortCode(buildSession, buildSession.shortcode);
		}
		return buildSession;
	}

	private getAvailableCurrency(data: V2.UnitModelFamily, country: string, region: string, preferredCurrency?: string) : string {
		preferredCurrency = preferredCurrency || PREFFERED_CURRENCY[this.session.country];

		//Look through everything with a price and determine what currencies we have information for in the selected country
		let usage : {[key:string]: number} = {};
		let specialisation : {[key:string]: number} = {};
		for(const unitModel of data.unitModels || []) {
			for(const unit of this.getValidUnits(unitModel).map(m => m.unit)) {
				const prices = [
					...((unit.prices instanceof Array) ? unit.prices : [unit.prices]),
					//...(this.getIncludedAccessories(unit)?.map(a => a.prices)?.reduce((p, a) => [...(p || []), ...(a || [])], []) || []),
					...(this.getAccessories(unit)?.filter(a => a).map(a => (a.prices instanceof Array) ? a.prices : [a.prices as V2.PriceV2])?.reduce((p, a) => [...p, ...a], []) || [])
				]
				for(const price of prices) {
					if(!price?.currency) {
						continue;
					}
					if(specialisation[price.currency] === undefined) {
						specialisation[price.currency] = 0;
					}
					if("amountsByCountry" in price || "defaultAmounts" in price) {
						specialisation[price.currency] += price.amountsByCountry?.length || 0;
						if((preferredCurrency !== price.currency && price.defaultAmounts?.length) || price.amountsByCountry?.find(a => a.countryCode && (typeof(a.countryCode) === "string" ? a.countryCode === country : a.countryCode.includes(country)))) {
							if(usage[price.currency] === undefined) {
								usage[price.currency] = 0;
							}
							usage[price.currency] += 1;
						}
					}
					else {
						usage[price.currency] += 1;
					}
				}
			}
		}
		
		let selectedCurrency = undefined;
		const availableCurrencies = Object.keys(usage).sort((l, r) => usage[l] !== usage[r] ? usage[r] - usage[l] : specialisation[l] - specialisation[r]);
		if(availableCurrencies.length > 0) {
			if(preferredCurrency && availableCurrencies.includes(preferredCurrency)) {
				selectedCurrency = preferredCurrency;
			}
			else {
				selectedCurrency = availableCurrencies[0];
			} 
		}
		else {
			selectedCurrency = preferredCurrency || DEFAULT_CURRENCY_FOR_COUNTRY[this.session.country] || DEFAULT_CURRENCY;
		}
		console.log(`Selected currency: ${selectedCurrency}`, {availableCurrencies, preferredCurrency, defaultCurrencyForRegion: DEFAULT_CURRENCY_FOR_COUNTRY[this.session.country], fallbackCurrency: DEFAULT_CURRENCY})
		return selectedCurrency;
	}

	private initConfigCats(modelType?: string){
		if (modelType || this.state.model){
			this.configurableFeatureCategories = this.configurableFeatureCategories.map((cat: string) => cat.replace("{segment}", (modelType || this.getModelType()).toUpperCase()))
		}
	}

	private dispatchUpdate(state: State, summary?: UpdateSummary, accessoryCache?: AccessoryCache, config?: Configuration) {
		const stateUpdated = state !== this.state;
		const saved = stateUpdated ? undefined : this.saved;
		const shortcode = stateUpdated ? undefined : this.shortcode || undefined;
		const updatedBuildSession = new BuildSessionV2(this.session, this.data, state, this.onBuildSessionUpdate, this.onConflict, this.onAttachChoice, this.getSharingData, this.onShortCode, this.familyCache, accessoryCache || this.accessoryCache, config || this.config, saved, shortcode, this.ensureAccessoriesReady);
		this.onBuildSessionUpdate(updatedBuildSession, summary);
		if(stateUpdated) {
			updatedBuildSession.autosave();
		}
	}

	clone(onBuildSessionUpdate: OnBuildSessionUpdate, onConflict: OnConflict, onAttachChoice: OnAttachChoice, onShortCode: OnShortCode, lock: boolean=false) : IBuildSession {
		return new BuildSessionV2(this.session, this.data, {...this.state, locked: this.locked() || lock}, onBuildSessionUpdate, onConflict, onAttachChoice, this.getSharingData, this.onShortCode, this.familyCache, this.accessoryCache, this.config, undefined, undefined, this.ensureAccessoriesReady);
	}

	close() {
		postMessage("brp_close_build");
	}

	private async getSaveData() : Promise<SaveData> {
		const unit = this.getUnit();
		const price = this.getTotalPrice();
		let shortcode = "FAILED"
		try {
			shortcode = await this.getShortCode(); //BRP are relying on shortcodes for all saves, instead of passing a save UUID to reload, not ideal as it places unnecessary work on the shortcode service
		}
		catch {

		}
		
		return {
			...this.state,
			realplatform: this.getPlatformId(true),
			expiry: unit.validTo,
			price: {
				...price,
				amount: price!.total.amount,
				currency: price!.total.currency,
				hide: this.config?.hidePrices
			},
			fees: this.getApplicableFees().reduce((p, c) => {
				if(c.type?.id && c.amount && c.currency) {
					p[c.type?.id] = {amount: c.amount, currency: c.currency}
				}
				return p
			}, {} as {[type: string]: BasicPrice}),
			locale: this.session.locale,
			region: this.session.region,
			segment: this.state.segment,
			shortcode
		};
	}

	async save() : Promise<ManagableSaveData> {
		if(this.locked()) {
			throw "Build Locked";
		}

		const saveData = {...await this.getSaveData(), autosaved: false}

		postMessage("brp_save_build", saveData);

		try {
			const buildKey = `SavedBuild.${saveData.uuid}`;
			window.localStorage.setItem(buildKey, JSON.stringify(saveData));
		}
		catch {
			console.log("local storage not available for saved build")
		}

		this.saved = true;
		
		return makeManagable(saveData);
	}

	private async autosave() {
		if(!this.locked() && this.config?.autosave) {
			const saveData = {...await this.getSaveData(), autosaved: true}
			
			postMessage("brp_autosave_build", saveData);

			try {
				const buildKey = `SavedBuild.autosave`;
				window.localStorage.setItem(buildKey, JSON.stringify(saveData));
			}
			catch {
				console.log("local storage not available for saved build")
			}
		}
	}

	doExport() {
		return {state: this.state}
	}

	doImport(data: any) {
		if(JSON.stringify(data.state) === JSON.stringify(this.state)) {
			return;
		}
		
		const state = data.state as State;
		if(state.year !== this.state.year || state.model !== this.state.model || state.package !== this.state.package || state.locale !== this.state.locale) {
			throw Error("Changing year, model, package or locale during import is currently unsupported") //Would need to reload API data and reinitialise caches
		}
		const updatedBuildSession = new BuildSessionV2(this.session, this.data, state, this.onBuildSessionUpdate, this.onConflict, this.onAttachChoice, this.getSharingData, this.onShortCode, this.familyCache, this.accessoryCache, this.config, false, undefined, this.ensureAccessoriesReady);
		this.onBuildSessionUpdate?.(updatedBuildSession)
	}

	forceRequestShortCode() {
		if(this.shortcode) {
			if(this.onShortCode) {
				this.onShortCode(this, this.shortcode)
			}
		}
		this.getShortCode();
	}

	async getShortCode() : Promise<string> {
		if(this.shortcode === undefined) {
			this.shortcode = null;
			let sharing = undefined;
			if(this.getSharingData) {
				sharing = this.getSharingData(this)
			}
			this.shortcode = await GenerateShortCode(this.session.userId, this.getPRCodes(true), this.session.version, {...this.state, locale: this.session.locale}, sharing, this.session.zlservice);
			if(this.onShortCode) {
				this.onShortCode(this, this.shortcode);
			}
		}
		else if(this.shortcode === null) {
			//Called from another thread?
			const waitForShortcode = async () => {
				return new Promise<void>(resolve => {
					const interval = setInterval(() => {
						if(this.shortcode) {
							clearInterval(interval);
							resolve()
						}
					}, 50)
				})
			}
			await waitForShortcode();
		}
		return this.shortcode as string;
	}

	hasUnsavedChanges() : boolean {
		return !this.saved;
	}

	locked() : boolean {
		return this.state.locked === true;
	}

	changePackage() {
		postMessage("brp_change_package")
	}
	
	async getFinance() {
		postMessage("brp_get_finance", await this.getSaveData())
	}

	async retailSave(message: string) {
		postMessage(message || "brp_retail_save", await this.getSaveData());
	}

	async requestQuote(){
		let action = "brp_get_quote";
		postMessage(action, await this.getSaveData());
	}
	
	isPreorderOnly() : boolean {
		return isPreorderOnly(this.getModelYear(false), this.session.country, this.getModelSegment());
	}

	async preorder(){
		let action = "brp_pre_order";
		postMessage(action, await this.getSaveData());
	}

	async promotion(){
		let action = "brp_promotion";
		postMessage(action, await this.getSaveData());
	}

	async share(channel:ShareTarget, image: string, lock: boolean = false) {
		postMessage("brp_share", {...await this.getSaveData(), channel, image, locked: lock || this.locked()})
	}

	resetUnit() : void {
		if(this.locked()) {
			throw "Build Locked"
		}

		const unit = this.getUnit();
		this.dispatchUpdate({
			...this.state,
			selected: [
				...(unit.bundledFeatures || []).map((feature) => (
					{
						featureid: feature.id,
						locked: 0,
						category: feature.type && feature.type.id || "",
						quantity: 1,
						attached: undefined
					}
				)),
				...this.getIncludedAccessories(unit).map((accessory) => (
					{
						accessorysku: accessory.id,
						locked: getIncludedCount(accessory),
						category: accessory.type && accessory.type.id || "",
						quantity: getIncludedCount(accessory),
						attached: this.getValidAttachPoints(accessory)
					}
				))
			]
		})
	}

	resetBuild() : void {
		if(this.locked()) {
			throw "Build Locked"
		}

		if(this.state.initial) {
			this.dispatchUpdate({
				...this.state,
				...this.state.initial
			})
		}
		else {
			this.resetUnit() //Not enough info to go back to the original setup
		}
	}

	setConfig(blacklist: Blacklist, priorityList: string[], ignoreAttachPoints?: boolean, optimise?: boolean, inject?: {[id: string]: any}, hidePrices?: boolean, autosave?: boolean) {
		if(ignoreAttachPoints === undefined) {
			ignoreAttachPoints = process.env.REACT_APP_IGNORE_ATTACH_POINTS === "true" || _SEARCH_PARAMS.has("ignoreattachpoints");
			if(!ignoreAttachPoints && process.env.REACT_APP_IGNORE_ATTACH_POINTS) {
				const rules = process.env.REACT_APP_IGNORE_ATTACH_POINTS.toLowerCase().split(",");
				if(rules.includes(this.getPlatformId().toLowerCase()) || rules.includes(this.getPlatformId().toLowerCase()+"_"+this.getModelYear(false))) {
					ignoreAttachPoints = true;
				}
			}
		}

		if(optimise === undefined) {
			const param = _SEARCH_PARAMS.get("optimise")
			if(param !== null) {
				optimise = ["", "true"].includes(param);
			}
			else {
				optimise = this.config?.optimise || false;
			}
		}

		if(inject && this.config?.inject !== inject) {
			//Convert any usage of contains: string[] to accessoryPackage: {id: string, quantity: number}
			//Can be removed after manta 2022 metadata is configured in the new format or made obsolete
			const recursiveConvert = (item: any) => {
				if(Array.isArray(item)) {
					item.forEach(v => recursiveConvert(v))
				}
				else if(typeof(item) === "object") {
					if(item.contains) {
						const groupById = (item.contains as string[]).reduce((p, c) => Object.assign(p, {[c]: (p[c] || 0) + 1}), {} as {[id: string]: number})
						item.accessoryPackage = Object.keys(groupById).map(id => ({id, quantity: groupById[id]}))
						console.log("Converted", item.contains, "to", item.accessoryPackage)
						delete item.contains
					}
					for(const value of Object.values(item)) {
						if(value) {
							recursiveConvert(value);
						}
					}
				}
			}
			recursiveConvert(inject)

			const recursiveInject = (item: any) => {
				if(Array.isArray(item)) {
					item.forEach(v => recursiveInject(v))
				}
				else if(typeof(item) === "object") {
					for(const value of Object.values(item)) {
						if(value) {
							recursiveInject(value);
						}
					}
					if(item.id && inject[item.id]) {
						console.debug("Injecting data into " + item.id, inject[item.id], item)
						Object.assign(item, inject[item.id])
					}
				}
			}
			console.debug("Attempting to inject data", inject);
			recursiveInject(this.data);
		}

		const config = new Configuration(blacklist, priorityList, ignoreAttachPoints, optimise, inject, hidePrices, autosave);

		let accessoryCache = undefined;
		try {
			if(blacklist !== this.config?.blacklist || inject !== this.config?.inject) {
				accessoryCache = this.buildAccessoriesList(this.getUnit(), blacklist);
			}
		}
		catch {}

		this.dispatchUpdate(this.state, undefined, accessoryCache, config);
	}

	getCountryCode() : string {
		return this.session.country;
	}

	getCurrencyCode() : string {
		return this.session.currency;
	}

	getProductLineUp(): string {
		let pl = (this.data.productLineUp && this.data.productLineUp.id) ? this.data.productLineUp.id : "unknown-lineup";
		return pl.replace(/-/g, "").toLowerCase();
	}

	private getTranslatedUrl(data?: V2.I18nUrl | string) : string | undefined {
		if(!data) {
			return undefined;
		}

		if(typeof(data) === "string") {
			return data;
		}

		if(data.locales !== undefined) {
			const translation = data.locales.find(k => k.key === this.session.locale);
			if(translation && translation.value) {
				return translation.value;
			}
		}

		return data.default;
	}

	
	private getTranslationFromList(dataset?: (V2.I18nString | V2.I18nUrl | string | undefined)[], locale?: string) : string {
		if(dataset) {
			for(const data of dataset) {
				if(typeof(data) === "string") {
					continue
				}
				const translations = data?.locales?.reduce((s, k) => {
					if(k.value && k.key === (locale || this.session.locale)) {
						s[k.value] = (s[k.value] || 0) + 1
					}
					return s
				}, {} as {[translation: string]: number});
				if(translations && Object.keys(translations).length > 0) {
					return Object.keys(translations).sort((l, r) => translations[r] - translations[l])[0];
				}
			}
			
			for(const data of dataset) {
				if(data) {
					if(typeof(data) === "string") {
						return data;
					}
					else if(data?.default) {
						return data.default;
					}
				}
			}
		}
		return "";
	}

	private getTranslation(data?: V2.I18nString | V2.I18nUrl | string, locale?: string) : string {
		if(!data) {
			return "";
		}

		if(typeof(data) === "string") {
			return data;
		}

		if(data.locales !== undefined) {
			const translation = data.locales.find(k => k.key === (locale || this.session.locale));
			if(translation && translation.value) {
				return translation.value;
			}
		}

		return data.default || "";
	}

	private getPrice(priceData?: V2.Price[] | V2.PriceV2, includeSurcharges?: boolean) : BasicPrice | undefined {
		if(!(priceData instanceof Array)) {
			if(priceData === undefined || !checkValidityPeriod(priceData) || (this.session.currency && priceData.currency !== this.session.currency)) {
				if(NOPRICES === true || NOPRICES?.includes(this.session.country)){
					return {amount: 0.00, currency: this.session.currency};
				}
				return undefined;
			}

			let price : BasicPrice = {
				amount: Math.max(priceData.value || 0, priceData.valueWithVAT || 0),
				currency: priceData.currency
			}

			if((includeSurcharges === undefined || includeSurcharges) && priceData.surcharges && this.session.region) {
				for(const surcharge of priceData.surcharges) {
					if(!checkValidityPeriod(surcharge)) {
						console.log("Ignoring surcharge because its outside the validity period", surcharge.validFrom, surcharge.validTo)
						continue;
					}
					else if(surcharge.regionCode !== undefined && surcharge.regionCode !== this.session.region) {
						console.log(`Ignoring surcharge because the region doesn't match '${surcharge.regionCode}' !== '${this.session.region}'`, surcharge)
						continue;
					}
					price.amount += Math.max(surcharge.value || 0, surcharge.valueWithVAT || 0)
				}
			}

			return price;
		}

		if(!priceData?.length) {
			if(NOPRICES === true || NOPRICES?.includes(this.session.country)){
				return {amount: 0.00, currency: this.session.currency};
			}
			return undefined;
		}

		let lowestPrice : BasicPrice | undefined = undefined;
		let lowestSurcharge : BasicPrice | undefined = undefined;

		const now = today();

		const processPrice = (currentLowest: BasicPrice | undefined, price: V2.PriceAmount, currency?: string) : BasicPrice | undefined => {
			if(!currency) {
				console.error("Currency not specified")
				return currentLowest;
			}
			const value = Math.max(price.value || 0, price.valueWithVAT || 0)
			if(price.value === undefined && price.valueWithVAT === undefined) {
				if(DEBUG) {
					console.debug("Invalid price", price);
				}
			}
			else if(!checkValidityPeriod(price, now)) {
				if(DEBUG) {
					console.debug("Invalid price validity range", price);
				}
			}
			else if(!currentLowest || value < currentLowest.amount) {
				return {amount: value, currency};
			}
			return currentLowest;
		}
		
		for(const price of priceData) {
			if(this.session.currency && price.currency !== this.session.currency) {
				continue;
			}

			for(const countryPrice of price.amountsByCountry || []) {
				if(!countryPrice.countryCode || (typeof(countryPrice.countryCode) === "string" ? countryPrice.countryCode !== this.session.country : !countryPrice.countryCode.includes(this.session.country))) {
					continue;
				}
				lowestPrice = processPrice(lowestPrice, countryPrice, price.currency);
			}
		}

		if (includeSurcharges === undefined || includeSurcharges){
			// now including surcharges by default when we find them.
			for(const price of priceData) {
				for(const surchargePrice of price.surcharges || []) {
					if(surchargePrice.regionCode !== this.session.region) {
						continue;
					}
					lowestSurcharge = processPrice(lowestSurcharge, surchargePrice, price.currency);
				}
			}
		}

		if(!lowestPrice) {
			for(const price of priceData) {
				if(price.currency !== this.session.currency) {
					continue;
				}
				for(const defaultPrice of price.defaultAmounts || []) {
					lowestPrice = processPrice(lowestPrice, defaultPrice, price.currency);
				}
			}
		}

		if (lowestSurcharge && lowestPrice){
			//console.log("Including a surcharge:", JSON.stringify(lowestSurcharge));
			lowestPrice.amount += lowestSurcharge.amount;
		}

		if(!lowestPrice && (NOPRICES === true || NOPRICES?.includes(this.session.country))){
			return {amount: 0, currency: this.session.currency}
		}

		return lowestPrice;
	}

	private formatPrice(price: BasicPrice | undefined, negate?: boolean) : string | undefined {
		return formatPrice(price, this.session.locale, negate);
	}

	getModelType() : string {
		return this.state.model.split("_")[0];
	}

	getModelRangeName(isOverridden : boolean = true) : string {
		let modelName = this.state.model.split("_")[1];
		if(window.overrideVehicles){
			if(window.overrideVehicles.shouldApply && isOverridden){
				modelName = window.overrideVehicles.model || modelName;
			}
		}
		return modelName;
	}

	getModelYear(isOverridden : boolean = true) : string {
		let year = this.state.year;
		if(window.overrideVehicles){
			if(window.overrideVehicles.shouldApply && isOverridden){
				year = window.overrideVehicles.year || year;
			}
		}
		return year;
	}

	getModelName(): string {
		return this.getTranslation(this.getUnitModel().name);
	}

	getModelSegment(): string {
		return this.state.segment;
	}

	getSegment(model: string, type?: V2.VehicleType): string {
		/*type = type || undefined;
		
		if(type && type.id){
			let segment = type.id;
			if(/\_/g.test(segment)){
				segment = segment.split("_", 2)[1];
			}
			if(/\s/g.test(segment)){
				segment = segment.replace(/\s/g, "-");
			}
			return segment.toUpperCase();
		}else{
		*/
		return model.split("_")[0].toUpperCase();
	}
	
	getMarketingName(unitModel?: V2.UnitModel, unit?: V2.Unit): string {
		unit = unit || this.getUnit();
		if(unit && unit.name) {
			return this.getTranslation(unit.name);
		}
		unitModel = unitModel || this.getUnitModel();
		return this.getTranslation(unitModel.packageLabel);
	}

	getDefaultMarketingImage(): string {
		let um = this.getUnitModel();
		if (um && um.defaultImage){
			return this.getQualifiedUrl(this.getTranslatedUrl(um.defaultImage)) || "";
		}
		return "";
	}

	getModelKeyFeatures() : string[] {
		const keyFeatures = this.getUnit()?.keyFeatures || this.getUnitModel()?.keyFeatures || [] as V2.I18nString[]
		const keyFeatureStrings = keyFeatures.map(f => this.getTranslation(f)).filter(f => f) as string[];
		return keyFeatureStrings.filter((f, i) => keyFeatureStrings.indexOf(f) === i);
	}

	getPlatformId(real: boolean = false) : string {
		if(real) {
			const unit = this.getUnit();
			if(unit && unit.platform) {
				return unit.platform;
			}
		}
		return this.state.model;
	}

	getPackageId() : string {
		return this.state.package;
	}

	private _getDefaultHomologation() : string {
		if(typeof(DEFAULT_HOMOLOGATION) === "string") {
			return DEFAULT_HOMOLOGATION;
		}
		return DEFAULT_HOMOLOGATION[this.session.country] || "";
	}

	getHomologationIdFromUnit(unit : V2.Unit) {
		const homologationFeature = unit.bundledFeatures?.find(f => f.id?.includes("homologation"));
		if(!homologationFeature?.id?.endsWith("none")) {
			const parts = homologationFeature?.id?.split("_");
			if(parts) {
				return parts[parts?.length-1].toUpperCase();
			}
		}
		return this._getDefaultHomologation();
	}

	getHomologationId() : string {
		return this.state.homologation || this._getDefaultHomologation();
	}

	getModelSku(): string {
		if (this.state){
			return this.state.unitsku;
		}
		return "";
	}

	getModifiedAt() : number {
		return Date.parse(this.state.modified);
	}
	
	isOutOfStock(): boolean {
		if(IGNORE_OUT_OF_STOCK) {
			return false;
		}
		const unit = this.getUnit();
		if(unit.soldOutRegions) {
			return unit.soldOutRegions.includes(this.session.locale)
		}
		return unit.outOfStockCountries?.includes(this.session.country) === true;
	}
	

	sanitizeFeatureCode(featureCode: string, code: string){
		switch (featureCode){
			case this.configurableFeatureCategories[FCI.Color]:{
				return code; //historically was doing .replace("color_", "").split(/_._/)[0]; // split is to cope with colour codes like "b_253_b_325_b_350" (not always 'b_', sometimes 'm_')
			}
			case this.configurableFeatureCategories[FCI.Engine]:
			{
				let index = code.toLowerCase().indexOf("engine");
				if(index >= 0){
					let codeStart = index + 7;
					code = code.substr(codeStart);
				}else{
					console.log("Was expecting to find the word engine in the code " + code + " but didn't?")
				}
				return code;
			}
			case "BRP_LYN_TRACK":
			{
				let index = code.toLowerCase().indexOf("track");
				if(index >= 0){
					let codeStart = index + 6;
					code = code.substr(codeStart);
				}else{
					console.log("Was expecting to find the word track in the code " + code + " but didn't?")
				}
				return code;
			}
			case "BRP_LYN_STARTER":
			{
				let index = code.toLowerCase().indexOf("starter");
				if(index >= 0){
					let codeStart = index + 8;
					code = code.substr(codeStart);
				}else{
					console.log("Was expecting to find the word starter in the code " + code + " but didn't?")
				}
				return code;
			}
			case "BRP_SEA_SEATING_CAPACITY":
			{
				let index = code.toLowerCase().indexOf("capacity");
				if(index >=0){
					let codeStart = index + 9;
					code = code.substr(codeStart);
				}else{
					console.log("Was expecting to find the word starter in the code " + code + " but didn't?");
				}
				return code;
			}
			case "BRP_SEA_IBR":
			{
				if(code && code.endsWith("_none")){
					let index = code.toLowerCase().indexOf("ibr");
					if(index >= 0){
						let codeStart = index;
						code = code.substr(codeStart);
					}else{
						console.log("Was expecting to find the word ibr in the code " + code + " but didn't?");
					}
				}else{
					let index = code.toLowerCase().indexOf("ibr");
					if(index >=0){
						let codeStart = index + 4;
						code = code.substr(codeStart);
					}else{
						console.log("Was expecting to find the word ibr in the code " + code + " but didn't?");
					}
				}
				return code;
			}
			case "BRP_SEA_HULL":
			{
				let index = code.toLowerCase().indexOf("hull");
				if(index >=0){
					let codeStart = index + 5;
					code = code.substr(codeStart);
				}else{
					console.log("Was expecting to find teh word hull in the code " + code + " but didn't?");
				}
				return code;
			}
			case "BRP_SEA_DECK":
			{
				let index = code.toLowerCase().indexOf("deck");
				if(index >=0){
					let codeStart = index + 5;
					code = code.substr(codeStart);
				}else{
					console.log("Was expecting to find teh word deck in the code " + code + " but didn't?");
				}
				return code;
			}
			default:
			{
				if(code && code.endsWith("_none")) {
					let type = code.split("_")
					type = type.slice(1, type.length - 1);
					if(type.length === 0) {
						return code
					}
					else if(type.length === 1) {
						return type + "0";
					}
					else {
						return type[0][0] + "0";
					}
				}
				else {
					if(featureCode === "BRP_LYN_COOLER_OPTION") {
						return code.split("lyn_cooler_option_")[1];
					}
					else if(featureCode === "BRP_LYN_KIT_OPTION") {
						return code.split("lyn_kit_option_")[1];
					}
					return code.split("_").reverse()[0];
				}
			}
		}
	}

	getFeatureCode(featureCategory: string, nosanitise?: boolean): string {

		const feature = this.state.selected.find((s) => {
			return s.category == featureCategory && s.featureid;
		})
		
		if (feature && feature.featureid){
			if(nosanitise) {
				return feature.featureid;
			}
			else {
				return this.sanitizeFeatureCode(featureCategory, feature.featureid);
			}
			
		}
		else if(!featureCategory.startsWith(ZL_ACCESSORY_GROUP)){
			console.log("No " + featureCategory + " found in ", this.state.selected);
		}
		return "";
	}

	getColorCode(): string {
		return this.getFeatureCode(this.configurableFeatureCategories[FCI.Color]);
	}

	getTypeForItemId(id: string){
		const unit = this.getUnit();
		let found = this.getAccessories(unit).find(a => a.id === id);
		if (found && found.type && found.type.id){
			return found.type.id;
		}
		return undefined;
	}

	getCategoryForItemId(id: string) : string[] | undefined {
		const unit = this.getUnit();
		let found = this.getAccessories(unit).find(a => a.id === id);
		if (found && found.category){
			return found.category.map(c => c.id as string);
		}
		return undefined;
	}

	buildFamilyCache(unitModel: V2.UnitModel) : FamilyCache {
		let engCatName = this.configurableFeatureCategories[FCI.Engine]
		
		const validUnits = this.getValidUnits(unitModel, undefined, undefined, true)
		const engines : {[id: string]: number} = {};
		validUnits.forEach(vu => {
			const engine = vu.unit.bundledFeatures?.find(f => f?.type?.id === engCatName);
			if(engine?.id) {
				engines[engine.id] = (engine.id in engines) ? Math.max(engines[engine.id], vu.price) : vu.price;
			}
		})

		const orderedPricing = Array.from(new Set(Object.values(engines))).sort((l,r) => l - r);
		const multiplier = orderedPricing.length < 3 ? 2 : 1;
		Object.keys(engines).forEach(e => engines[e] = orderedPricing.indexOf(engines[e]) * multiplier);

		const featureOutOfStock : {[featureId: string]: boolean} = {}
		validUnits.forEach(vu => {
			const outOfStock = !IGNORE_OUT_OF_STOCK && (vu.unit.soldOutRegions?.includes(this.session.locale) || vu.unit.outOfStockCountries?.includes(this.session.country));
			vu.unit.bundledFeatures?.forEach(f => {
				if(f.id) {
					featureOutOfStock[f.id] = (featureOutOfStock[f.id] ?? true) && (outOfStock ?? false);
				}
			})
		})

		return {modelId: unitModel.id as string, validUnits, featureOutOfStock, engineBrackets: engines}
	}

	getEngineBracket(id: string): number {
		if(this.familyCache?.engineBrackets[id] !== undefined) {
			return this.familyCache?.engineBrackets[id];
		}

		return -1;	// not an engine!
	}

	getMarketingSummary(): string {
		const um = this.getUnitModel();
		if (um.packageDescription){
			return this.getTranslation(um.packageDescription);
		}
		if (this.data.description){
			return this.getTranslation(this.data.description);
		}
		
		return ""; 
	}

	getPRCodes(stripIncludedAccessories?: boolean) : string[] {
		const unit = this.getUnit();
		let platformId = this.getPlatformId();
		if(unit && unit.platform) {
			platformId = unit.platform;
		}

		let ignoreAttachPoints = this.config ? this.config.ignoreAttachPoints === true : process.env.REACT_APP_IGNORE_ATTACH_POINTS === "true";
		let ignoreIncludedAccessories = this.config?.optimise === true;

		//Will need expanding to include location information
		let baseOptions = [unit.id as string, platformId, this.getPackageId(), this.getHomologationId(), this.getCountryCode()];
		
		// keep feature codes (color/engine) at the start 
		let features = [];
		let unattachedAcc = [];
		let attachedAcc = [];

		for (let s=0;s<this.state.selected.length;s++) {
			let o = this.state.selected[s];
			if (o.featureid) {
				features.push(o.featureid);
				const bodgedFeatureCode = this.sanitizeFeatureCode(o.category, o.featureid);
				if(o.featureid !== bodgedFeatureCode) {
					features.push(bodgedFeatureCode);
				}
			}
			else {
				if(o.accessorysku && (!o.locked || !ignoreIncludedAccessories || !stripIncludedAccessories)) {
					if(o.attached?.length) {
						attachedAcc.push(o.accessorysku);
					}
					else if(ignoreAttachPoints) {
						unattachedAcc.push(o.accessorysku);
					}
				}
			}
		}
		return baseOptions.concat(features, ["MY" + this.getModelYear(false)], unattachedAcc, attachedAcc).map(prcode => REMAP_PRCODES[prcode] || prcode);
	}

	private getUnitModel(id?:string): V2.UnitModel {
		id = id || this.state.package;
		
		if(this.data && this.data.unitModels) {
			let unitModel = this.data.unitModels.find(um => um.id === id);
			if(!unitModel && ALLOW_FALLBACK) {
				//console.log("Fallback - returning first model");
				unitModel = this.data.unitModels[0];
			}
			if(unitModel) {
				return unitModel;
			}
		}
		
		throw Error("Unknown Unit Model: " + id + " : " + (this.data.unitModels || []).map(um => um.id));
	}

	private getUnit(id?:string, modelId?:string): V2.Unit {
		id = id || this.state.unitsku;
		const um = this.getUnitModel(modelId);
		if(um.units) {
			let unit = um.units.find(u => u.id === id);
			if(!unit && ALLOW_FALLBACK) {
				console.log("Fallback - returning first units");
				unit = um.units[0];
			}
			if(unit) {
				return unit;
			}
		}
		throw Error("Unknown Unit: " + um.id + "." + id)
	}

	
	getOption = (id:string) => {
		try {
			return this.getAccessoryItemBySku(this.getUnit(), id)
		}
		catch {
			return this.getFeatureItemById(id);
		}
	}

	getAllOptions() : Item[] {
		let options : Item[] = [];
		const unitModel = this.getUnitModel()
		for(const unit of unitModel.units || []) {
			for(const option of unit.bundledFeatures || []) {
				const category = option.type && option.type.id;
				if(category) {
					let fi = this.getFeatureItem(option);
					let validUnits = this.getValidUnits(unitModel, [option.id ? option.id:""], undefined, true);
					
					if (validUnits.length > 0){
						options.push(fi);
					}
				}
			}
		}
		
		//TODO: Filter Unique

		const unit = this.getUnit();
		for(const accessory of this.getAccessories(unit)) {
			options.push(this.getAccessoryItem(accessory));
		}

		return options;
	}

	private getUnitFeatures(unitModel: V2.UnitModel, requiredFeatures?: string[]) : V2.UnitFeature[] {
		if(unitModel.unitFeatureFamily && unitModel.unitFeatureFamily.length > 0) {
			/*Never seen this in actual data samples, assuming it is the unique set of bundled features for all the units*/
			return unitModel.unitFeatureFamily
		}

		/*Fallback: generate set manually*/
		let found : string[] = []
		let features : V2.UnitFeature[] = []
		for(const unit of this.getValidUnits(unitModel, requiredFeatures, undefined, true) || []) {
			for(const feature of unit.unit.bundledFeatures || []) {
				if(feature.id && feature.type && !found.includes(feature.id)) {
					features.push(feature)
					found.push(feature.id)
				}
			}
		}
		return features;
	}

	sortOptions(sortType: string|undefined, options: Item[]) {
		if(!sortType) {
			return;
		}

		if(sortType.includes("numerical")) {
			options.sort((l, r) => parseFloat(l.name) - parseFloat(r.name));
		}
		else if(sortType.includes("textual")) {
			options.sort((l, r) => l.name.localeCompare(r.name))
		}

		if(sortType.includes("reverse")) {
			options.reverse();
		}
	}

	getOptions(parentGroupId?:string, maxTopLevelItems:number=3, userFilter?: (item: Item) => boolean, userFilterCollapse?: number) : Item[] {

		let options: Item[] = [];
		const unitModel = this.getUnitModel();

		const requiredFeatures = [...this.state.selected.filter(o => o.featureid !== undefined && (o.locked || (BLOCKED_OPTION_GROUPS.includes(o.category)))).map(o => o.featureid as string)];
		const unitFeatures = this.getUnitFeatures(unitModel, requiredFeatures);
		if(!parentGroupId || parentGroupId === ZL_OPTIONS) {
			let types:string[] = [];

			let categoryOptions : {[key:string]: string[]}= {}
			for(const option of unitFeatures) {
				if(option.type && option.id) {
					const category = option.type.id;
					if(!category || EXCLUDED_OPTION_GROUPS.includes(category)) {
						continue;
					}
					if(!types.includes(category)) {
						types.push(category);
						categoryOptions[category] = []

						options.push({
							id: category,
							name: this.getTranslation(option.type.name),
							categoryId: parentGroupId,
							description: this.getTranslation(option.type.description),
							itemType: ItemType.Group
						});
					}
					
					categoryOptions[category].push(option.id);
				}
			}
			
			const availableBundleIDs = this.getBundleIds(this.getUnit(), true)
			if(availableBundleIDs.length > 0) {
				maxTopLevelItems += 1
			}
			const availableAccessoryIDs = this.getAccessoryIDs(this.getUnit(), undefined, true, true)
			const availableTopLevelSlots = maxTopLevelItems > 0 ? maxTopLevelItems - (Math.min(availableAccessoryIDs.length, 1) + Math.min(availableBundleIDs.length, 1)) : options.length;
			const availableOptionCategories = Object.keys(categoryOptions).filter(category => {
				const options = new Set(categoryOptions[category]);
				if(options.size === 1 && Array.from(options)[0].endsWith("_none")) {
					return false;
				}
				return !CONDITIONAL_OPTION_GROUPS.includes(category) || options.size > 1
			});

			options = options.filter(o => availableOptionCategories.includes(o.id));

			options.sort((l,r) => (OPTION_SORT_KEYS[l.id] || 0) - (OPTION_SORT_KEYS[r.id] || 0))
			
			if(!parentGroupId) {
				if(options.length > availableTopLevelSlots) {
					options = options.slice(0, availableTopLevelSlots - 1);
					options.push({
						id: ZL_OPTIONS,
						name: "OPTIONS",
						categoryId: parentGroupId,
						itemType: ItemType.Group
					})
				}

				if(availableBundleIDs.length > 0) {
					options.push({
						id: ZL_BUNDLES,
						name: "BUNDLES",
						categoryId: parentGroupId,
						selected: this.state.selected.reduce((count, option) => (count + (option.accessorysku !== undefined && !Object.values(OPTION_GROUP_REDIRECT).find(ro => ro.includes(option.category)) && availableBundleIDs.includes(option.accessorysku) ? option.quantity : 0)), 0),
						itemType: ItemType.Group
					})
				}

				if(options.length < maxTopLevelItems && availableAccessoryIDs.length > 0) {
					options.push({
						id: ZL_ACCESSORIES,
						name: "ACCESSORIES",
						categoryId: parentGroupId,
						selected: this.state.selected.reduce((count, option) => (count + (option.accessorysku !== undefined && !Object.values(OPTION_GROUP_REDIRECT).find(ro => ro.includes(option.category)) && availableAccessoryIDs.includes(option.accessorysku) && !HIDDEN_ACCESSORY_GROUPS.includes(option.category) ? option.quantity : 0)), 0),
						itemType: ItemType.Group
					})
				}
			}
			else {
				options = options.slice(availableTopLevelSlots - 1);
				if(parentGroupId === ZL_OPTIONS) {
					options.sort((l,r) => l.name.localeCompare(r.name))
				}
			}
		}
		else if(unitFeatures.find((optionGroup) => (optionGroup.type && optionGroup.type.id === parentGroupId)) !== undefined) {
			for(const option of unitFeatures) {
				if(option.type && option.type.id === parentGroupId) {
					let validUnits = this.getValidUnits(unitModel, [option.id ? option.id : ""], undefined, true);
					if (validUnits.length > 0){
						options.push(this.getFeatureItem(option))
					}
				}
			}

			//Hide alternative options if were locked to a specific one
			const lockedOptions = options.filter(o => this.state.selected.find(i => i.featureid === o.id)?.locked === 1)
			if(lockedOptions.length > 0) {
				options = lockedOptions;
			}

			if(options.every(o => o.sortKey !== undefined)) {
				options.sort((l,r) => (l.sortKey as number) - (r.sortKey as number))
			}
			else if(parentGroupId === this.configurableFeatureCategories[FCI.Engine]) {
				//Hack to sort engines by class
				options.sort((l,r) => this.getEngineBracket(l.id) - this.getEngineBracket(r.id));
			}
			else {
				this.sortOptions(SORT_FEATURE_OPTIONS[parentGroupId], options)
			}

		}
		else if(parentGroupId === ZL_BUNDLES) {
			const unit = this.getUnit();
			options = this.getAccessories(unit, undefined, true).filter(a => isBundle(a)).map(a => this.getAccessoryItem(a))
		}
		else if(parentGroupId === ZL_ACCESSORIES || parentGroupId.startsWith(ZL_ACCESSORY_GROUP)) {
			let filter : string[] | undefined = undefined;
			if(parentGroupId !== ZL_ACCESSORIES) {
				filter = parentGroupId.split(";").pop()!.split(",");
			}
			
			const unit = this.getUnit();
			
			let numAccessories = 0
			const groupedAccessories = this.getAccessories(unit).reduce((c, a) => {
				if(a.type?.id && HIDDEN_ACCESSORY_GROUPS.includes(a.type.id)) {
					return c
				}
				if(a.type?.id && !(filter && !filter.includes(a.type.id)) && !isBundle(a) && userFilter?.(this.getAccessoryItem(a)) !== false) {
					if(!c[a.type.id]) {
						c[a.type.id] = []
					}
					c[a.type.id].push(a)
					++numAccessories
				}
				return c;
			}, {} as {[group: string]: V2.Accessory[]})
			
			if((userFilter && userFilterCollapse && (userFilterCollapse >= numAccessories || userFilterCollapse < 0 && Object.keys(groupedAccessories).length < -userFilterCollapse)) || UNGROUP_ACCESSORIES) {
				options = Object.values(groupedAccessories).reduce((c, g) => ([...c, ...(g.map(a => this.getAccessoryItem(a)))]), [] as Item[]);
			}
			else {
				let groups: {[key:string]: Item} = {};
				for(const groupId of Object.keys(groupedAccessories)) {
					const accessories = groupedAccessories[groupId]
					const subtotalPriceValue = {
						amount: accessories.reduce((subtotal, item) => {
							const selected = this.state.selected.find(o => o.accessorysku === item.id);
							if(selected) {
								const price = this.getPrice(item.prices)
								if(price?.amount) {
									return subtotal + ((selected.quantity - selected.locked) * price.amount)
								}
							}
							return subtotal
						}, 0),
						currency: this.session.currency
					}
					groups[groupId] = {
						id: groupId,
						categoryId: groupId,
						name: this.getTranslationFromList(accessories.map(a => a.type?.name)),
						description: this.getTranslationFromList(accessories.map(a => a.type?.description)),
						selected: accessories.reduce((p, c) => p + (this.state.selected.find(o => o.accessorysku === c.id)?.quantity || 0), 0),
						locked: accessories.reduce((p, c) => p + (this.state.selected.find(o => o.accessorysku === c.id)?.locked || 0), 0),
						validAttachPoints: accessories.map(a => a.id as string),
						isNew: accessories.some(a => isNew(a)),
						itemType: ItemType.Group,
						subtotalPriceValue,
						subtotalPrice: formatPrice(subtotalPriceValue, this.session.locale)
					};
				}
				options = Object.values(groups)
			}

			if(filter) {
				options = options.sort((l,r) => (filter?.indexOf(l.id) || 0) - (filter?.indexOf(r.id) || 0));
			}
			else if(SORT_CATEGORIES) {
				options = options.sort((l,r) => l.name.localeCompare(r.name))
			}
		}
		else {
			const unit = this.getUnit();
			for(const accessory of this.getAccessories(unit, undefined, true)) {
				if(accessory.type && accessory.type.id === parentGroupId && !isBundle(accessory)) {
					const a = this.getAccessoryItem(accessory)
					if(userFilter?.(a) !== false) {
						options.push(a);
					}
				}
			}

			if(SORT_ACCESSORIES) {
				options.sort((lhs, rhs) => {
					const lhsContains = Math.min(lhs.contains?.length || 0, 1);
					const rhsContains = Math.min(rhs.contains?.length || 0, 1);
					if(lhsContains !== rhsContains) {
						return rhsContains - lhsContains;
					}
					if((lhs.validAttachPoints?.length || 0) > 0 && (rhs.validAttachPoints?.length || 0) > 0) {
						const categoryOrder = lhs.validAttachPoints?.[0]?.localeCompare(rhs.validAttachPoints?.[0] || "") || 0;
						if(categoryOrder !== 0) {
							return categoryOrder;
						}
					}
					return lhs.name.localeCompare(rhs.name);
				})
			}
		}

		options = options.filter((o) => {
			//Remove redirected options from the normal location
			return parentGroupId?.startsWith(ZL_ACCESSORY_GROUP) || !Object.values(OPTION_GROUP_REDIRECT).find(ro => ro.includes(o.id));
		}).map((o) => {
			//Update id's of existing items to reference the redirects
			const redirects = OPTION_GROUP_REDIRECT[o.id];
			if(redirects && redirects.length > 0) {
				const redirect = redirects.length === 1 ? redirects[0] : ZL_ACCESSORY_GROUP + ";" + o.id + ";" + redirects.join(",")
				const redirectedOptions = this.getOptions(redirect);
				if(redirectedOptions.length > 0) {
					return {...o, id: redirect, selected: redirectedOptions.reduce((t, o) => t + (o.selected || 0), 0)};
				}
			}
			return o;
		});

		return options;
	}

	isFeeApplicable(fee: V2.Fee, when: number) {
		if(fee.currency !== this.session.currency) {
			//console.log("Ignoring fee because its of the wrong currency");
			return false;
		}
		if(!fee.type || !fee.type.id) {
			//console.log("Ignoring fee because its of the wrong type")
			return false;
		}
		if(fee.region && (fee.region.country !== this.session.country || (fee.region.region && fee.region.region !== this.session.region))) {
			//console.log("Ignoring fee because its only for a different region")
			return false;
		}
		if(!fee.amount){
			//console.log("Ignoring fee because it has no amount")
			return false;
		}
		if(!checkValidityPeriod(fee, when)) {
			//console.log("Ignoring fee because it not valid at the moment")
			return false;
		}
		return true
	}

	getApplicableFee(type: string) : BasicPrice | null {
		let lowest : V2.Fee | null = null;
		const unit = this.getUnit();
		const now = today();
		for(const fee of unit.fees || []) {
			if(this.isFeeApplicable(fee, now)) {
				if(fee.type?.id !== type) {
					//console.log("Ignoring fee because its of the wrong type")
					continue;
				}
				if(!lowest || lowest.amount! > fee.amount!) {
					lowest = fee;
				}
			}
		}
		return lowest as BasicPrice;
	}

	getApplicableFees() : V2.Fee[] {
		let feeTypes: {[key: string] : V2.Fee} = {};
		const unit = this.getUnit();
		const now = today();
		for(const fee of unit.fees || []) {
			if(!this.isFeeApplicable(fee, now)) {
				continue;
			}
			if(!feeTypes[fee.type!.id!] || feeTypes[fee.type!.id!].amount! > fee.amount!) {
				feeTypes[fee.type!.id!] = fee;
			}
		}
		return Object.values(feeTypes);
	}

	private sumPrices(prices: BasicPrice[]) : BasicPrice | undefined {
		const total = prices.reduce((total, current) => {
			if(total && current) {
				if(total.currency !== current.currency) {
					throw "Attempting to add different currencies";
				}
				if(current.amount === 0) {
					return total;
				}
				total.amount += current.amount;
			}
			return total;
		}, {amount: 0, currency: this.session.currency});

		total.amount = parseFloat(total.amount.toFixed(2));

		return total;
	}

	getTotalPrice(add?: Item[], remove?: Item[], unitId?: string) : {subtotal: BasicPrice, total: BasicPrice} | undefined {
		const unit = this.getUnit(unitId);
		const emptyPrice = {amount: 0, currency: this.session.currency}
		const unitPrice = this.getPrice(unit.prices) || emptyPrice

		let selectedItems = this.state.selected;
		if(remove && add){
			selectedItems = this.updateSelected(selectedItems, {add: add.map(a => a.id), remove: remove.map(a => a.id), splitBundles: [], targetUnitId:unitId}, "skipattach")
		}

		const accessoryPrices = []
		for(const accessory of this.getAccessories(unit)) {
			const selected = selectedItems.find(o => o.accessorysku === accessory.id);
			if(selected) {
				if(isKit(accessory) || isBundle(accessory)) {
					//Don't double count, the individual accessories are already added
					//if they ever have their own prices to offer a discount, the discount should be deducted here
					continue;
				}
				const price = this.getAccessoryPrice(accessory);
				if(price) {
					for(let i = selected.locked; i < selected.quantity; ++i) {
						accessoryPrices.push(price);
					}
				}
			}
		}

		const subtotal = this.sumPrices([this.getPrice(unit.prices, false) || emptyPrice, ...accessoryPrices]) as BasicPrice;

		//const fees = this.getApplicableFees() as BasicPrice[];

		const total = this.sumPrices([unitPrice, ...accessoryPrices/*, ...fees*/]) as BasicPrice;

		return {subtotal, total};
	}

	getSelectedFeatures(featureFamilyId:string): Item[]  {
		const unit = this.getUnit();
		const groupFeatures = (unit.bundledFeatures || []).filter(feature => feature.type && feature.type.id === featureFamilyId)
		return groupFeatures.map((feature) : Item => (
			{
				id: feature.id || "",
				categoryId: feature.type && feature.type.id,
				name: this.getTranslation(feature.name),
				description: this.getTranslation(feature.description),
				price: undefined,
				icon: undefined,
				selected: 1,
				itemType: ItemType.Feature
			}
		));
	}

	getPricingDetails(financeParameters?: FinanceParameters, add?: Item[], remove?: Item[], unitId?: string) : {subtotalValue: BasicPrice, totalValue: BasicPrice, monthlyValue?: BasicPrice} | undefined {
		const price = this.getTotalPrice(add, remove, unitId);
		if(!price) {
			return undefined;
		}

		if(!financeParameters) {
			return {subtotalValue: price.subtotal, totalValue: price.total};
		}

		const {downpayment, tradeIn, months, apr} = financeParameters;
		let monthlyPayment : number;
		if(apr === 0) {
			monthlyPayment = (price.total.amount - (downpayment + tradeIn)) / months;
		}
		else {
			const rpp = (apr * 0.01) / 12;
			//Is this calculation correct, I wouldn't have expected it to devolve at 0% APR
			monthlyPayment = (rpp * (price.total.amount - (downpayment + tradeIn))) / (1 - Math.pow(1 + rpp, -months));
		}

		monthlyPayment = Math.max(monthlyPayment, 0);

		const monthlyValue = {amount: monthlyPayment, currency: price.total.currency}
		return {
			subtotalValue: price.subtotal,
			totalValue: price.total,
			monthlyValue
		};
	}

	getPricingInfo(financeParameters?: FinanceParameters, add?: Item[], remove?: Item[], unitId?: string) : FinanceDetails | undefined {
		const details = this.getPricingDetails(financeParameters, add, remove, unitId);
		if(!details) {
			return undefined;
		}

		return {
			subtotal: this.formatPrice(details.subtotalValue),
			total: this.formatPrice(details.totalValue),
			monthly: this.formatPrice(details.monthlyValue),
			subtotalValue: details.subtotalValue.amount,
			totalValue: details.totalValue.amount,
			monthlyValue: details.monthlyValue?.amount,
			currency: details.totalValue.currency
		};
	}

	getSummary(includeIncluded?: boolean) : SummaryDetails {
		const unit = this.getUnit();
		const unitPrice = this.getPrice(unit.prices, false);
		const accessories = this.getAccessories(unit);
		
		const options : {id: string, items: Item[], name: string, subtotal: string | undefined}[] = []
		const optionPrices : BasicPrice[] = []
		
		for(const feature of unit.bundledFeatures || []) {
			if(!feature?.type?.id || EXCLUDED_OPTION_GROUPS.includes(feature.type.id)) {
				continue
			}
			
			let items : Item[] = []
			if(OPTION_GROUP_REDIRECT[feature.type.id]) {
				for(const redirect of OPTION_GROUP_REDIRECT[feature.type.id]) {
					items = items.concat(this.getOptions(redirect))
				}
			}
			if(items.length == 0) {
				items = this.getOptions(feature.type.id);
			}

			if((items.length <= 1 && CONDITIONAL_SUMMARY_GROUPS.includes(feature.type.id)) || (items.length === 1 && items[0].id.endsWith("_none"))) {
				continue;
			}

			items = items.filter(v => (v.selected || 0) > 0);
			const itemPrices = items.reduce((p, c) => {
				const item = accessories.find(a => a.id == c.id);
				if(item) {
					const price = this.getAccessoryPrice(item);
					if(price) {
						return [...p, price];
					}
				}
				return p;
			}, [] as BasicPrice[]);
				
			options.push({
				id: feature.type.id,
				items: items,
				name: this.getTranslation(feature.type.name),
				subtotal: this.formatPrice(this.sumPrices(itemPrices))
			});
			optionPrices.push(...itemPrices)
		}
		
		let groupedSelectedAccessories : {[key: string] : {name: string, items: Item[]}} = {};
		let accessoryPrices : BasicPrice[] = [];
	
		let accSources = [];
		if(includeIncluded === undefined || includeIncluded){
			accSources.push(this.getIncludedAccessories(unit));
		}

		accSources.push(accessories);
		for (let s=0;s<accSources.length;s++){
			for(const accessory of accSources[s] || []) {
				let accessorytype: V2.AccessoryType = (accessory.type && accessory.type.id) ? accessory.type : {id: ""};

				if(Object.values(OPTION_GROUP_REDIRECT).find(r => r.includes(accessorytype.id || ""))) {
					continue;
				}
				
				const selected = this.state.selected.find(o => o.accessorysku === accessory.id);
				if(selected) {
					if(groupedSelectedAccessories[accessorytype.id || ""] === undefined) {
						groupedSelectedAccessories[accessorytype.id || ""] = {
							name: this.getTranslation(accessorytype.name),
							items: []
						};
					}
					groupedSelectedAccessories[accessorytype.id || ""].items.push(this.getAccessoryItem(accessory));
					if(s == (accSources.length - 1) && !isKit(accessory) && !isBundle(accessory)) {
						const price = this.getAccessoryPrice(accessory);
						if(price) {
							for(let i = selected.locked; i < selected.quantity; ++i) {
								accessoryPrices.push(price);
							}
						}
					}
				}
			}
		}

		const applicableFees = this.getApplicableFees();
		const fees = applicableFees.map((f) => ({name: this.getTranslation(f.type && f.type.name), price: this.formatPrice(f as BasicPrice)}));
		const optsum = this.sumPrices(optionPrices)
		const accsum = this.sumPrices(accessoryPrices);

		const subtotalprice = this.sumPrices([unitPrice || {amount: 0, currency: this.session.currency}, ...optionPrices, ...accessoryPrices]) as BasicPrice;

		const totalprice = this.sumPrices([this.getPrice(unit.prices) || {amount: 0, currency: this.session.currency}, ...optionPrices, ...accessoryPrices/*, ...applicableFees.map(f => f as BasicPrice)*/]);

		return {
			package: {
				id: unit.id as string,
				name: this.getMarketingName(),
				features: this.getModelKeyFeatures(),
				price: this.formatPrice(unitPrice),
				rawprice: unitPrice,
				copy: this.getMarketingSummary(),
				platformId: this.getPlatformId(),
				modelYear: this.getModelYear()
			},
			options: {
				groups: options,
				subtotal: this.formatPrice(optsum),
				rawsubtotal: optsum
			},
			accessories: {
				groups: Object.values(groupedSelectedAccessories),
				subtotal: this.formatPrice(accsum),
				rawsubtotal: accsum,
			},
			fees: {
				items: fees,
				subtotal: this.formatPrice(this.sumPrices(applicableFees as BasicPrice[]))
			},
			subtotalprice: this.formatPrice(subtotalprice),
			totalprice: this.formatPrice(totalprice),
			rawsubtotalprice: subtotalprice,
			rawtotalprice: totalprice,
			outOfStock: !IGNORE_OUT_OF_STOCK && (unit.soldOutRegions?.includes(this.session.locale) || unit.outOfStockCountries?.includes(this.session.country)),
			prcodes: this.getPRCodes(true)
		}
	}

	private getQualifiedUrl(path?:string) : string | undefined {
		if(path) {
			if(path.includes(" ")) {
				if(OUTPUT_DATA_WARNINGS) {
					console.warn("Unencoded URL: " + path);
				}
				path = encodeURI(path);
			}
			if(path.startsWith("http://") || path.startsWith("https://")) {
				return path;
			}
			return this.session.server + path; 
		}
		return undefined;
	}

	private getValidUnits(model: V2.UnitModel, requiredFeatures?:Array<string>, desiredFeatures?: Array<Selected>, includeOutOfStock: boolean=false) : {unit: V2.Unit, price: number, matches: Selected[]}[] {
		if(!requiredFeatures?.length && !desiredFeatures?.length && this.familyCache?.modelId === (model.id as string) && includeOutOfStock) {
			return this.familyCache.validUnits;
		}

		let validUnits = [];
		const units = (this.familyCache && this.familyCache.modelId === (model.id as string)) ? this.familyCache?.validUnits.map(vu => vu.unit) : model.units || [];
		let blacklistFailures = [];
		let dateFailures = [];
		let countryfailures = [];
		let priceFailures = [];
		let stockFailures = []
		const now = today();

		for(const unit of units) {
			if((this.config?.blacklist || []).includes(unit.id || "")) {
				blacklistFailures.push(unit.id);
				continue;
			}

			if(!checkValidityPeriod(unit, now)) {
				dateFailures.push(unit.id);
				continue;
			}
			if(unit.availableCountries && !(sanitizeCountryList(unit.availableCountries)).includes(this.session.country)){
				countryfailures.push(unit.id);
				continue;
			}
			const price = this.getPrice(unit.prices)
			if(!price) {
				priceFailures.push(unit.id);
				continue;
			}
			if(!IGNORE_OUT_OF_STOCK && !includeOutOfStock && (unit.soldOutRegions?.includes(this.session.locale) || unit.outOfStockCountries?.includes(this.session.country))) {
				stockFailures.push(unit.id);
				continue
			}

			//Should check for region surcharges here

			const features = (unit.bundledFeatures || []);
			if(requiredFeatures) {
				let hasRequiredFeatures = true;
				for(const requiredFeature of requiredFeatures) {
					if(!features.find(f => f.id === requiredFeature)) {
						hasRequiredFeatures = false;
						break;
					}
				}
				if(!hasRequiredFeatures) {
					continue;
				}
			}

			let matches:Array<Selected> = [];
			if (desiredFeatures){
				matches = desiredFeatures.filter((f:Selected) => features.find(bf => bf.id === f.featureid));
			}
				
			validUnits.push({unit: unit, matches, price: price.amount});
		}
		if(DEBUG){
			// log unit failures per category
			if(blacklistFailures.length > 0){
				console.log("Unit blacklist failures: ", blacklistFailures);
			}
			if(dateFailures.length > 0){
				console.log("Unit date failures: ", dateFailures);
			}
			if(countryfailures.length > 0){
				console.log("Unit country failures: ", countryfailures);
			}
			if(priceFailures.length > 0){
				console.log("Unit price failures: ", priceFailures);
			}
			if(stockFailures.length > 0) {
				console.log("Unit out of stock failures: ", stockFailures);
			}
		}
		return validUnits;
	}

	private simplifyResolverStates(add: ResolverState, remove: ResolverState) {
		for(const sku of Object.keys(remove)) {
			if(add[sku]) {
				if(remove[sku] > add[sku]) {
					remove[sku] = remove[sku] - add[sku]
					delete add[sku]
				}
				else if(add[sku] > remove[sku]) {
					add[sku] = add[sku] - remove[sku]
					delete remove[sku]
				}
				else {
					delete add[sku]
					delete remove[sku]
				}
			}
		}
	}

	private async setOption(category:string, id:string) {
	
		const prevUnit = this.getUnit();

		if(this.state.selected.find(s => s.featureid === id)) {
			return; //Already selected
		}
		
		const requiredFeatures = [id, ...this.state.selected.filter(o => o.featureid !== undefined && BLOCKED_OPTION_GROUPS.includes(o.category)).map(o => o.featureid as string)];

		//Try and find another unit model that contains the selected features
		const desiredFeatures = this.state.selected.filter(o => o.featureid !== undefined && o.category !== category && !EXCLUDED_OPTION_GROUPS.includes(o.category));

		let validUnits = this.getValidUnits(this.getUnitModel(), requiredFeatures, desiredFeatures);

		if(validUnits.length === 0) {
			console.error("No available sku contains the requested feature");
			this.onConflict(this.generateConflictDetails(Action.ChangedFeature, id, []));
			return;
		}

		if(validUnits.some(u => u.matches.length === desiredFeatures.length)) {
			//If we find any units matching all the desired features, don't even bother checking compatibility on any others - Potentially Lossy?
			validUnits = validUnits.filter(u => u.matches.length === desiredFeatures.length);
		}

		validUnits.sort((r,l)=>(l.matches.length - r.matches.length));

		await Promise.allSettled(validUnits.map(vu => this.ensureAccessoriesReady(vu.unit.id as string))).then(() => {
			let availableAccessories : {[unitId: string]: V2.Accessory[]} = {}
			let includedAccessories : {[unitId:string]: V2.IncludedAccessory[]} = {}
			for(const target of validUnits) {
				if(target.unit.id) {
					includedAccessories[target.unit.id] = this.getIncludedAccessories(target.unit);
					availableAccessories[target.unit.id] = this.getAccessories(target.unit, undefined, true);
				}
			}
			if(prevUnit.id && !includedAccessories[prevUnit.id]) {
				includedAccessories[prevUnit.id] = this.getIncludedAccessories(prevUnit)
				availableAccessories[prevUnit.id] = this.getAccessories(prevUnit, undefined, true);
			}

			const ignorableAccessoryAdds : {[unitsku: string]: ResolverState} = {}
			const ignorableAccessoryRemoves : {[unitsku: string]: ResolverState} = {}
			let solutions = validUnits.map((target) => {
				if(!target.unit.id) {
					throw new Error("Invalid unit");
				}
				const targetUnitId = target.unit.id;
				const prevFeatures = prevUnit.bundledFeatures || [];
				const nextFeatures = target.unit.bundledFeatures || [];
				
				const addFeatures = nextFeatures.filter(f => f && f.id && !prevFeatures.find(bf => bf.id===f.id)).reduce((p, c) => Object.assign(p, {[c.id as string]: 1}), {} as ResolverState);
				const removeFeatures = prevFeatures.filter(f => f && f.id && !nextFeatures.find(bf => bf.id===f.id)).reduce((p, c) => Object.assign(p, {[c.id as string]: 1}), {} as ResolverState);

				//Add/Update any included in package accessories
				const addAccessories : ResolverState = {};
				const removeAccessories : ResolverState = {};
				for(const accessory of includedAccessories[targetUnitId]) {
					if(accessory.id) {
						const expected = getIncludedCount(accessory);
						const existing = this.state.selected.find(s => s.accessorysku === accessory.id)
						const current = existing ? existing.locked : 0;
						if(current > expected) {
							removeAccessories[accessory.id] = current - expected;
						}
						else if(current < expected) {
							addAccessories[accessory.id] = expected - current;
						}
					}
				}

				//Remove any included in package accessories that aren't included on the new package at all
				this.state.selected.forEach(s => {
					if(s.accessorysku !== undefined && s.locked > 0) {
						const included = includedAccessories[targetUnitId].some(a => a.id === s.accessorysku);
						if(!included) {
							removeAccessories[s.accessorysku] = s.locked + (removeAccessories[s.accessorysku] || 0)
						}
					}
				})

				ignorableAccessoryAdds[targetUnitId] = {...addAccessories}
				ignorableAccessoryRemoves[targetUnitId] = {...removeAccessories}

				//Remove any accessories not available on the new unit (ignoring any locked copies as these should have been handled above) and expanding any bundles/kits
				this.state.selected.forEach(s => {
					if(s.accessorysku !== undefined && s.quantity > s.locked) {
						const available = availableAccessories[targetUnitId].some(a => a.id === s.accessorysku);
						if(!available) {
							const previousLoose = this.getAccessoryBreakdown(s).loose
							removeAccessories[s.accessorysku] = (previousLoose) + (removeAccessories[s.accessorysku] || 0);
							const accessory = availableAccessories[prevUnit.id as string].find(a => a.id === s.accessorysku)
							if(accessory && accessory.accessoryPackage?.length) {
								accessory.accessoryPackage.forEach(i => removeAccessories[i.id] = i.quantity + (removeAccessories[i.id] || 0))
								
								if(IGNORE_KIT_BUNDLE_REMOVAL_ON_FEATURE_CHANGE) {
									ignorableAccessoryRemoves[targetUnitId][accessory.id as string] = s.quantity + (ignorableAccessoryRemoves[targetUnitId]?.[accessory.id as string] || 0)
									accessory.accessoryPackage.forEach(i => ignorableAccessoryRemoves[targetUnitId][i.id as string]  = i.quantity + (ignorableAccessoryRemoves[targetUnitId][i.id as string] || 0))
								}
							}
						}
					}
				})
				
				this.simplifyResolverStates(addAccessories, removeAccessories)

				const solutions = this.resolveBreadthFirst(target.unit, {...addFeatures, ...addAccessories}, {...removeFeatures, ...removeAccessories}, this.getResolverState(this.getCurrentState()), this.buildAccessoriesList(target.unit));
				if(solutions) {
					return solutions.reduce((p, c) => Object.assign(p, {[this.getSolutionKey(c)]: c}), {} as KeyedSolutions)
				}
				return undefined
			}).reduce(this.mergeSolutions, {} as KeyedSolutions);

			const filteredSolutions = Object.values(this.filterSolutions(solutions));

			const autoApply = filteredSolutions.find(s => {
				//Should ignore changes to locked items too?
				const filterConfigurable = (unit: V2.Unit, changes: ResolverState, ignore: ResolverState = {}) => {
					return Object.keys(changes).filter((id) => {
						if(changes[id] === ignore[id]) {
							return false;
						}
						if((unit.availableAccessories || []).find(a => a.id === id)) {
							return true;
						}
						if((unit.bundledFeatures || []).find(f => f.id === id && f.type && f.type.id && !EXCLUDED_OPTION_GROUPS.includes(f.type.id))) {
							return true;
						}
						return false;
					});
				}
				if(!s.targetUnitId) {
					throw new Error("Invalid Unit");
				}
				const nextUnit = this.getUnit(s.targetUnitId);
				const importantAdds = filterConfigurable(nextUnit, this.getResolverState(s.add), ignorableAccessoryAdds[nextUnit.id as string])
				const importantRemoves = filterConfigurable(prevUnit, this.getResolverState(s.remove), ignorableAccessoryRemoves[nextUnit.id as string])

				return importantAdds.length === 1 && importantRemoves.length === 1;
			});
			if(autoApply) {
				this.applySolution(Action.ChangedFeature, this.getFeatureItemById(id), autoApply)
			}
			else {
				this.onConflict(this.generateConflictDetails(Action.ChangedFeature, id, filteredSolutions, includedAccessories))
			}
		})
	}

	private mergeSolutions(existing: KeyedSolutions, toAdd: KeyedSolutions | undefined) {
		if(toAdd) {
			Object.assign(existing, toAdd);
		}
		return existing;
	}

	private resolveDepthFirst(unit: V2.Unit, state: string[], incompatible: string[], required: string[], solutions: KeyedSolutions, attaching: boolean=false, validating: boolean=false, restrictions?: Restrictions) : KeyedSolutions | undefined {
		for(const incompatibility of incompatible) {
			const count = state.filter(id => id === incompatibility).length
			if(count > 0) {
				let updatedSolutions : KeyedSolutions = {};
				for(const solutionKey of Object.keys(solutions)) {
					const solution = solutions[solutionKey];
					const resolutions = this.resolveRemove(unit, [...state], incompatibility, count, solution.add, solution.remove, required, attaching, solutionKey, restrictions);
					this.mergeSolutions(updatedSolutions, resolutions);
				}
				if(Object.keys(updatedSolutions).length === 0) {
					return undefined;
				}
				solutions = updatedSolutions
			}
		}

		for(const requirement of required) {
			const requirementCount = state.filter(id => id === requirement).length
			if(requirementCount === 0 || validating) {
				let updatedSolutions : KeyedSolutions = {};
				for(const solutionKey of Object.keys(solutions)) {
					const solution = solutions[solutionKey]
					const resolutions = this.resolveAdd(unit, [...state, ...(validating ? required : [])], requirement, validating ? 0 : 1, solution.add, solution.remove, attaching, solutionKey, restrictions);
					this.mergeSolutions(updatedSolutions, resolutions);

					if(validating && (!resolutions || Object.keys(resolutions).length > 1 || (Object.keys(resolutions).length > 0 && (Object.values(resolutions)[0].add != solution.add || Object.values(resolutions)[0].remove != solution.remove)))) {
						const resolutions = this.resolveRemove(unit, [...state], requirement, requirementCount, solution.add, solution.remove, undefined, attaching, solutionKey, restrictions)
						this.mergeSolutions(updatedSolutions, resolutions);
					}
				}
				
				if(Object.keys(updatedSolutions).length === 0) {
					return undefined;
				}
				solutions = updatedSolutions;
			}
		}

		return solutions;
	}

	private getAccessories(unit: V2.Unit, category?: string, onlyAvailable?: boolean) : V2.Accessory[] {
		let accessoryCache = this.accessoryCache;
		if(!accessoryCache || unit.id !== this.state.unitsku) {
			accessoryCache = this.buildAccessoriesList(unit);
			if(!accessoryCache) {
				return [];
			}
		}

		if(onlyAvailable) {
			return accessoryCache.available.orderedItems;
		}

		if(category) {
			//category = category?.toLowerCase();
			return accessoryCache.categorised[category]?.orderedItems || [];
		}
		return accessoryCache.all.orderedItems;
	}

	private getAccessoryIDs(unit: V2.Unit, category?: string, onlyAvailable?: boolean, ignoreBundles: boolean=false) : string[] {
		if(ignoreBundles) {
			const bundleIds = this.getBundleIds(unit)
			return this.getAccessoryIDs(unit, category, onlyAvailable, false).filter(id => !bundleIds.includes(id))
		}

		let accessoryCache = this.accessoryCache;
		if(!accessoryCache || unit.id !== this.state.unitsku) {
			accessoryCache = this.buildAccessoriesList(unit);
			if(!accessoryCache) {
				return [];
			}
		}

		if(onlyAvailable) {
			return Object.keys(accessoryCache.available.items);
		}

		if(category) {
			//category = category?.toLowerCase();
			if(!accessoryCache.categorised[category]) {
				//console.error("Attempting to get accessories for non-existent category " + category);
				return []
			}
			return Object.keys(accessoryCache.categorised[category].items);
		}
		return Object.keys(accessoryCache.all.items);
	}

	private getBundleIds(unit: V2.Unit, onlyAvailable?: boolean) {
		return this.getAccessories(unit, undefined, onlyAvailable).filter(a => isBundle(a)).map(a => a.id as string);
	}

	private buildAccessoriesList(unit: V2.Unit, blacklist?: Blacklist) : AccessoryCache {
		//Should just do this on unit change and cache the results
		const allAccessories = [...(unit.availableAccessories || []), ...(unit.includedAccessories || [])];
		let uniqueAccessories = allAccessories.filter((a, index) => allAccessories.findIndex(b => b.id === a.id) === index);
		
		//Ignore errors with included accessories
		const includedAccessoryIds = this.getIncludedAccessories(unit, true).map(a => a.id); 

		//Filter accessories that are blacklisted
		const blacklistSet = new Set((blacklist || this.config?.blacklist || []).filter(r => {
			if(typeof(r) !== "string") {
				if(r.country) {
					if(!r.country.map(c => c.toLowerCase())?.includes(this.session.country.toLowerCase())) {
						return false;
					}
				}
				if(!processLogic([unit.id as string, unit.platform || this.getPlatformId(), this.getPackageId()], r)) {
					return false;
				}
			}
			return true;
		}).map(r => typeof(r) === "string" ? r : r.code));
		uniqueAccessories = uniqueAccessories.filter(a => {
			if(blacklistSet.has(a.id || "") || blacklistSet.has(a.type?.id || "")) {
				if(IGNORE_INCLUDED_ACCESSORY_VALIDATION && includedAccessoryIds.includes(a.id)) {
					if(OUTPUT_DATA_WARNINGS) {
						console.log("Ignoring override blacklisting on " + this.getTranslation(a.name) + " (" + a.id + ") because its included with the unit " + unit.id)
					}
				}
				else {
					//console.log("Removing " + this.getTranslation(a.name) + " (" + a.id + ") due to override blacklist", a, blacklistSet);
					return false;
				}
			}
			return true;
		});

		//Filter accessories that aren't currently available
		const now = today();
		uniqueAccessories = uniqueAccessories.filter(a => {
			if(!checkValidityPeriod(a, now)) {
				if(IGNORE_INCLUDED_ACCESSORY_VALIDATION && includedAccessoryIds.includes(a.id)) {
					if(OUTPUT_DATA_WARNINGS) {
						console.log("Ignoring valitity period on " + this.getTranslation(a.name) + " (" + a.id + ") because its included with the unit " + unit.id)
					}
				}
				else {
					//console.log("Removing " + this.getTranslation(a.name) + " (" + a.id + ") due to validity dates", a);
					return false;
				}
			}
			return true;
		})

		//Filter accessories that aren't available in the current country
		uniqueAccessories = uniqueAccessories.filter(a => {
			if(sanitizeCountryList(a.excludedCountries).includes(this.session.country)) {
				if(IGNORE_INCLUDED_ACCESSORY_VALIDATION && includedAccessoryIds.includes(a.id)) {
					if(OUTPUT_DATA_WARNINGS) {
						console.log("Ignoring excludedCountries blacklist on " + this.getTranslation(a.name) + " (" + a.id + ") because its included with the unit " + unit.id)
					}
				}
				else {
					if(!ALLOW_FALLBACK) {
						//console.log("Removing " + this.getTranslation(a.name) + " (" + a.id + ") due to excludedCountries blacklist", a);
					}
					return false;
				}
			}
			if(a.includedCountries && !sanitizeCountryList(a.includedCountries).includes(this.session.country)) {
				if(IGNORE_INCLUDED_ACCESSORY_VALIDATION && includedAccessoryIds.includes(a.id)) {
					if(OUTPUT_DATA_WARNINGS) {
						console.log("Ignoring includedCountries whitelist on " + this.getTranslation(a.name) + " (" + a.id + ") because its included with the unit " + unit.id)
					}
				}
				else {
					if(!ALLOW_FALLBACK) {
						//console.log("Removing " + this.getTranslation(a.name) + " (" + a.id + ") due to includedCountries whitelist", a);
					}
					return false;
				}
			}
			return true;
		})

		//Remove any dependencies on included accessories, presume they are already valid
		uniqueAccessories.forEach(a => {
			if(includedAccessoryIds.includes(a.id)) {
				a.dependencies = undefined;
			}
		})

		//Filter accessories that have unresolvable dependencies
		let availableAccessoryIds = uniqueAccessories.map(a => a.id);
		let accessoryCharacteristics = uniqueAccessories.reduce((o, a) => ({...o, [a.id || ""]: a.category?.map(c => c.id || "") || []}), {} as {[key: string]: string[]})
		availableAccessoryIds.push(NONE);
		let nextAvailableAccessoryIds = [...availableAccessoryIds]
		let pass = 0;
		while(availableAccessoryIds.length > 0) {
			for(const a of uniqueAccessories) {
				if(!availableAccessoryIds.includes(a.id)) {
					continue;
				}
				if(a.dependencies) {
					let remove2Way = false;
					let remove3Way = false;
					let reasons = []
					if(a.dependencies.twoWay) {
						for(const groupName of Object.keys(a.dependencies.twoWay)) {
							const group = a.dependencies.twoWay[groupName];
							const availableDeps = group.filter(d => availableAccessoryIds.includes(d));
							if(availableDeps.length === 0) {
								remove2Way = true;
								reasons.push("No accessories available to resolve twoWay: " + groupName + " : " + group);
							}
							else if(group.includes(NONE) && a.category?.find((g) => g.id == groupName) && !group.includes(a.id as string)) {
								remove2Way = true;
								reasons.push("Self exclusion based on twoWay: " + groupName + " : " + group)
							}
							else if(!availableDeps.some(d => d===NONE || accessoryCharacteristics[d].includes(groupName))) {
								remove2Way = true;
								reasons.push("Accessory has wrong characteristic to resolve twoWay: " + groupName + " : " + availableDeps.map(d => d + "=" + accessoryCharacteristics[d].join(",")).join(" - "));
							}
						}
					}

					const exclusiveDependencies = a.dependencies.threeWay || [];
					if(exclusiveDependencies && exclusiveDependencies.length > 0) {
						
						type Pair = [string, string];
						type Group = {characteristics:Pair, constraints: V2.AccessoryDependenciesThreeWay[][]};
						const groups = exclusiveDependencies.reduce((prev, tw) => {
							const valid = (availableAccessoryIds.includes(tw[0].accessory) && availableAccessoryIds.includes(tw[1].accessory));
						
							const characteristics = tw.map(r => r.characteristic || "").sort() as Pair
							for(const set of prev) {
								if(set.characteristics[0] === characteristics[0] && set.characteristics[1] === characteristics[1]) {
									if(valid) {
										set.constraints.push(tw);
									}
									//else {
									//	console.log("Ignoring constraint: ", tw)
									//}
									return prev;
								}
							}
							return [...prev, {characteristics, constraints: (valid ? [tw] : [])}]
						}, [] as Group[])

						remove3Way = groups.some(g => g.constraints.length === 0);
						
						if(remove3Way) {
							reasons.push("No accessories to resolve threeWay: " + JSON.stringify(a.dependencies.threeWay))
						}
						else {
							let failed = new Set<string>()
							remove3Way = groups.some(g => g.constraints.every(tw => tw.some(r => {
								const invalid = r.accessory !== NONE && !accessoryCharacteristics[r.accessory || ""]?.includes(r.characteristic || "")
								if(invalid) {
									failed.add(`${r.accessory} does not have characteristic ${r.characteristic}`)
								}
								return invalid
							})));
							if(remove3Way) {
								reasons.push("Accessories with characteristic mismatches threeWay: " + JSON.stringify(Array.from(failed), null, 2))
							}
						}
					}
					if(remove2Way || remove3Way) {
						if(IGNORE_ALL_ACCESSORY_VALIDATION) {
							if(OUTPUT_DATA_WARNINGS) {
								console.log("Ignoring dependency errors on " + this.getTranslation(a.name) + " (" + a.id + ")")
							}
						}
						else if(IGNORE_INCLUDED_ACCESSORY_VALIDATION && includedAccessoryIds.includes(a.id)) {
							if(OUTPUT_DATA_WARNINGS) {
								console.log("Ignoring dependency errors on " + this.getTranslation(a.name) + " (" + a.id + ") because its included with the unit " + unit.id)
							}
						}
						else {
							if(OUTPUT_DATA_WARNINGS) {
								if(pass === 0) {
									console.warn("Removing " + this.getTranslation(a.name) + " (" + a.id + ") because it has unsatisfiable dependencies", {accessory: a, reasons});
								}
								else {
									console.warn("Removing " + this.getTranslation(a.name) + " (" + a.id + ") because it depends on other items that have been removed due to unsatisfiable dependencies", {accessory: a, reasons});
								}
							}
							nextAvailableAccessoryIds.splice(nextAvailableAccessoryIds.indexOf(a.id), 1);
						}
					}
				}
			}
			if(availableAccessoryIds.length === nextAvailableAccessoryIds.length) {
				break;
			}
			availableAccessoryIds = nextAvailableAccessoryIds;
			pass += 1;
		}

		const items = uniqueAccessories.filter(a => availableAccessoryIds.includes(a.id) || includedAccessoryIds?.includes(a.id));
		const availableItems = uniqueAccessories.filter(a => availableAccessoryIds.includes(a.id))
		let accessoryCache : AccessoryCache = {
			all: {
				items: items.reduce((p,c) => Object.assign(p, {[c.id as string]: c}), {} as {[sku: string]: V2.Accessory}),
				orderedItems: items,
			},
			available: {
				items: availableItems.reduce((p,c) => Object.assign(p, {[c.id as string]: c}), {} as {[sku: string]: V2.Accessory}),
				orderedItems: availableItems,
			},
			categorised: {}
		};
		for(const item of items) {
			if(!availableAccessoryIds.includes(item.id)) {
				continue;
			}
			for(const category of item.category || []) {
				if(category.id) {
					if(!accessoryCache.categorised[category.id]) {
						accessoryCache.categorised[category.id] = {items: {}, orderedItems: []};
					}
					accessoryCache.categorised[category.id].items[item.id as string] = item;
					accessoryCache.categorised[category.id].orderedItems.push(item);
				}
			}
		}

		console.assert(Object.keys(accessoryCache.categorised).length === new Set(Object.keys(accessoryCache.categorised).map(c => c.toLowerCase())).size, "Mixed case characteristics", Object.keys(accessoryCache.categorised));
		
		let skusForCharacteristics = Object.keys(accessoryCache.categorised).reduce((prev, characteristic) => {
			prev[characteristic as string] = Object.keys(accessoryCache!.categorised[characteristic].items);
			return prev;
		}, {} as {[characteristic: string]: string[]});

		for(const accessory of accessoryCache.all.orderedItems) {
			if(!accessory.dependencies || accessory.dependencies.optimised) {
				continue;
			}
			
			if(accessory.dependencies.twoWay) {
				//Remove duplicates
				for(const characteristic of Object.keys(accessory.dependencies.twoWay)) {
					accessory.dependencies.twoWay[characteristic] = accessory.dependencies.twoWay[characteristic].filter((sku, index) => (accessory.dependencies?.twoWay || {})[characteristic].indexOf(sku) === index)
				}
			}

			if(accessory.dependencies.threeWay) {
				
				if(DEBUG_SKUS.includes(accessory.id as string)) {
					console.debug(`${accessory.id} Unoptimised threeWay`, {twoWay: {...(accessory.dependencies?.twoWay || {})}, threeWay: [...(accessory.dependencies?.threeWay || [])]})
				}

				const newDeps = this.unrollThreeWay(accessory.id, Object.keys(accessoryCache.all.items), skusForCharacteristics, accessory.dependencies.threeWay);		

				if(DEBUG_SKUS.includes(accessory.id as string)) {
					console.debug(`${accessory.id} Unrolled threeWay`, newDeps)
				}

				accessory.dependencies.threeWay = newDeps.threeWay;
				if(newDeps.twoWay && Object.keys(newDeps.twoWay).length > 0) {
					if(accessory.dependencies.twoWay) {
						for(const characteristic of Object.keys(newDeps.twoWay)) {
							if(accessory.dependencies.twoWay[characteristic]) {
								accessory.dependencies.twoWay[characteristic] = accessory.dependencies.twoWay[characteristic].filter(sku => (newDeps.twoWay || {})[characteristic].includes(sku))
								console.assert(accessory.dependencies.twoWay[characteristic].length > 0, "Conflicting twoWay & threeWay dependencies: " + this.getTranslation(accessory.name) + " ("+ accessory.id + ")", accessory)
							}
							else {
								accessory.dependencies.twoWay[characteristic] = newDeps.twoWay[characteristic]
							}
						}
					}
					else {
						accessory.dependencies.twoWay = newDeps.twoWay
					}
				}
			}
			if(accessory.dependencies.twoWay) {
				for(const characteristic of Object.keys(accessory.dependencies.twoWay)) {
					accessory.dependencies.twoWay[characteristic] = accessory.dependencies.twoWay[characteristic].filter(sku => sku === NONE || accessoryCache?.all.items[sku] !== undefined);
					if(accessory.dependencies.twoWay[characteristic].length === (skusForCharacteristics[characteristic]?.length || 0) + 1) {
						delete accessory.dependencies.twoWay[characteristic];
					}
				}
			}
			if(DEBUG_SKUS.includes(accessory.id as string)) {
				console.debug(`${accessory.id} Optimised threeWay`, {twoWay: {...(accessory.dependencies?.twoWay || {})}, threeWay: [...(accessory.dependencies?.threeWay || [])]})
			}
			accessory.dependencies.optimised = true;
		}

		//Filter out any accessories we don't know about from bundles & kits
		uniqueAccessories.forEach(a => {
			if(a.accessoryPackage) {
				a.accessoryPackage = a.accessoryPackage.filter(i => {
					if(!availableAccessoryIds.includes(i.id)) {
						console.warn(`Removing ${i.id} from ${a.id} because its not available`)
						return false
					}
					return true;
				})
			}
		})

		accessoryCache.optimisableCategories = Object.keys(accessoryCache.categorised).filter(c => !accessoryCache?.categorised[c].orderedItems.some(i => i.category!.length > 1))

		for(const debugSku of DEBUG_SKUS) {
			console.group("Outputting dependencies for " + debugSku)
			let outputskus : string[] = []
			let pendingskus = [debugSku];
			let involvedcharacterisics : string[] = [];
			while(pendingskus.length > 0) {
				const sku = pendingskus.pop();
				if(!sku) {
					continue
				}
				outputskus.push(sku);

				const item = accessoryCache.all.items[sku];
				if(item) {
					item.category?.forEach(c => {
						if(c.id && !involvedcharacterisics.includes(c.id)){
							involvedcharacterisics.push(c.id);
						}
					})
					console.log("Deps: " + item.id + " : " + item.category?.map(c => c.id), item?.dependencies);
					for(const twoWay of Object.values(item?.dependencies?.twoWay || {})) {
						for(const twoWaySku of twoWay) {
							if(twoWaySku !== NONE && !pendingskus.includes(twoWaySku) && !outputskus.includes(twoWaySku)) {
								pendingskus.push(twoWaySku)
							}
						}
					}
					for(const threeWay of item.dependencies?.threeWay || []) {
						for(const threeWayItem of threeWay) {
							if(threeWayItem.accessory && threeWayItem.accessory !== NONE && !pendingskus.includes(threeWayItem.accessory) && !outputskus.includes(threeWayItem.accessory)) {
								pendingskus.push(threeWayItem.accessory)
							}
						}
					}
				}
				else {
					console.log("Failed to find accessory for " + sku)
				}
			}
			console.log("Involved accessories: " + outputskus.length)
			console.log("Involved characteristics: " + involvedcharacterisics.length, involvedcharacterisics)
			console.groupEnd()
		}

		//console.log("accessoryCache", accessoryCache.all.items.length, Object.keys(accessoryCache.categorised), Object.values(accessoryCache.categorised).map(c => c.items.length))
		//console.log("accessories:", accessoryCache.all.items.map(a => ({id: a.id, characteristics: a.category?.map(c => c.id), dependencies: a.dependencies})));

		return accessoryCache;
	}

	private getIncludedAccessories(unit: V2.Unit, warn: boolean=false) {
		return (unit.includedAccessories || []).filter((a) => {
			if(a.excludedCountries && sanitizeCountryList(a.excludedCountries).includes(this.session.country)) {
				if(warn) {
					console.warn(`Ignoring included accessory ${a.id} because it has ${this.session.country} in the excludedCountries list`, sanitizeCountryList(a.excludedCountries));
				}
				return false;
			}
			if(a.includedCountries && !sanitizeCountryList(a.includedCountries).includes(this.session.country)) {
				if(warn) {
					console.warn(`Ignoring included accessory ${a.id} because it doesn't have ${this.session.country} in the includedCountries list`, sanitizeCountryList(a.includedCountries));
				}
				return false;
			}
			return true;
		})
	}

	private getSolutionKey(solution: Solution) : string {
		solution.add.sort();
		solution.remove.sort();
		//return JSON.stringify(solution);
		return (solution.targetUnitId || "") + ":" + solution.add.join("") + ":" + solution.remove.join("");
	}



	private resolveExisting(unit: V2.Unit, existingState: string[], solutions: KeyedSolutions, attaching: boolean=false) : KeyedSolutions {
		//Filter solutions to unique set
		const filteredSolutions = this.filterSolutions(solutions);
		console.log(window.performance.now() + ": " + Object.keys(solutions).length + " Reduced to " + Object.keys(filteredSolutions).length + " unique");

		const features = (unit.bundledFeatures || []).map(f => f.id);
		const includedAccessories = this.getIncludedAccessories(unit).map(a => a.id);
		const previouslyAddedItems = existingState.filter(id => !features.includes(id) && !includedAccessories.includes(id));

		if(TEST_OPTIMISE_RESOLVE_EXISTING) {
			const allSolutionsOptimised = this.filterSolutions(Object.keys(filteredSolutions).reduce((allFixedSolutions, solutionKey) => {
				const solution = filteredSolutions[solutionKey];
				let toValidate = previouslyAddedItems.filter(id => !solution.remove.includes(id))
				console.log("toValidate: ", existingState, previouslyAddedItems, toValidate, solution);
				
				//Is it safe to use the knowledge about which items were still valid to reduce the toValidate set?
				toValidate = toValidate.filter(sku => !this.checkCompatibility(unit, existingState, sku))
				if(toValidate.length === 0) {
					console.log("All items already valid")
					allFixedSolutions[this.getSolutionKey(solution)] = solution;
					return allFixedSolutions;
				}
				console.log("Not all items are still valid", toValidate)
	
				let newState = [...existingState, ...solution.add]
				for(const id of solution.remove) {
					newState.splice(newState.indexOf(id), 1); //Can't use filter because we only want to remove one instance per remove code
				}
	
				console.log("newState: ", newState);
				const fixedSolutions = this.resolveDepthFirst(unit, newState, [], toValidate, {[this.getSolutionKey(solution)]: solution}, attaching, true, {});
				console.log("Resolved existing for " + solutionKey)
				this.mergeSolutions(allFixedSolutions, fixedSolutions);
				return allFixedSolutions;
			}, {} as KeyedSolutions));

			const allSolutions = Object.keys(filteredSolutions).reduce((allFixedSolutions, solutionKey) => {
				const solution = filteredSolutions[solutionKey];
				let toValidate = previouslyAddedItems.filter(id => !solution.remove.includes(id))
				console.log("toValidate: ", existingState, previouslyAddedItems, toValidate, solution);
	
				let newState = [...existingState, ...solution.add]
				for(const id of solution.remove) {
					newState.splice(newState.indexOf(id), 1); //Can't use filter because we only want to remove one instance per remove code
				}
	
				console.log("newState: ", newState);
				const fixedSolutions = this.resolveDepthFirst(unit, newState, [], toValidate, {[this.getSolutionKey(solution)]: solution}, attaching, true, {});
				console.log("Resolved existing for " + solutionKey)
				this.mergeSolutions(allFixedSolutions, fixedSolutions);
				return allFixedSolutions;
			}, {} as KeyedSolutions);

			const unoptimisedSolutions = this.filterSolutions(allSolutions)
			if(Object.keys(unoptimisedSolutions).length !== Object.keys(allSolutionsOptimised).length || Object.keys(allSolutionsOptimised).some(k => unoptimisedSolutions[k] === undefined)) {
				console.log("allSolutions: ", unoptimisedSolutions);
				console.log("optimisedSolutions: ", allSolutionsOptimised);
				alert("Optimised solution not the same as unoptimised solutions")
			}
		}

		const allSolutions = Object.keys(filteredSolutions).reduce((allFixedSolutions, solutionKey) => {
			const solution = filteredSolutions[solutionKey];
			let toValidate = previouslyAddedItems.filter(id => !solution.remove.includes(id))
			console.log("toValidate: ", existingState, previouslyAddedItems, toValidate, solution);

			if(OPTIMISE_RESOLVE_EXISTING) {
				//Is it safe to use the knowledge about which items were still valid to reduce the toValidate set?
				toValidate = toValidate.filter(sku => !this.checkCompatibility(unit, existingState, sku))
				if(toValidate.length === 0) {
					console.log("All items already valid")
					allFixedSolutions[this.getSolutionKey(solution)] = solution;
					return allFixedSolutions;
				}
				console.log("Not all items are still valid", toValidate)
			}

			let newState = [...existingState, ...solution.add]
			for(const id of solution.remove) {
				newState.splice(newState.indexOf(id), 1); //Can't use filter because we only want to remove one instance per remove code
			}

			console.log("newState: ", newState);
			const fixedSolutions = this.resolveDepthFirst(unit, newState, [], toValidate, {[this.getSolutionKey(solution)]: solution}, attaching, true, {});
			console.log("Resolved existing for " + solutionKey)
			this.mergeSolutions(allFixedSolutions, fixedSolutions);
			return allFixedSolutions;
		}, {} as KeyedSolutions);

		console.log(window.performance.now() + " Resolved existing items " + Object.keys(allSolutions).length + " solutions");

		return allSolutions;
	}

	private unrollThreeWay(sku: string|undefined, availableSkus: (string|undefined)[], skusForCharacteristics: {[key:string]: string[]}, threeWay:V2.AccessoryDependenciesThreeWay[][]): V2.AccessoryDependencies {
		if(threeWay.some(tw => tw.length != 2)) {
			return {threeWay} //Already unrolled
		}

		type Pair = [string, string];
		type Group = {characteristics:Pair, constraints: V2.AccessoryDependenciesThreeWay[][]};
		let groups = threeWay.reduce((prev, tw) => {
			const characteristics = tw.map(r => r.characteristic || "").sort() as Pair
			const valid = (tw[0].accessory === NONE || availableSkus.includes(tw[0].accessory)) && (tw[1].accessory === NONE || availableSkus.includes(tw[1].accessory));
			for(const set of prev) {
				if(set.characteristics[0] === characteristics[0] && set.characteristics[1] === characteristics[1]) {
					if(valid) {
						if(!set.constraints.find(existing => existing[0].characteristic === tw[0].characteristic && existing[0].accessory === tw[0].accessory && existing[1].characteristic === tw[1].characteristic && existing[1].accessory === tw[1].accessory)) {
							set.constraints.push(tw);
						}
					}
					return prev;
				}
			}
			return [...prev, {characteristics, constraints: valid ? [tw] : []}]
		}, [] as Group[])

		if(groups.some(g => g.constraints.length === 0)) {
			console.error(`${sku} Unsatisfiable Dependencies`, threeWay)
			return {threeWay: []};
		}

		//console.log(sku + " Original 3 Way:", threeWay)

		//Filter constraints that offer all combinations (including None)?
		groups = groups.filter(group => {
			const max = ((skusForCharacteristics[group.characteristics[0]]?.length || 0) + 1) * ((skusForCharacteristics[group.characteristics[1]]?.length || 0) + 1)
			return group.constraints.length < max;
		})

		//If there is no overlap on either characteristic with other groups, and the group allows every combination of the specified ids, convert them to twoWays
		let twoWay : {[key:string]: string[]} = {};
		const characteristicUsage = groups.reduce((usage: {[key: string]: Set<string>}, group) => {
			for(const characteristic of group.characteristics) {
				if(usage[characteristic] === undefined) {
					usage[characteristic] = new Set<string>();
				}
				usage[characteristic].add(group.characteristics.join());
			}
			return usage;
		}, {})

		if(DEBUG_SKUS.includes(sku as string)) {
			console.debug(`${sku} Characteristic Usage`, characteristicUsage);
		}

		let characteristicVariants = groups.reduce((variants:{[key:string]: Set<string>}, group) => {
			for(const constraintPair of group.constraints) {
				for(const constraint of constraintPair){
					if(variants[constraint.characteristic as string] === undefined) {
						variants[constraint.characteristic as string] = new Set<string>();
					}
					variants[constraint.characteristic as string].add(constraint.accessory as string);
				}
			}
			return variants;
		}, {});

		if(DEBUG_SKUS.includes(sku as string)) {
			console.debug(`${sku} Characteristic Variants`, characteristicVariants);
		}
		
		groups = groups.filter(group => {
			if(characteristicUsage[group.characteristics[0]].size > 1 || characteristicUsage[group.characteristics[1]].size > 1) {
				return true; //Unsafe to do this before the unroll if there are multiple things that use these characteristics
			}
			const max = (characteristicVariants[group.characteristics[0]].size || 0) * (characteristicVariants[group.characteristics[1]].size || 0)
			if(group.constraints.length === max) {
				twoWay[group.characteristics[0]] = Array.from(characteristicVariants[group.characteristics[0]])
				twoWay[group.characteristics[1]] = Array.from(characteristicVariants[group.characteristics[1]])
				return false;
			}
			return true;
		})

		const allCharacteristics = threeWay.reduce((prev, curr) => [...prev, ...curr.map(r => r.characteristic || "")], [] as Array<string>);
		const characteristicCounts = allCharacteristics.reduce((totals, characteristic) => ({...totals, [characteristic]: (totals[characteristic] || 0) + 1 }), {} as {[key:string]:number});
		
		const sortedGroups = groups.sort((lhs:Group, rhs:Group) => {
			return (characteristicCounts[lhs.characteristics[0]] * characteristicCounts[lhs.characteristics[1]]) - (characteristicCounts[rhs.characteristics[0]] * characteristicCounts[rhs.characteristics[1]]);
		});
		//console.log("Groups: ", sortedGroups)
		const step = (remainingGroups: Group[], solutions: V2.AccessoryDependenciesThreeWay[][]) : V2.AccessoryDependenciesThreeWay[][] => {
			const group = remainingGroups[0];
			if(group === undefined) {
				return solutions;
			}
			const otherGroups = remainingGroups.slice(1);
			if(solutions.length === 0) {
				return step(otherGroups, group.constraints)
			}
			let updatedSolutions: V2.AccessoryDependenciesThreeWay[][] = [];
			//console.log("Group:", group)
			for(const [c0, c1] of group.constraints) {
				for(const solution of solutions) {
					const conflict = solution.some(c => (c.characteristic === c0.characteristic && c.accessory !== c0.accessory) || (c.characteristic === c1.characteristic && c.accessory !== c1.accessory))
					if(!conflict) {
						const newCharacteristics = [c0, c1].filter(c => solution.find(ec => c.characteristic === ec.characteristic) === undefined);
						updatedSolutions = [...updatedSolutions, ...step(otherGroups, [[...solution, ...newCharacteristics]])];
					}
					else {
						//console.debug(`${sku} conflict`, solution, c0, c1);
					}
				}
			}
			return updatedSolutions;
		}
		const combinations = step(sortedGroups, []);
		
		//console.log(sku + " Expanded 3 Way:", combinations)

		//const uniqueCharacteristics = new Set(allCharacteristics);
		//console.assert(combinations.every(c => c.length == uniqueCharacteristics.size), sku + " Some combinations don't include all characteristics", uniqueCharacteristics);

		//If all threeWay's include a common dependency, convert it to a twoWay dependency
		characteristicVariants = combinations.reduce((variants:{[key:string]: Set<string>}, group) => {
			for(const constraint of group) {
				if(variants[constraint as string] === undefined) {
					variants[constraint as string] = new Set<string>();
				}
				variants[constraint as string].add(constraint.accessory as string);
			}
			return variants;
		}, {});
		for(const characteristic of Object.keys(characteristicVariants)) {
			const variants = characteristicVariants[characteristic];
			if(variants.size === 1) {
				twoWay[characteristic] = Array.from(variants);
			}
		}

		return {threeWay: combinations.map(c => c.sort((l,r) => (l.characteristic as string).localeCompare(r.characteristic as string))), twoWay};
	}

	private checkCompatibility(unit: V2.Unit, state: string[], sku: string) : boolean {
		const allAccessories = this.getAccessories(unit)

		const item = allAccessories.find(a => a.id === sku);
		if(!item) {
			return false;
		}

		state = state.filter(s => s !== sku);

		if(!item.dependencies) {
			return true;
		}
		const [commonDependancies, exclusiveDependencies] = [item.dependencies.twoWay || {}, item.dependencies.threeWay || []];

		if(commonDependancies && !DEBUG_IGNORE_2WAY) {

			const characteristics = Object.keys(commonDependancies)
			for(const category of characteristics) {
				
				const deps = commonDependancies[category];


				// if a NONE field appears, then anything else from this category not listed in this deps list would need to be removed
				if(deps.includes(NONE)) {
					// only include accessories we know about, filters out ids we don't know about
					const thiscat = deps.filter((id:string) => id !== NONE && allAccessories.find(a => a.id === id));

					const incompatible = this.getAccessoryIDs(unit, category).filter(id => !thiscat.includes(id) && state.includes(id));
					if(incompatible.length > 0) {
						return false;
					}
				}
				else if (deps.length > 0) {
					// only include accessories we know about, filters out ids we don't know about
					const thiscat = deps.filter((id:string) => id !== NONE && allAccessories.find(a => a.id === id));

					// if we already have something valid from this category, we don't need to add anything
					if (!thiscat.find((id:string) => state.includes(id))){
						return false
					}
				}
			}
		}

		if(exclusiveDependencies && exclusiveDependencies.length > 0 && !DEBUG_IGNORE_3WAY) {
			//Check to see if any of the solutions is already applied before recursively gathering them
			let resolved = false;
			for(const exclusive of exclusiveDependencies) {
				resolved = true;
				for(const category of exclusive) {
					if(!category.characteristic) {
						console.warn("Ignoring category with unspecified characteristic")
						continue;
					}

					const options = this.getAccessoryIDs(unit, category.characteristic);
					if(category.accessory === NONE) {
						if(state.some(id => options.includes(id))) {
							resolved = false;
							break;
						}
					}
					else if(category.accessory) {
						if(!state.includes(category.accessory)) {
							resolved = false;
							break;
						}
					}
				}
				if(resolved) {
					break;
				}
			}
			if(!resolved) {
				return false;
			}
		}

		return true;
	}

	private resolveAdd(unit: V2.Unit, state: string[], sku: string, quantity: number, add: string[], remove: string[], attaching: boolean, prevSolutionKey?: string, restrictions?: Restrictions) : KeyedSolutions | undefined {
		if(remove.includes(sku)) {
			return undefined; //cannot be resolved on this branch
		}

		if(DEBUG) {
			touchedAccessories.add(sku)
		}

		if(add.includes(sku)) {
			const prevSolution = {targetUnitId: unit.id, add, remove, splitBundles: []}
			return {[prevSolutionKey || this.getSolutionKey(prevSolution)]: prevSolution};
		}

		add = [...add];
		for(let i:number=0; i < quantity; ++i) {
			state.push(sku);
			add.push(sku);
		}
		
		const solution = {add, remove, targetUnitId: unit.id, splitBundles: []}
		let solutions : KeyedSolutions = {[this.getSolutionKey(solution)]: solution};

		const allAccessories = this.getAccessories(unit)

		const item = allAccessories.find(a => a.id === sku);
		if(!item) {
			//throw Error("Unknown accessory: " + sku)
			console.error("Trying to add an accessory we can't find in available list!", sku, unit.id);
			return undefined;
		}

		if(this.config?.optimise && restrictions && item.category?.length) {
			restrictions = {...restrictions, [item.category[0].id as string]: sku}
		}

		if(item.dependencies) {
			const [commonDependancies, exclusiveDependencies] = [item.dependencies.twoWay || {}, item.dependencies.threeWay || []];

			if(commonDependancies && !DEBUG_IGNORE_2WAY) {
				if(DEBUG_SKUS.includes(sku)) {
					console.log("Processing 2 way deps on " + sku, commonDependancies)
				}

				const characteristics = Object.keys(commonDependancies)
				for(const category of characteristics) {
					
					const deps = commonDependancies[category];


					// if a NONE field appears, then anything else from this category not listed in this deps list would need to be removed
					if(deps.includes(NONE)) {
						// only include accessories we know about, filters out ids we don't know about
						const thiscat = deps.filter((id:string) => id !== NONE && allAccessories.find(a => a.id === id));

						const incompatible = this.getAccessoryIDs(unit, category).filter(id => !thiscat.includes(id) && state.includes(id));
						if(incompatible.length > 0) {
							const updatedSolutions = this.resolveDepthFirst(unit, state, incompatible, [], solutions, attaching, false, restrictions);
							if(updatedSolutions === undefined) {
								return undefined;
							}
							solutions = updatedSolutions;
						}
					}
					else if (deps.length > 0) {
						// only include accessories we know about, filters out ids we don't know about
						const thiscat = deps.filter((id:string) => id !== NONE && allAccessories.find(a => a.id === id) && (!restrictions?.[category] || restrictions[category] === id));

						// if we already have something valid from this category, we don't need to add anything
						if (thiscat.find((id:string) => state.includes(id))){
							continue;
						}

						if(!this.getAccessoryIDs(unit, category).some(id => thiscat.includes(id))) {
							//console.warn("None of the required accessories are available to add " + sku, {category, required: deps, available: this.getAccessoryIDs(unit, category)})
							return undefined;
						}
						
						let updatedSolutions : KeyedSolutions = {};
						for(const option of thiscat) {
							this.mergeSolutions(updatedSolutions, this.resolveDepthFirst(unit, state, [], [option], solutions, attaching, false, restrictions));
						}
						if(Object.keys(updatedSolutions).length === 0) {
							return undefined;
						}
						solutions = updatedSolutions;
					}
				}
			}

			if(exclusiveDependencies && exclusiveDependencies.length > 0 && !DEBUG_IGNORE_3WAY) {
				//Check to see if any of the solutions is already applied before recursively gathering them
				let resolved = false;
				for(const exclusive of exclusiveDependencies) {
					resolved = true;
					for(const category of exclusive) {
						if(!category.characteristic) {
							console.warn("Ignoring category with unspecified characteristic")
							continue;
						}

						const options = this.getAccessoryIDs(unit, category.characteristic);
						if(category.accessory === NONE) {
							if(state.some(id => options.includes(id))) {
								resolved = false;
								break;
							}
						}
						else if(category.accessory) {
							if(!state.includes(category.accessory)) {
								resolved = false;
								break;
							}
						}
					}
					if(resolved) {
						break;
					}
				}

				if(!resolved) {
					if(DEBUG_SKUS.includes(sku)) {
						console.log("Processing 3 way deps on " + sku, exclusiveDependencies)
					}

					let exclusiveSolutions : KeyedSolutions = {}
					exclusiveDependencies.forEach((exclusive) => {
						let required : string[] = []
						let incompatible : string[] = []

						for(const category of exclusive) {
							if(!category.characteristic) {
								return;
							}
							if(this.config?.optimise && this.accessoryCache?.optimisableCategories?.includes(category.characteristic) && restrictions?.[category.characteristic] && restrictions[category.characteristic] !== category.accessory) {
								return;
							}
						}

						for(const category of exclusive) {
							const options = this.getAccessoryIDs(unit, category.characteristic);

							if(category.accessory === NONE) {
								incompatible = incompatible.concat(options);
							}
							else if(category.accessory) {
								if(options.includes(category.accessory)) {
									required.push(category.accessory);
								}
								else {
									try {
										console.error("Ignoring potential solution with miscategorised accessory", category, options, this.getAccessoryItemBySku(unit, category.accessory))
									}
									catch {
										console.warn("Ignoring potential solution with unknown accessory", category, options)
									}
									return;
								}
							}
							else {
								console.log("Ignoring potential solution with unspecified accessory")
								return;
							}
						}

						if(required.length > 0 || incompatible.length > 0) {
							//Should hopefully never happen, but ignore any ignored items that are explicitly required
							const originalIncompatible = [...incompatible];
							incompatible = incompatible.filter(id => !required.includes(id));
							console.assert(originalIncompatible.length === incompatible.length, "Required items incompatible", originalIncompatible, incompatible)

							const filteredSolutions = Object.keys(solutions).reduce((reducedSolutions, key) => {
								const s =  solutions[key];
								if(!(s.add.some(a => incompatible.includes(a)) || s.remove.some(r => required.includes(r)))) {
									reducedSolutions[key] = s;
								}
								return reducedSolutions;
							}, {} as KeyedSolutions)

							this.mergeSolutions(exclusiveSolutions, this.resolveDepthFirst(unit, state, incompatible, required, filteredSolutions, attaching, false, restrictions))
						}
					})
					
					if(Object.keys(exclusiveSolutions).length === 0) {
						return undefined;
					}
					solutions = exclusiveSolutions;
				}
			}
		}
		return solutions;
	}

	private getDependants(state: string[], accessories: V2.Accessory[], sku: string, ignore: string[]) : string[] {
		let dependents : string[] = []
		for(const selected of state) {
			if(ignore.includes(selected)) {
				continue;
			}
			//console.log("getDependants: Ignore:", ignore)
			const accessory = accessories.find(a => a.id === selected);
			if(accessory && accessory.dependencies) {
				const [common, exclusive] = [accessory.dependencies.twoWay || {}, accessory.dependencies.threeWay || []];
				const dependentCommon = Object.values(common).some(group => {
					return group.includes(sku) && !group.includes(NONE) && !group.some(g => state.includes(g))
				});

				//console.log("3 way dependencies:", exclusive)
				const dependantExclusive = (exclusive && exclusive.length > 0 && !exclusive.some((group/*, index*/) => {
					for(const item of group) {
						if(!item.accessory) {
							continue;
						}
						if(item.accessory === NONE) {
							//Check no items of the group exist in state
							const cgroup = accessories.filter(a => a.id && a.category && a.category.find(c => c.id === item.characteristic)).map(a => a.id) as string[];
							if(cgroup.some(a => state.includes(a) && !ignore.includes(a))) {
								//console.log("Failed to match group " + index + " based on " + item.characteristic, item, cgroup, state);
								return false;
							}
						}
						else if(!state.includes(item.accessory)) {
							//console.log("Failed to match group " + index + " based on " + item.characteristic, item, state);
							return false;
						}
					}
					return true;
				}))

				if(dependentCommon || dependantExclusive) {
					//console.log("Dependency Type", {dependentCommon, dependantExclusive})
					dependents.push(selected)
				}
			}
		}
		return dependents;
	}

	private resolveRemove(unit: V2.Unit, state: string[], sku: string, quantity: number, add: string[], remove: string[], pendingAdd: string[] = [], attaching: boolean, prevSolutionKey?: string, restrictions?: Restrictions) : KeyedSolutions | undefined {
		if(add.includes(sku)) {
			return undefined; //cannot be resolved on this branch
		}
		
		touchedAccessories.add(sku)

		if(remove.includes(sku)) {
			const prevSolution = {add, remove, targetUnitId: unit.id, splitBundles: []}
			return {[prevSolutionKey || this.getSolutionKey(prevSolution)]: prevSolution};
		}

		if(!attaching) {
			const includedAccessory = this.getIncludedAccessories(unit).find(a => a.id === sku);
			if(includedAccessory) {
				const minimum = (includedAccessory.quantityRange?.minvalue || 1);
				const available = state.filter(id => sku === id).length;
				if(available > minimum) {
					quantity = Math.min(quantity, available - minimum);
				}
				else {
					//Don't force the removal of an included accessory
					const solution = {add, remove, targetUnitId: unit.id, splitBundles: []}
					return {[this.getSolutionKey(solution)]: solution};
				}
			}
		}

		//console.log("Remove " + sku)

		remove = [...remove];
		for(let i = 0; i < quantity; ++i) {
			state.splice(state.findIndex((i) => i === sku), 1);
			remove.push(sku);
		}

		const allAccessories = this.getAccessories(unit);

		let dependents : string[] = [];
		if(!state.includes(sku)) {
			dependents = this.getDependants([...state, ...pendingAdd], allAccessories, sku, [...add, ...pendingAdd]);
			//console.log("Dependents on " + sku, dependents);
		}

		//Remove any accessories that are left in the build that don't have grouping info and are no longer depended on by anything else
		//This might be dodgy - should we only do this after processing all dependencies - after resolveExisting, otherwise future adds that try to readd it will fail
		const appliedAccessories : V2.Accessory[] = state.filter(s => !dependents.includes(s)).map(s => allAccessories.find(a => a.id === s)).filter(a => a !== undefined) as V2.Accessory[];
		const ungroupedAccessories = appliedAccessories.filter(a => a && (!a.type || !a.type.id));
		if(ungroupedAccessories.length > 0) {
			let changed = true;
			while(changed) {
				changed = false;
				for(const ua of ungroupedAccessories) {
					if(!ua || !ua.id) {
						continue;
					}
					if(this.getDependants(appliedAccessories.map(aa => aa.id || "").filter(id => id !== "" && id !== ua.id), appliedAccessories, ua.id, add).length === 0) {
						//console.log("Removing " + ua.id + " because its not needed after removing " + sku);
						dependents.push(ua.id);
						appliedAccessories.splice(appliedAccessories.indexOf(ua), 1);
						ungroupedAccessories.splice(ungroupedAccessories.indexOf(ua), 1);
						changed = true;
						break;
					}
				}
			}
		}


		const solution = {add, remove, targetUnitId: unit.id, splitBundles: []}
		let solutions : KeyedSolutions = {[this.getSolutionKey(solution)]: solution};
		if(dependents.length > 0) {
			const updatedSolutions = this.resolveDepthFirst(unit, state, dependents, [], solutions, attaching, false, restrictions);
			if(!updatedSolutions) {
				return undefined;
			}
			solutions = updatedSolutions;
		}

		return solutions;
	}

	private getFeatureItemById(id: string) : Item {
		const feature = this.getUnitFeatures(this.getUnitModel()).find(f => f.id === id);
		if(feature) {
			return this.getFeatureItem(feature);
		}
		throw new Error("Feature not found: " + id);
	}

	private getFeatureItem(feature: V2.UnitFeature) : Item {
		const selected = this.state.selected.find(o => o.featureid === feature.id);
		return {
			id: feature.id || "",
			name: this.getTranslation(feature.name),
			categoryId: feature.type && feature.type.id,
			categoryName: feature.type ? this.getTranslation(feature.type.name) : undefined,
			description: this.getTranslation(feature.description),
			price: "",
			icon: this.getQualifiedUrl(this.getTranslatedUrl(feature.imageUrl)),
			deselectable:false,
			selected: selected ? selected.quantity : 0,
			onAdd: this.locked() ? noop : async () => { if(feature.id && feature.type && feature.type.id) {await this.setOption(feature.type.id, feature.id) }},
			onClick: this.locked() ? noop : async () => { if(feature.id && feature.type && feature.type.id) {await this.setOption(feature.type.id, feature.id) }},
			analyticsName: this.getTranslation(feature.name, "en-US"),
			outOfStock: this.familyCache?.featureOutOfStock[feature.id as string],
			itemType: ItemType.Feature,
			sortKey: feature.index
		}
	}

	private getAccessoryItemBySku(unit: V2.Unit, sku:string, negatePrice?: boolean) : Item {
		const accessory = this.getAccessories(unit).find(a => a.id === sku)
		if(accessory) {
			return this.getAccessoryItem(accessory, negatePrice);
		}
		throw new Error("Accessory not found: " + sku);
	}

	private getValidAttachPoints(accessory: V2.Accessory) {
		const attachPoints = accessory.category?.map(c => c.id as string) || [];

		//Hacks to avoid item placement in locations that we haven't configured 3d for
		const remappedAttachPoints = attachPoints.map(ap => ATTACH_POINT_ALIASES[ap] || ap)
		if(remappedAttachPoints.find(ap => PREFERRED_ATTACH_POINTS.includes(ap))) {
			return PREFERRED_ATTACH_POINTS.filter(ap => remappedAttachPoints.includes(ap))
		}

		return remappedAttachPoints;
	}

	private getAccessoryPrice(accessory: V2.Accessory) : BasicPrice | undefined {
		if(accessory.accessoryPackage?.length) {
			const accessories = this.getAccessories(this.getUnit());
			const subAccessoryPrices = accessory.accessoryPackage.map(item => {
				const subAccessory = accessories.find(a => a.id === item.id);
				const unitPrice = this.getPrice(subAccessory?.prices);
				const prices = new Array(item.quantity).fill(unitPrice)
				return this.sumPrices(prices);
			}) as BasicPrice[];
			if(subAccessoryPrices.some(a => !a?.amount)) {
				return undefined;
			}
			return this.sumPrices(subAccessoryPrices)
		}
		else {
			return this.getPrice(accessory.prices)
		}
	}

	private getAccessoryBreakdown(selected: Selected | undefined) {
		const breakdown = {
			package: 0,
			kit: {} as {[id: string]: number},
			bundle: {}as {[id: string]: number},
			loose: 0
		}

		if(selected) {
			const includedInKit = this.state.selected.filter(s => s.accessorysku).map(s => this.accessoryCache?.all.items[s.accessorysku as string]).filter(a => a && isKit(a) && a.accessoryPackage?.find(p => p.id === selected.accessorysku));
			const includedInBundle = this.state.selected.filter(s => s.accessorysku).map(s => this.accessoryCache?.all.items[s.accessorysku as string]).filter(a => a && isBundle(a) && a.accessoryPackage?.find(p => p.id === selected.accessorysku));
			
			breakdown.package = selected.locked ?? 0,
			breakdown.kit = includedInKit.reduce((p, c) => Object.assign(p, {[c!.id as string]: (p[c!.id as string] ?? 0) + c!.accessoryPackage!.find(a => a.id === selected.accessorysku)!.quantity * this.state.selected.find(o => (o.accessorysku as string) === c!.id)!.quantity}), {} as {[id: string]: number}),
			breakdown.bundle = includedInBundle.reduce((p, c) => Object.assign(p, {[c!.id as string]: (p[c!.id as string] ?? 0) + c!.accessoryPackage!.find(a => a.id === selected.accessorysku)!.quantity * this.state.selected.find(o => (o.accessorysku as string) === c!.id)!.quantity}), {} as {[id: string]: number}),
			breakdown.loose = (selected.quantity || 0) - (breakdown.package + Object.values(breakdown.kit).reduce((p,c) => p + c, 0) + Object.values(breakdown.bundle).reduce((p,c) => p + c, 0))
		}

		return breakdown;
	}

	private getAccessoryItem(accessory: V2.Accessory, negatePrice?: boolean): Item {
		const selected = this.state.selected.find(o => o.accessorysku === accessory.id);
		const locked = selected && selected.locked;

		const priceValue = this.getAccessoryPrice(accessory)
		const price = this.formatPrice(priceValue, negatePrice);


		let subtotalPriceValue = priceValue && {amount: 0, currency: priceValue.currency};
		if(selected && (selected.quantity - selected.locked) > 0) {
			subtotalPriceValue = this.sumPrices(new Array(selected.quantity - selected.locked).fill(priceValue))
		}
		const subtotalPrice = this.formatPrice(subtotalPriceValue);
		
		const breakdown = this.getAccessoryBreakdown(selected)
		
		return {
			id: accessory.id || "",
			categoryId: isBundle(accessory) ? ZL_BUNDLES : accessory.type && accessory.type.id,
			categoryName: accessory.type ? this.getTranslation(accessory.type.name) : undefined,
			name: this.getTranslation(accessory.name),
			description: this.getTranslation(accessory.description),
			icon: this.getQualifiedUrl(this.getTranslatedUrl(accessory.image)),
			content: [
				...[this.getTranslatedUrl(accessory.external)].filter(i => i).map(i => ({url: i as string})),
				...[this.getQualifiedUrl(this.getTranslatedUrl(accessory.video))].filter(i => i).map(i => ({video: i as string})),
				...[this.getQualifiedUrl(this.getTranslatedUrl(accessory.image))].filter(i => i).map(i => ({image: i as string}))
			],
			price,
			priceValue,
			subtotalPrice,
			subtotalPriceValue,
			deselectable: true,
			selected: selected ? selected.quantity : 0,
			locked: locked,
			breakdown,
			...getQuantityRange(accessory),
			validAttachPoints: this.getValidAttachPoints(accessory),
			attachedPoints: selected && selected.attached,
			onAdd: this.locked() ? noop : (quantity?:number) => { if(accessory.id) {this.addAccessory(accessory.id, quantity) }},
			onRemove: this.locked() ? noop : (quantity?:number) => { if(accessory.id) {this.removeAccessory(accessory.id, quantity) }},
			onClick: this.locked() ? noop : () => { if(accessory.id) {this.toggleAccessory(accessory.id) }},
			onAttach: (this.locked() || !selected) ? noop : (attachPoint?: string) => { if(accessory.id) {this.attachAccessory(accessory.id, attachPoint)}},
			onDetach: (this.locked() || !selected) ? noop : (attachPoint?: string) => { if(accessory.id) {this.detachAccessory(accessory.id, attachPoint)}},
			analyticsName: this.getTranslation(accessory.name, "en-US"),
			validFrom: accessory.validFrom,
			validTo: accessory.validTo,
			isNew: isNew(accessory),
			contains: accessory.accessoryPackage?.length ? accessory.accessoryPackage.reduce((p, c) => ([...p, ...new Array(c.quantity).fill(c.id)]), [] as string[]) : undefined,
			outOfStock: !IGNORE_OUT_OF_STOCK && (accessory.soldOutRegions?.includes(this.session.locale) || accessory.outOfStockCountries?.includes(this.session.country)),
			itemType: isKit(accessory) ? ItemType.Kit : (isBundle(accessory) ? ItemType.Bundle : ItemType.Accessory)
		};
	}

	private updateSelected(prevSelected:Selected[], solution: Solution, mode: "normal" | "attachonly" | "skipattach" = "normal", manuallyAdded?: string) {
		const prevUnit = this.getUnit();
		const nextUnit = solution.targetUnitId ? this.getUnit(solution.targetUnitId) : prevUnit;
		const prevAccessories = this.getAccessories(prevUnit);
		const accessories = this.getAccessories(nextUnit);
		const added:Item[] = solution.add.filter(i => accessories.find(a => a.id === i)).map((i:string) => this.getAccessoryItemBySku(nextUnit, i));
		const repurposedAttachPoints = added.map(a => a.validAttachPoints && a.validAttachPoints.length > 0 ? a.validAttachPoints[0] : undefined);
		const removed:Item[] = solution.remove.filter(i => prevAccessories.find(a => a.id === i)).map((i:string) => this.getAccessoryItemBySku(prevUnit, i));
		const increment = mode === "attachonly" ? 0 : 1;
		const groupedAdd: any = solution.add.reduce((s:any, i:string) => ({...s, [i]: (s[i] || 0) + increment}), {});
		const groupedRemove: any = solution.remove.reduce((s:any, i:string) => ({...s, [i]: (s[i] || 0) + increment}), {});
		const addedAccessoryPackage = added.find(a => a.contains?.length)
		
		let freedUpAttachPoints : string[] = [];
		const uniqueRemovedAccessories = removed.filter((a, i) => removed.findIndex(ra => ra.id == a.id) === i);
		for(const a of uniqueRemovedAccessories) {
			if(a.attachedPoints && a.attachedPoints.length > 0 && a.selected) {
				if(mode === "attachonly") {
					for(const ap of a.attachedPoints || []) {
						repurposedAttachPoints.push(ap);
					}
				}
				for(const ap of a.attachedPoints.slice(a.selected - groupedRemove[a.id])) {
					if(!repurposedAttachPoints.includes(ap)) {
						freedUpAttachPoints.push(ap);
					}
				}
			}
		}

		let speculativelyReattached : string[] = []

		const isMultiAttachPoint = (attachpoint: string) => {
			//Nasty looking hack for ZLBRP-6479 to prevent previously attached items of the same characteristic from being deprioritised
			//Linq items are sometimes stackable and are shown as such in the BYO
			if(parseInt(this.state.year) > 2025 || this.state.model === "spy_canyon") {
				return attachpoint.includes("_linq_")
			}
			return false;
		}

		let selected : Selected[] = [
			...prevSelected.filter(i => {
					//Remove any entries that were removing all instance of
					const id = i.accessorysku || i.featureid;
					if(!id) {
						return true;
					}
					return !solution.remove.includes(id) || i.quantity > groupedRemove[id]
				})
				.map(i => {
					//Update any entries where were adding or removing some 
					if(i.accessorysku) {
						let updates : any = {}
						updates.quantity = i.quantity + (groupedAdd[i.accessorysku] || 0) - (groupedRemove[i.accessorysku] || 0);
						
						if(mode === "normal" && i.accessorysku === manuallyAdded) {
							updates.addedautomatically = true;
						}

						if(groupedAdd[i.accessorysku] !== undefined) {
							//Ensure anything were adding has at least one instance attached
							if(i.attached && i.attached.length === 0) {
								const acc = added.find(a => a.id === i.accessorysku);
								if(acc) {
									updates.attached = acc.validAttachPoints && acc.validAttachPoints.length > 0 ? [acc.validAttachPoints[0]] : [];
								}
							}
						}
						else if(i.attached && i.attached.length > 0 && repurposedAttachPoints.includes(i.attached[0]) && !isMultiAttachPoint(i.attached[0])) {
							//Detach anything that we need to give away the attach point
							updates.attached = [];
						}
						else if(i.attached && i.attached.length === 0) {
							//Reattach anything that doesn't already have something else vying for its attach point
							const acc = accessories.find(a => a.id === i.accessorysku);
							if(acc) {
								const validAttachPoints = this.getValidAttachPoints(acc);
								if (acc && acc.id && validAttachPoints[0]){
									let slot = freedUpAttachPoints.indexOf(validAttachPoints[0]);
									if (slot >= 0) {
										freedUpAttachPoints.splice(slot, 1); // after fitting one item, make sure we don't find any more
										updates.attached = [validAttachPoints[0]];
										speculativelyReattached.push(acc.id)
									}
								}
							}
						}
						return {...i, ...updates};
					}
					return i;
				}),
			//Add new entries for anything we didn't already have an instance of
			...Object.keys(groupedAdd).filter(i => !prevSelected.find(s => s.accessorysku===i || s.featureid===i)).map(i => {
				const acc = accessories.find(a => a.id === i);
				if(acc) {
					const validAttachPoints = this.getValidAttachPoints(acc);
					return {
						accessorysku: i,
						category: isBundle(acc) ? ZL_BUNDLES : acc && acc.type && acc.type.id,
						locked: false,
						quantity: groupedAdd[i],
						attached: validAttachPoints.length > 0 ? [validAttachPoints[0]] : undefined,
						addedautomatically: mode === "normal" ? i !== manuallyAdded : undefined
					}
				}
				const feature = (nextUnit.bundledFeatures || []).find(f => f.id === i);
				if(feature) {
					return {
						featureid: i,
						category: feature.type && feature.type.id,
						locked: false,
						quantity: 1,
						attached: undefined
					}
				}
				throw Error("Adding unknown item: " + i);
			}).sort((l, r) => {
				if(!addedAccessoryPackage) {
					return 0;
				}
				if(!l.accessorysku || !r.accessorysku) {
					return (l.accessorysku ? 1 : 0) - (r.accessorysku ? 1 : 0);
				}
				if(l.accessorysku === addedAccessoryPackage.id) {
					return 1
				}
				if(r.accessorysku === addedAccessoryPackage.id) {
					return -1
				}
				return addedAccessoryPackage.contains!.indexOf(l.accessorysku) - addedAccessoryPackage.contains!.indexOf(r.accessorysku)
			})
		]


		selected = selected.map(s => {
			if(s.accessorysku) {
				const acc = this.getIncludedAccessories(nextUnit).find(a => s.accessorysku === a.id);
				if(acc) {
					s.locked = getIncludedCount(acc)
					if(!s.attached?.length) {
						//Reattach anything that doesn't already have something else vying for its attach point
						const validAttachPoints = this.getValidAttachPoints(acc);
						if (acc.id && validAttachPoints[0]) {
							let slot = freedUpAttachPoints.indexOf(validAttachPoints[0]);
							if (slot >= 0) {
								freedUpAttachPoints.splice(slot, 1); // after fitting one item, make sure we don't find any more
								s.attached = [validAttachPoints[0]];
								speculativelyReattached.push(acc.id);
							}
						}
					}
				}
				else {
					s.locked = 0;
				}
			}
			return s;
		})

		if(mode === "normal") {
			const prohibited = repurposedAttachPoints.map((ap) => {
				const item = prevSelected.find(a => (a.attached || []).includes(ap || ""));
				return item ? item.accessorysku : undefined;
			}).filter(sku => sku !== undefined) as string[];

			selected = this.resolveAttachPoints(nextUnit, selected, solution.add, prohibited, speculativelyReattached);
		}

		return selected;
	}

	private resolveAttachPoints(unit: V2.Unit, selected: Selected[], required: string[], prohibited: string[], freeChanges: string[]=[]) {
		const accessories = this.getAccessories(unit);
		const accessorySkus = accessories.map(a => a.id);

		//Ignore feature codes
		required = required.filter(sku => accessorySkus.includes(sku));

		//Extend prohibited list to anything that will steal the attach point of a required item
		const requiredAttachPoints = required.map(sku => {
			const acc = accessories.find(a => a.id === sku);
			if(acc) {
				const validAttachPoints = this.getValidAttachPoints(acc);
				return validAttachPoints[0];
			}
			return undefined;
		})
		prohibited = [...prohibited, ...selected.filter((i) => {
			if(!i.accessorysku || required.includes(i.accessorysku)) {
				return false;
			}
			const acc = accessories.find(a => a.id === i.accessorysku);
			if(acc) {
				const validAttachPoints = this.getValidAttachPoints(acc);
				return validAttachPoints[0] && requiredAttachPoints.includes(validAttachPoints[0]);
			}
			return false;
		}).map(i => i.accessorysku as string)]
		
		const available = selected.map(i => i.accessorysku || i.featureid);
		const attached = selected.filter(i => i.accessorysku && i.attached && i.attached.length > 0).map(a => a.accessorysku as string);
		const solution = {add:[], remove:[], targetUnitId: unit.id, splitBundles: []}
		let solutions = this.resolveDepthFirst(unit, attached, [], required, {[this.getSolutionKey(solution)]: solution}, true, undefined, {});
		if(solutions) {
			solutions = this.resolveExisting(unit, attached, solutions, true);

			//Ignore solutions that require items not already in the basket, or that attempt to remove items we just added
			const filteredSolutions = Object.values(solutions).filter(s => {
				if(!s.add.every(a => available.includes(a))) {
					return false;
				}
				if(s.remove.some(a => required.includes(a))) {
					return false;
				}
				if(s.add.some(a => prohibited.includes(a))) {
					return false;
				}
				return true;
			});

			if(filteredSolutions.length > 0) {
				filteredSolutions.sort((lhs, rhs) => {
					const lhsChanges = lhs.add.filter(sku => !freeChanges.includes(sku)).length + lhs.remove.filter(sku => !freeChanges.includes(sku)).length;
					const rhsChanges = rhs.add.filter(sku => !freeChanges.includes(sku)).length + rhs.remove.filter(sku => !freeChanges.includes(sku)).length;
					if(lhsChanges!= rhsChanges){
						return lhsChanges - rhsChanges;
					}
					return rhs.add.filter(sku => !freeChanges.includes(sku)).length - lhs.add.filter(sku => !freeChanges.includes(sku)).length;
				})

				console.log("Attach point solutions:", filteredSolutions, attached)

				if(filteredSolutions[0].add.length > 0 || filteredSolutions[0].remove.length > 0) {
					filteredSolutions[0].add.push(...required)
					filteredSolutions[0].remove.push(...prohibited)
					selected = this.updateSelected(selected, filteredSolutions[0], "attachonly");
				}
			}
			else {
				console.log("No attach point changes needed?")
			}
		}
		return selected
	}

	private applySolution(action: Action, item: Item, solution: Solution, resolution?: ConflictResolution) {
		const unitModel = this.getUnitModel();
		const unit = this.getUnit(solution.targetUnitId);
		const availableAccessories = (solution.targetUnitId && solution.targetUnitId !== this.state.unitsku) ? this.buildAccessoriesList(unit) : undefined;
		this.dispatchUpdate(
			{
				...this.state,
				name: this.getMarketingName(unitModel, unit) || this.state.name,
				unitsku: solution.targetUnitId || this.state.unitsku,
				homologation: this.getHomologationIdFromUnit(unit),
				modified: new Date().toISOString(),
				selected: this.updateSelected(this.state.selected, solution, "normal", action===Action.AddedAccessory ? item.id : undefined)
			},
			{
				action,
				item,
				resolution
			},
			availableAccessories
		)
	}

	private generateConflictDetails(action: Action, sku: string, resolutions: Solution[], includedAccessories?: {[key:string]: V2.Accessory[]}) : ConflictDetails {
		this.sortSolutions(resolutions);

		const getItem = (unit:V2.Unit, sku:string, remove?: boolean) => {
			try {
				return this.getAccessoryItemBySku(unit, sku, remove)
			}
			catch {
				return this.getFeatureItemById(sku);
			}
		}

		const currUnit = this.getUnit()
		
		const item = getItem(currUnit, sku, action===Action.RemovedAccessory);

		const ignoreRemove = includedAccessories?.[currUnit.id as string]?.map((i) => i.id) ?? []
		
		return {
			action,
			item,
			resolutions: resolutions.map((r) => {
				const nextUnit = r.targetUnitId ? this.getUnit(r.targetUnitId) : currUnit;
				const add:Item[] = r.add.map((i) => getItem(nextUnit, i));
				const remove:Item[] = r.remove.map((i) => getItem(currUnit, i, true));
				const splitBundles:Item[] = r.splitBundles.map((i) => getItem(currUnit, i, true));
				const ignoreAdd = includedAccessories?.[nextUnit.id as string]?.map((i) => i.id) ?? []
				let resolution = {
					add: add.filter((i) => i.id!==sku && !ignoreAdd.includes(i.id)),
					remove: remove.filter((i) => i.id!==sku && !ignoreRemove.includes(i.id)),
					splitBundles,
					unitsku: r.targetUnitId,
					getPrice:  (financeParameters?:FinanceParameters) : FinanceDetails | undefined => {
						return this.getPricingInfo(financeParameters, add, remove, r.targetUnitId);
					},
					getPriceDifference:  (financeParameters?:FinanceParameters) : FinanceDetails | undefined => {
						const oldPrice = this.getPricingDetails(financeParameters);
						const newPrice = this.getPricingDetails(financeParameters, add, remove, r.targetUnitId);
						if(oldPrice && newPrice) {``
							const subtotalValue = newPrice.subtotalValue.amount - oldPrice.subtotalValue.amount;
							const totalValue = newPrice.totalValue.amount - oldPrice.totalValue.amount;
							const monthlyValue = newPrice.monthlyValue && oldPrice.monthlyValue ? newPrice.monthlyValue?.amount - oldPrice.monthlyValue?.amount : undefined;
							return {
								totalValue,
								subtotalValue,
								monthlyValue,
								subtotal: this.formatPrice({amount: subtotalValue, currency: oldPrice.subtotalValue.currency}),
								total: this.formatPrice({amount: totalValue, currency: oldPrice.totalValue.currency}),
								monthly: monthlyValue && oldPrice.monthlyValue ? this.formatPrice({amount: monthlyValue, currency: oldPrice.monthlyValue.currency}) : "",
								currency: newPrice.totalValue.currency
							};
						}
						return undefined;
					},
					apply: noop
				}
				resolution.apply = () => {
					this.applySolution(action, item, r, resolution);
				}
				return resolution;
			})
		}
	}

	private getCurrentState() : string[] {
		return ([] as string[]).concat(...this.state.selected.map((i) => {
			return new Array(i.quantity).fill((i.accessorysku || i.featureid) as string);
		}));
	}

	private filterSolutions(solutions: KeyedSolutions) : KeyedSolutions {
		
		//Filter any duplicate solutions or unnecessarily complex solutions
		const isSubset = (set:string[], superset:string[]) => {
			for (const elem of set) {
				if (!superset.includes(elem)) {
					return false;
				}
			}
			return true;
		}
		
		const solutionKeys = Object.keys(solutions)
		const filteredSolutions = solutionKeys.reduce((filtered, solutionKey, currIndex) => {
			const curr = solutions[solutionKey];
			const superceeded = solutionKeys.some((otherKey, otherIndex) => {
				const other = solutions[otherKey];
				if(currIndex != otherIndex) {
					if(other.add.length <= curr.add.length && other.remove.length <= curr.remove.length && isSubset(other.add, curr.add) && isSubset(other.remove, curr.remove)) {
						if(curr.add.length === other.add.length && curr.remove.length === other.remove.length) {
							if(currIndex > otherIndex) {
								return true;
							}
						}
						else {
							return true;
						}
					}
				}
				return false;
			});
			if(!superceeded) {
				filtered[solutionKey] = curr;
			}
			return filtered;
		}, {} as KeyedSolutions)

		return filteredSolutions;
	}

	private filterIncludedSolutions(solutions: KeyedSolutions) : KeyedSolutions {
		//Filter any solutions that remove included accessories but are otherwise a subset of another solution

		let includedAccessories : string[] | undefined = undefined
		const unitIds = new Set(Object.values(solutions).map(s => s.targetUnitId))
		if(unitIds.size === 1) {
			includedAccessories = this.getUnit(Array.from(unitIds)[0]).includedAccessories?.map(a => a.id as string)
		}

		if(!includedAccessories?.length) {
			return solutions;
		}

		const isSubset = (set:string[], superset:string[]) => {
			for (const elem of set) {
				if (!superset.includes(elem)) {
					return false;
				}
			}
			return true;
		}
		
		const solutionKeys = Object.keys(solutions)
		const filteredSolutions = solutionKeys.reduce((filtered, solutionKey, currIndex) => {
			const curr = solutions[solutionKey];
			if(!curr.remove.some(a => includedAccessories?.includes(a))) {
				filtered[solutionKey] = curr;
				return filtered;
			}
			const superceeded = solutionKeys.some((otherKey, otherIndex) => {
				const other = solutions[otherKey];
				if(currIndex != otherIndex) {
					if(other.add.length >= curr.add.length && other.remove.length <= curr.remove.length && isSubset(curr.add, other.add) && isSubset(other.remove, curr.remove.filter(a => !includedAccessories?.includes(a)))) {
						return true;
					}
				}
				return false;
			});
			if(!superceeded) {
				filtered[solutionKey] = curr;
			}
			return filtered;
		}, {} as KeyedSolutions)

		return filteredSolutions;
	}

	private getResolverState(skus: string[]) : ResolverState {
		return skus.reduce((t: ResolverState, a: string) => Object.assign(t, {[a]: (t[a] ?? 0) + 1}), {} as ResolverState)
	}

	private compareResolverState(l: ResolverState, r: ResolverState) {
		if(Object.keys(l).length === Object.keys(r).length) {
			if(Object.keys(l).every(sku => l[sku] === r[sku])) {
				return true;
			}
		}
		return false;
	}

	private findSolutionToAutoApply(unit: V2.Unit, add: ResolverState, remove: ResolverState, solutions: Solution[]) : Solution | undefined {

		//If all the changes are to add items that don't have groups or were what the user actually modified, auto apply
		const autoApplyUntypedItems = false
		const autoApplyHiddenItems = true;

		let autoApply = undefined;
		const autoAppliedAccessories : string[] = this.getAccessories(unit)
			.filter(a => 
				(autoApplyUntypedItems && !a.type?.id) ||
				(autoApplyHiddenItems && a.type?.id && HIDDEN_ACCESSORY_GROUPS.includes(a.type.id))
			)
			.map(a => a.id || "")

		const filteredAdd = Object.keys(add).reduce((p, a) => autoAppliedAccessories.includes(a) ? p : Object.assign({[a]: add[a]}), {} as ResolverState);
		const filteredRemove = Object.keys(remove).reduce((p, a) => autoAppliedAccessories.includes(a) ? p : Object.assign({[a]: remove[a]}), {} as ResolverState);

		for(const solution of solutions) {
			if(solution.splitBundles?.length) {
				continue;
			}

			if(this.compareResolverState(this.getResolverState(solution.add), add) && this.compareResolverState(this.getResolverState(solution.remove), remove)) {
				return solution; //Exactly matches what was requested, can't get any better than that
			}

			if(autoAppliedAccessories.length) {
				const canAutoApply = this.compareResolverState(this.getResolverState(solution.add.filter(sku => !autoAppliedAccessories.includes(sku))), filteredAdd) &&
									 this.compareResolverState(this.getResolverState(solution.remove.filter(sku => !autoAppliedAccessories.includes(sku))), filteredRemove)
				if(canAutoApply) {
					if(autoApply === undefined) {
						autoApply = solution;
					}
					else {
						autoApply = undefined;
						break;
					}
				}
			}
		}
		return autoApply;
	}

	private sortSolutions(solutions: Solution[]) {
		//Add only - sorted by price
		//Everything else - sorted by total num changes (Should we account for items that aren't displayed)

		const currUnit = this.getUnit();
		
		const getItem = (unit:V2.Unit, sku:string, remove?: boolean) => {
			try {
				return this.getAccessoryItemBySku(unit, sku, remove)
			}
			catch {
				return this.getFeatureItemById(sku);
			}
		}
		
		const _getTotalPrice = this.getTotalPrice.bind(this);
		solutions.sort((lhs, rhs) => {
			const lhsUnit = this.getUnit(lhs.targetUnitId);
			const rhsUnit = this.getUnit(rhs.targetUnitId);
			const lhsAdd = lhs.add.map(sku => getItem(lhsUnit, sku));
			const lhsRemove = lhs.remove.map(sku => getItem(currUnit, sku));
			const rhsAdd = rhs.add.map(sku => getItem(rhsUnit, sku));
			const rhsRemove = rhs.remove.map(sku => getItem(currUnit, sku));

			//Prefer minimal feature changes
			const lhsFeatureChanges = lhsAdd.filter(i => i.deselectable===false).length;
			const rhsFeatureChanges = rhsAdd.filter(i => i.deselectable===false).length;
			if(lhsFeatureChanges !== rhsFeatureChanges) {
				return lhsFeatureChanges - rhsFeatureChanges;
			}

			if(lhsFeatureChanges > 0 || rhsFeatureChanges > 0) {
				//Prefer minimal attach point changes
				const gatherAttachPoints = (attachPoints:string[], item:Item) => {
					for(const attachPoint of item.validAttachPoints || []) {
						if(!attachPoints.includes(attachPoint)) {
							attachPoints.push(attachPoint);
						}
					}
					return attachPoints;
				}
				const lhsAffectedAttachPoints = [...lhsAdd, ...lhsRemove].reduce(gatherAttachPoints, [] as string[]);
				const rhsAffectedAttachPoints = [...rhsAdd, ...rhsRemove].reduce(gatherAttachPoints, [] as string[]);
				if(lhsAffectedAttachPoints.length !== rhsAffectedAttachPoints.length) {
					return lhsAffectedAttachPoints.length - rhsAffectedAttachPoints.length;
				}
			}

			//Prefer all adds or all removes (least inteferance from resolver)
			const lhsOpTypes = (lhs.add.length ? 1 : 0) + (lhs.remove.length ? 1 : 0);
			const rhsOpTypes = (rhs.add.length ? 1 : 0) + (rhs.remove.length ? 1 : 0);
			if((lhsOpTypes === 1) !== (rhsOpTypes === 1)) {
				return lhsOpTypes - rhsOpTypes;

			}

			const lhsPrice = _getTotalPrice(lhsAdd, lhsRemove, lhsUnit.id);
			const rhsPrice = _getTotalPrice(rhsAdd, rhsRemove, rhsUnit.id);
			
			//Prefer lowest price assuming a significant separation (more than 1% of total basket price)
			if(lhsPrice && rhsPrice && Math.abs(lhsPrice.total.amount - rhsPrice.total.amount) > (0.005 * (lhsPrice.total.amount + rhsPrice.total.amount))) {
				return lhsPrice.total.amount - rhsPrice.total.amount;
			}
			
			//Prefer items we can show 3d for (as a percentage so more changes isn't automatically better)
			if(this.config?.priorityList) {
				const lhs3D = (lhs.add.filter(a => this.config?.priorityList.includes(a)).length - lhs.remove.filter(a => this.config?.priorityList.includes(a)).length) / (lhs.add.length - lhs.remove.length);
				const rhs3D = (rhs.add.filter(a => this.config?.priorityList.includes(a)).length - rhs.remove.filter(a => this.config?.priorityList.includes(a)).length) / (rhs.add.length - rhs.remove.length);
				if(lhs3D != rhs3D) {
					return rhs3D - lhs3D;
				}
			}
			
			//Prefer lowest price
			if(lhsPrice && rhsPrice && lhsPrice.total.amount) {
				return lhsPrice.total.amount - rhsPrice.total.amount;
			}

			//Prefer least changes
			return (lhs.add.length + lhs.remove.length) - (rhs.add.length + rhs.remove.length);
		});
	}
	
	private addAccessory(sku: string, quantity?: number) {
		if(OPTIMISE_BREADTH_FIRST) {
			this.addAccessoryBreadthFirst(sku, quantity);
		}
		else {
			this.addAccessoryDepthFirst(sku, quantity);
		}
	}
		
	private removeAccessory(sku: string, quantity?: number) {
		if(OPTIMISE_BREADTH_FIRST) {
			this.removeAccessoryBreadthFirst(sku, quantity);
		}
		else {
			this.removeAccessoryDepthFirst(sku, quantity);
		}
	}

	private addAccessoryDepthFirst(sku: string, quantity?: number) {
		if(DEBUG) {
			touchedAccessories = new Set<string>();
		}

		const unit = this.getUnit();
		if(quantity === undefined) {
			const item = this.getAccessoryItemBySku(unit, sku);
			quantity = item.minimum || 1;
		}
		if(quantity <= 0) {
			console.error("Requested to add <= 0 instances");
			return;
		}

		console.log(window.performance.now() + " Start")
		const state = this.getCurrentState()
		let solutions = this.resolveAdd(unit, state, sku, quantity, [], [], false, undefined, {});

		if(solutions === undefined) {
			console.error("Failed to add " + sku + ". No solution to conflict.", state);
			this.onConflict(this.generateConflictDetails(Action.AddedAccessory, sku, []))
			return;
		}

		console.log(window.performance.now() + " Generated " + Object.keys(solutions).length + " Solutions")
		
		solutions = this.resolveExisting(unit, state, solutions);
		if(Object.keys(solutions).length === 0) {
			console.error("Failed to add " + sku + ". No solution to conflict.", state);
			this.onConflict(this.generateConflictDetails(Action.AddedAccessory, sku, []))
			return;
		}

		console.log(window.performance.now() + " Generated " + Object.keys(solutions).length + " Solutions after fixing existing")

		const filteredSolutions = Object.values(this.filterSolutions(solutions));

		console.log(window.performance.now() + " Filtered to " + filteredSolutions.length + " Solutions")

		if(DEBUG) {
			console.log("Touched Accessories:", touchedAccessories)
			const touchedAccessoryItems = Array.from(touchedAccessories).sort().map(sku => this.accessoryCache?.all.items[sku]);
			console.log("Touched Accessory Details:", touchedAccessoryItems.map(a => ({id: a?.id, name: this.getTranslation(a?.name, "en-US"), characteristics: a?.category?.map(c => c.id), dependencies: a?.dependencies})))
		}

		const autoApply = this.findSolutionToAutoApply(unit, {[sku]: quantity}, {}, filteredSolutions);
		if(autoApply) {
			const item = this.getAccessoryItemBySku(unit, sku);
			this.applySolution(Action.AddedAccessory, item, autoApply);
		}
		else {
			this.onConflict(this.generateConflictDetails(Action.AddedAccessory, sku, filteredSolutions))
		}
	}

	
	private processStep(step: number, state: ResolverState, add: ResolverState, remove: ResolverState, store: ResolverStore, validate: boolean) : void {
		const updatedState = {...state};
		Object.keys(add).reduce((s, k) => {
			s[k] = (s[k] ?? 0) + add[k];
			return s;
		}, updatedState);
		Object.keys(remove).reduce((s, k) => {
			s[k] = Math.max((s[k] ?? 0) - remove[k], 0); //If we shortcircuit the conflict resolution when removing 1 of 2+ we could just remove all instances here - this is what the rules will expect, they have no concept of quantity
			return s;
		}, updatedState);

		const addedCharacteristics : (string | undefined)[] = Object.keys(updatedState).filter(sku => !store.initialState[sku]).map(sku => {
			const accessory = store.accessoryCache?.all.items[sku];
			if(accessory?.category?.length !== 1) {
				return undefined;
			}
			return accessory.category[0].id;
		})

		let solutions : Solution[] = [{add: [], remove: [], targetUnitId: store.unit.id, splitBundles: []}]

		let validationFails : string[] = [];

		//Check each of the added accessories to see if we need anything else
		for(const sku of Object.keys(add)) {
			if(sku in state && !validate) {
				continue; //Already validated
			}

			const accessory = store.accessoryCache?.all.items[sku];
			if(accessory?.dependencies) {
				const [commonDependancies, exclusiveDependencies] = [accessory.dependencies.twoWay || {}, accessory.dependencies.threeWay || []];
				
				let currentlyValid = true;

				if(commonDependancies && !DEBUG_IGNORE_2WAY) {
					if(DEBUG_SKUS.includes(sku)) {
						console.log("Processing 2 way deps on " + sku, commonDependancies)
					}
					for(const characteristic of Object.keys(commonDependancies)) {
						const deps = commonDependancies[characteristic];

						// if a NONE field appears, then anything else from this category not listed in this deps list would need to be removed
						if(deps.includes(NONE)) {
							// only include accessories we know about, filters out ids we don't know about
							const thiscat = deps.filter((id:string) => id !== NONE);

							if(store.accessoryCache.categorised[characteristic] === undefined) {
								console.error(sku, "Unknown characteristic:", "'" + characteristic + "'")
								console.log("Known Characteristics:", Object.keys(store.accessoryCache.categorised).sort())
							}

							let incompatible = Object.keys(store.accessoryCache.categorised[characteristic]?.items || []).filter(id => !thiscat.includes(id));
							
							//Ignore incompatibilities with included accessories
							incompatible = incompatible.filter(incompatiblesku => !store.unit.includedAccessories?.some(includedAccessory => includedAccessory.id === incompatiblesku))

							if(incompatible.some(i => updatedState[i])) {
								currentlyValid = false;
							}
							
							//If any of the incompatible items have been added in a previous step of this solution path its a dead end
							if(incompatible.some(i => updatedState[i] && store.initialState[i] === undefined)) {
								if(validate) {
									solutions = []
									continue;
								}
								return;
							}
							

							const updatedSolutions : Solution[] = []
							for(const solution of solutions) {
								//If any of the incompatible items have been added in this solution step its a dead end
								if(incompatible.some(i => solution.add.includes(i))) {
									continue;
								}
/*
								if(validate && store.initialState[sku] && !solution.add.includes(sku) && !solution.remove.includes(sku)) {
									//In the validate phase, consider removing accessories that don't have valid dependencies
									updatedSolutions.push({add: solution.add, remove: [...solution.remove, sku], targetUnitId: solution.targetUnitId})
								}
*/
								//Remove items even if they weren't in the config to ensure nothing else re-adds them in this solution path
								for(const item of incompatible) {
									if(!solution.remove.includes(item)) {
										solution.remove.push(item)
									}
								}
								updatedSolutions.push(solution);
							}
							if(updatedSolutions.length === 0 && !validate) {
								//All potential solutions are dead ends
								return
							}
							solutions = updatedSolutions;
						}
						else if (deps.length > 0) {
							// only include accessories we know about, filters out ids we don't know about
							const thiscat = deps.filter((id:string) => store.accessoryCache.all.items[id]);

							// if we already have something valid from this category, we don't need to add anything
							if (thiscat.some((id:string) => updatedState[id])){
								continue;
							}

							if(addedCharacteristics.includes(characteristic) && !validate) {
								//console.log("Ignoring 2 way potential solution thats attempting to add something with the same characteristic as another items added by the same solution", characteristic)
								return;
							}

							currentlyValid = false;
							
							const updatedSolutions : Solution[] = [];
							for(const option of thiscat) {
								if(store.initialState[option]) {
									//If this was in the original state something earlier in the resolution must have removed it so this is a dead end
									continue;
								}
								for(const solution of solutions) {
									if(solution.remove.includes(option)) {
										//We already removed this accessory on this branch so its a dead end
										continue;
									}
									if(solution.add.includes(option)) {
										//Another change on this branch already requested this accessory
										updatedSolutions.push(solution)
									}
									else {
										updatedSolutions.push({
											add: [...solution.add, option],
											remove: solution.remove,
											targetUnitId: solution.targetUnitId,
											splitBundles: solution.splitBundles
										})
									}
								}
							}
							if(updatedSolutions.length === 0 && !validate) {
								//All routes are dead ends
								return;
							}
							solutions = updatedSolutions;
						}
					}
				}

				if(exclusiveDependencies && exclusiveDependencies.length > 0 && !DEBUG_IGNORE_3WAY) {
					if(DEBUG_SKUS.includes(sku)) {
						console.log("Processing 3 way deps on " + sku, exclusiveDependencies)
					}

					const updatedSolutions : Solution[] = []
					let anyThreeWayValid = exclusiveDependencies.some((exclusive) => {
						let required : string[] = []
						let incompatible : string[] = []
						for(const item of exclusive) {
							if(!item.characteristic) {
								return false;
							}
							if(item.accessory === NONE) {
								if(store.accessoryCache.categorised[item.characteristic]) {
									const options = Object.keys(store.accessoryCache.categorised[item.characteristic].items);
									incompatible = incompatible.concat(options);
								}
							}
							else if(item.accessory) {
								if(!updatedState[item.accessory] && addedCharacteristics.includes(item.characteristic)) {
									//console.log("Ignoring potential 3 way solution thats attempting to add something with the same characteristic as another items added by the same solution", item.characteristic)
									return false;
								}
								const options = Object.keys(store.accessoryCache.categorised[item.characteristic].items);
								if(options.includes(item.accessory)) {
									required.push(item.accessory);
								}
								else {
									try {
										console.error("Ignoring potential solution with miscategorised accessory", item, options, this.getAccessoryItemBySku(store.unit, item.accessory))
									}
									catch {
										console.warn("Ignoring potential solution with unknown accessory", item, options)
									}
									return false;
								}
							}
							else {
								console.log("Ignoring potential solution with unspecified accessory")
								return false;
							}
						}
						
						//Ignore incompatibilities with included accessories
						incompatible = incompatible.filter(incompatiblesku => !store.unit.includedAccessories?.some(includedAccessory => includedAccessory.id === incompatiblesku))

						let threeWayAlreadyValid = true;
						for(const id of required) {
							if(store.initialState[id] && !updatedState[id]) {
								return false; //Was part of initial solution but has since been removed - this path is invalid
							}
							if(!updatedState[id]) {
								threeWayAlreadyValid = false;
							}
						}
						for(const id of incompatible) {
							if(!store.initialState[id] && updatedState[id]) {
								return false; //Has been added in a previous step - this path is invalid
							}
							if(updatedState[id]) {
								threeWayAlreadyValid = false;
							}
						}
						return threeWayAlreadyValid;
					})

					if(!anyThreeWayValid) {
						currentlyValid = false;

						exclusiveDependencies.forEach((exclusive) => {
							let required : string[] = []
							let incompatible : string[] = []
							for(const item of exclusive) {
								if(!item.characteristic) {
									return;
								}
								if(item.accessory === NONE) {
									if(store.accessoryCache.categorised[item.characteristic]) {
										const options = Object.keys(store.accessoryCache.categorised[item.characteristic].items);
										incompatible = incompatible.concat(options);
									}
								}
								else if(item.accessory) {
									const options = Object.keys(store.accessoryCache.categorised[item.characteristic].items);
									if(options.includes(item.accessory)) {
										required.push(item.accessory);
									}
									else {
										try {
											console.error("Ignoring potential solution with miscategorised accessory", item, options, this.getAccessoryItemBySku(store.unit, item.accessory))
										}
										catch {
											console.warn("Ignoring potential solution with unknown accessory", item, options)
										}
										return;
									}
								}
								else {
									console.log("Ignoring potential solution with unspecified accessory")
									return;
								}
							}
							
							//Ignore incompatibilities with included accessories
							incompatible = incompatible.filter(incompatiblesku => !store.unit.includedAccessories?.some(includedAccessory => includedAccessory.id === incompatiblesku))

							let threeWayAlreadyValid = true;
							for(const id of required) {
								if(store.initialState[id] && !updatedState[id]) {
									return; //Was part of initial solution but has since been removed - this path is invalid
								}
								if(!updatedState[id]) {
									threeWayAlreadyValid = false;
								}
							}
							for(const id of incompatible) {
								if(!store.initialState[id] && updatedState[id]) {
									return; //Has been added in a previous step - this path is invalid
								}
								if(updatedState[id]) {
									threeWayAlreadyValid = false;
								}
							}
							if(threeWayAlreadyValid) {
								anyThreeWayValid = true;
							}

							solutions.forEach(solution => {
								const updatedSolution : Solution = {add: [...solution.add], remove: [...solution.remove], splitBundles: solution.splitBundles, targetUnitId: solution.targetUnitId}
								for(const id of required) {
									if(updatedSolution.remove.includes(id)) {
										return; //Has been removed in this step - this path is invalid
									}
									if(!updatedState[id] && !updatedSolution.add.includes(id)) {
										updatedSolution.add.push(id);
									}
								}
								for(const id of incompatible) {
									if(updatedSolution.add.includes(id)) {
										return; //Has been added in this step - this path is invalid
									}
									if(!updatedSolution.remove.includes(id)) {
										updatedSolution.remove.push(id)
									}
								}
								updatedSolutions.push(updatedSolution);
							})
						})
					
						if(Object.keys(updatedSolutions).length === 0 && !validate) {
							return; //No valid solution paths available
						}
						solutions = updatedSolutions;
					}
				}

				if(validate && store.initialState[sku] && !currentlyValid) {
					//In the validate phase, consider removing accessories that don't have valid dependencies
					validationFails.push(sku);
				}
			}
		}

		for(const sku of validationFails) {
			solutions.push({add: [], remove: new Array(store.initialState[sku]).fill(sku), targetUnitId: store.unit.id, splitBundles: []})
		}

		for(const solution of solutions) {
			const complete = solution.add.length === 0 && solution.remove.filter(i => updatedState[i]).length === 0;
			if(complete) {
				if(!validate) {
					//Attempt to add zero instances of any remaining accessories to check they are still valid
					const readd = Object.keys(updatedState).filter((s) => updatedState[s]).reduce((s, i) => Object.assign(s, {[i]: 0}), {});
					const keepRemoved = Object.keys(remove).reduce((s, i) => Object.assign(s, {[i]: 0}), {});
					//console.log("Validating solution: ", updatedState, readd, keepRemoved);
					this.queueStep(step, updatedState, readd, keepRemoved, store, true)
				}
				else {
					//Check if any kits are still complete - if not its back to the drawing board
					//This code relies on the assumption kits that contain the same accessories would be mutually exclusive - adding both would break the counting
					const includedItems = this.getIncludedAccessories(store.unit).reduce((p,c) => Object.assign(p, {[c.id as string]: getIncludedCount(c)}), {} as {[id: string]: number});

					const kitsToRemove : ResolverState = {}
					const expectedKitAccessories : ResolverState = {}
					const expectedBundleAccessories: ResolverState = {}
					for(const sku of Object.keys(updatedState)) {
						const accessory = store.accessoryCache.all.items[sku]
						if(updatedState[sku] && (isKit(accessory) || isBundle(accessory))) {
							const subItems = accessory.accessoryPackage!.reduce((p,c) => Object.assign(p, {[c.id]: c.quantity}), {} as ResolverState)
							let kitValid = true;
							const remainingKitItems : ResolverState = {}
							for(const subItem of Object.keys(subItems)) {
								const updatedCount = updatedState[subItem] ?? 0;
								if(isKit(accessory)) {
									expectedKitAccessories[subItem] = (expectedKitAccessories[subItem] ?? 0) + (subItems[subItem] * updatedState[sku])
									const expectedCount = (subItems[subItem] * updatedState[sku]) + (includedItems[subItem] ?? 0)
									if(updatedCount < expectedCount) {
										kitValid = false
									}
									const kitItemsToRemove = subItems[subItem] - ((store.initialState[subItem] - updatedState[subItem]) % subItems[subItem]) - Math.max(expectedCount - updatedCount, 0)
									if(kitItemsToRemove > 0) {
										remainingKitItems[subItem] = kitItemsToRemove
									}
								}
								else {
									expectedBundleAccessories[subItem] = (expectedBundleAccessories[subItem] ?? 0) + (subItems[subItem] * updatedState[sku])
								}
							}
							if(!kitValid) {
								Object.keys(remainingKitItems).reduce((p,c) => Object.assign(p, {[c]: (p[c] || 0) + remainingKitItems[c]}), kitsToRemove)
								kitsToRemove[sku] = 1
							}
						}
					}
					if(Object.keys(kitsToRemove).length) {
						//Register new solution steps for future evaluation
						this.queueStep(step, updatedState, {}, kitsToRemove, store, false)
						continue
					}


					//Check if any bundles need splitting 
					//This code relies on the assumption that any bundles with the same content are mutually exclusive and only a single instance of a bundle can be added
					const removeBundlesContaining : string[] = []
					for(const sku of Object.keys(updatedState)) {
						const expected = (includedItems[sku] ?? 0) + (expectedKitAccessories[sku] ?? 0) + (expectedBundleAccessories[sku] ?? 0)
						if(updatedState[sku] < expected) {
							removeBundlesContaining.push(sku)
						}
					}
					const splitBundles = []
					for(const sku of Object.keys(updatedState)) {
						const accessory = store.accessoryCache.all.items[sku]
						if(isBundle(accessory) && accessory.accessoryPackage?.some(s => removeBundlesContaining.includes(s.id))) {
							updatedState[sku] = 0
							splitBundles.push(sku)
						}
					}

					//Work out the difference between initialState and updatedState
					const solution : Solution = {
						add: [],
						remove: [],
						splitBundles,
						targetUnitId: store.unit.id
					}
					for(const id of Object.keys(updatedState)) {
						const count = updatedState[id] - (store.initialState[id] ?? 0);
						if(count > 0) {
							solution.add.push(...new Array<string>(count).fill(id))
						}
					}
					for(const id of Object.keys(store.initialState)) {
						const count = store.initialState[id] - (updatedState[id] ?? 0);
						if(count > 0) {
							solution.remove.push(...new Array(count).fill(id))
						}
					}
					//console.log("Solution:", solution.add, solution.remove, store.initialState, updatedState)
					store.solutions[this.getSolutionKey(solution)] = solution;
				}
			}
			else {
				//Register new solution steps for future evaluation
				this.queueStep(step, updatedState, solution.add.reduce((s, i) => Object.assign(s, {[i]: 1}), {}), solution.remove.reduce((s, i) => Object.assign(s, updatedState[i] ? {[i]: 1} : {}), {}), store, false)
			}
		}
	}
	
	private queueStep(currStep: number, state: ResolverState, add: ResolverState, remove: ResolverState, store: ResolverStore, validate: boolean) {
		const nextStep = currStep + Object.keys(add).filter(id => add[id] > 0).length + Object.keys(remove).filter(id => remove[id] > 0).length;
		if(store.steps[nextStep] === undefined) {
			store.steps[nextStep] = {}
		}

		const allAdds : string[] = Object.keys(add);
		const allRemoves : string[] = Object.keys(remove);
		for(const id of Object.keys(state)) {
			const count = state[id] - (store.initialState[id] ?? 0);
			if(count > 0) {
				allAdds.push(id)
			}
			else if(state[id] === 0) {
				allRemoves.push(id)
			}
		}
		for(const id of Object.keys(store.initialState)) {
			const count = store.initialState[id] - (state[id] ?? 0);
			if(count > 0 && !allRemoves.includes(id)) {
				allRemoves.push(id)
			}
		}

		const solutionkey = allAdds.sort().join(",") + ":" + allRemoves.sort().join(",") + ":" + validate;
		if(store.steps[nextStep][solutionkey] !== null) {
			store.steps[nextStep][solutionkey] = () => this.processStep(nextStep, state, add, remove, store, validate);
		}
	}

	private resolveBreadthFirst(unit: V2.Unit, add: ResolverState, remove: ResolverState, initialState?: ResolverState, accessoryCache?: AccessoryCache) {
		if(!initialState) {
			initialState = this.state.selected.reduce((s, i) => {
				if(i.accessorysku) {
					s[i.accessorysku] = i.quantity
				}
				return s
			}, {} as {[sku: string]: number})
		}

		
		const store : ResolverStore = {
			unit,
			accessoryCache: accessoryCache ?? this.accessoryCache as AccessoryCache,
			initialState,
			solutions: {},
			steps: []
		}

		//Automatically add & remove instances if it doesn't change between 0-1, the dependencies have no concept of quantity
		const state = {...initialState};
		add = {...add}
		for(const sku of Object.keys(add)) {
			if(state[sku]) {
				state[sku] += add[sku];
				delete add[sku];
			}
		}
		remove = {...remove}
		for(const sku of Object.keys(remove)) {
			if(state[sku]) {
				state[sku] = Math.max(state[sku] - remove[sku], 1);
				remove[sku] = remove[sku] - (initialState[sku] - state[sku]);
			}
			else {
				delete remove[sku];
			}
		}

		this.queueStep(0, state, add, remove, store, false);

		const isTest = process.env.NODE_ENV === "test"

		const extraSearchTimeLimitMs : number | undefined = isTest ? undefined : (2 * 1000);
		const maxExtraSteps : number | undefined = isTest ? 2 : 10;
		const startTimeMs = window.performance.now();
		let minimalSolution : { steps: number, processed: number, timeMs: number } | undefined = undefined;

		let currStep = 0;
		let processedSteps = 0;
		while(currStep < store.steps.length) {
			if(store.steps[currStep] === undefined) {
				++currStep;
				continue;
			}

			let terminate = false;
			while(!terminate && Object.values(store.steps[currStep]).some(f => f !== null)) {
				const keys = Object.keys(store.steps[currStep])
				for(const key of keys) {
					if(store.steps[currStep][key] !== null) {
						
						if(minimalSolution !== undefined) {
							//Should these checks be moved within the resolver loop?
							if(maxExtraSteps !== undefined && currStep > minimalSolution.steps + maxExtraSteps) {
								console.info(`Terminating search early as we have already checked for alternate solutions requiring ${maxExtraSteps} extra steps (${processedSteps - minimalSolution.processed} processed)`);
								terminate = true;
								break;
							}
							const currTimeMs = window.performance.now();
							if(extraSearchTimeLimitMs !== undefined && currTimeMs > (startTimeMs + extraSearchTimeLimitMs)) {
								console.info(`Terminating search early as we have already spent more than ${extraSearchTimeLimitMs}ms searching (${currTimeMs - minimalSolution.timeMs} since finding a valid solution)`)
								terminate = true;
								break;
							}
						}

						store.steps[currStep][key]?.();
						store.steps[currStep][key] = null;
					}
				}
			}

			++processedSteps;

			if(!minimalSolution && Object.keys(store.solutions).length) {
				minimalSolution = {steps: currStep, processed: processedSteps, timeMs: window.performance.now()}
			}
			++currStep;
		}

		const potentialSolutions = Object.values(this.filterIncludedSolutions(this.filterSolutions(store.solutions)))

		console.debug(`Found ${potentialSolutions.length} potential solutions in ${window.performance.now() - startTimeMs}ms\n\tTime to first solution: ${minimalSolution ? minimalSolution.timeMs - startTimeMs : "n/a"}\n\tSteps considered: ${processedSteps}/${currStep}/${store.steps.length}`)
		//console.trace("Branches", store.steps.map(s => s ? Object.keys(s).length : 0));

		return potentialSolutions;
	}

	private addAccessoryBreadthFirst(sku: string, quantity?: number) {
		const unit = this.getUnit();
		const item = this.getAccessoryItemBySku(unit, sku);

		if(quantity === undefined) {
			quantity = item.minimum || 1;
		}
		if(quantity <= 0) {
			console.error("Requested to add <= 0 instances");
			return;
		}

		let add : ResolverState = {[sku]: quantity}
		let remove : ResolverState = {}
		if(item.contains) {
			add = item.contains.reduce((a, i) => Object.assign(a, {[i]: (a[i] || 0) + 1}), add)

			const removalCriteria = item.itemType === ItemType.Bundle ?
										(existing: Selected) => existing.category === item.categoryId
										:
										(existing: Selected) => item.validAttachPoints?.some(p => existing.attached?.includes(p))
			for(const existing of this.state.selected) {
				if(existing.accessorysku && removalCriteria(existing)) {
					console.log("Removing existing bundle/kit: " + existing.accessorysku);
					const prevGroup = this.getAccessoryItemBySku(unit, existing.accessorysku);
					if(prevGroup.id && prevGroup.contains && prevGroup.selected) {
						remove[prevGroup.id] = prevGroup.selected
						remove = Object.assign(remove, this.getResolverState(prevGroup.contains))
					}
				}
			}
			this.simplifyResolverStates(add, remove)
		}

		const solutions = this.resolveBreadthFirst(unit, add, remove)

		const autoApply = this.findSolutionToAutoApply(unit, add, remove, solutions);
		if(autoApply) {
			this.applySolution(Action.AddedAccessory, item, autoApply);
		}
		else {
			const unitIds = Array.from(new Set(solutions.map(s => s.targetUnitId ?? this.state.unitsku)))
			const includedAccessories = unitIds.reduce((collection, unitId) => {collection[unitId] = this.getIncludedAccessories(this.getUnit(unitId)); return collection}, {} as {[sku: string]: V2.Accessory[]})
			this.onConflict(this.generateConflictDetails(Action.AddedAccessory, sku, solutions, includedAccessories))
		}
	}

	private removeAccessoryBreadthFirst(sku: string, quantity?: number) {
		const unit = this.getUnit();
		const item = this.getAccessoryItemBySku(unit, sku);
		
		if(quantity === undefined) {
			quantity = item.minimum || 1;
		}
		if((item.locked ?? 0) >= (item.selected ?? 0)) {
			return;
		}
		quantity = Math.min(quantity, (item.selected ?? 0) - (item.locked ?? 0));
		if(quantity <= 0) {
			console.error("Requested to add <= 0 instances");
			return;
		}

		let remove = {[sku]: quantity}
		if(item.contains) {
			//If kit or bundle, convert fake sku into real skus
			remove = item.contains.reduce((a, i) => Object.assign(a, {[i]: (a[i] || 0) + 1}), remove)
		}

		const solutions = this.resolveBreadthFirst(unit, {} , remove);

		const autoApply = this.findSolutionToAutoApply(unit, {}, remove, solutions);
		if(autoApply) {
			this.applySolution(Action.RemovedAccessory, item, autoApply);
		}
		else {
			const unitIds = Array.from(new Set(solutions.map(s => s.targetUnitId ?? this.state.unitsku)))
			const includedAccessories = unitIds.reduce((collection, unitId) => {collection[unitId] = this.getIncludedAccessories(this.getUnit(unitId)); return collection}, {} as {[sku: string]: V2.Accessory[]})
			this.onConflict(this.generateConflictDetails(Action.RemovedAccessory, sku, solutions, includedAccessories))
		}
	}

	private removeAccessoryDepthFirst(sku: string, quantity?: number) {
		if(quantity === undefined) {
			quantity = 1;
		}
		if(quantity <= 0) {
			console.error("Requested to remove <= 0 instances");
			return;
		}

		const unit = this.getUnit();
		const item = this.getAccessoryItemBySku(unit, sku);
		
		quantity = Math.min(quantity, (item.selected || 0) - (item.locked || 0));
		
		if(quantity <= 0) {
			console.warn("No unlocked instances to remove");
			return;
		}

		const state = this.getCurrentState();
		let solutions = this.resolveRemove(unit, state, sku, quantity, [], [], undefined, false);
		if(solutions === undefined) {
			console.error("Failed to remove " + sku + ". No solution to conflict.", state);
			this.onConflict(this.generateConflictDetails(Action.RemovedAccessory, sku, []))
			return;
		}
		
		
		solutions = this.resolveExisting(unit, state, solutions);
		if(Object.keys(solutions).length === 0) {
			console.error("Failed to add " + sku + ". No solution to conflict.", state);
			this.onConflict(this.generateConflictDetails(Action.RemovedAccessory, sku, []))
			return;
		}

		const filteredSolutions = Object.values(this.filterSolutions(solutions));

		const autoApply = this.findSolutionToAutoApply(unit, {}, {[sku]: quantity}, filteredSolutions);
		if(autoApply) {
			this.applySolution(Action.RemovedAccessory, item, autoApply);
		}
		else {
			this.onConflict(this.generateConflictDetails(Action.RemovedAccessory, sku, filteredSolutions))
		}
	}

	private toggleAccessory(sku: string) {
		if(this.state.selected.find(a => a.accessorysku == sku)) {
			this.removeAccessory(sku);
		}
		else {
			this.addAccessory(sku);
		}
	}

	private attachAccessory(sku: string, location?: string) {
		const attachItem = this.state.selected.find(s => s.accessorysku === sku);
		if(!attachItem) {
			throw new Error("Cannot attach an accessory that isn't added");
		}
		const unit = this.getUnit();
		const acc = this.getAccessoryItemBySku(unit, sku);
		if(!acc.validAttachPoints) {
			console.warn("No valid attach points for " + acc.name + " : "+ sku)
			return;
		}

		let selected = [...this.state.selected];

		if(!location || !(attachItem.attached && attachItem.attached.includes(location))) {
			if(location) {
				if(!acc.validAttachPoints.includes(location)) {
					throw new Error("Cannot attach accessory to the requested attach point")
				}
			}
			else {
				//Look for a free valid attach point
				const potentialAttachPoints = acc.validAttachPoints.filter(ap => !attachItem.attached || !attachItem.attached.includes(ap))
				if(potentialAttachPoints.length > 0) {
					for(const ap of potentialAttachPoints) {
						if(!this.state.selected.find(s => s.attached && s.attached.includes(ap))) {
							location = ap;
							break;
						}
					}
					if(!location) {
						location = potentialAttachPoints[0]
					}
				}
			}


			const detachItem = this.state.selected.find(s => location && s.attached && s.attached.includes(location));
			if(location) {
				selected = this.state.selected.map(s => {
					if(detachItem && s.accessorysku === detachItem.accessorysku && s.attached && location) {
						return {
							...s,
							attached: s.attached.filter(ap => ap !== location)
						}
					}
					else if(attachItem && s.accessorysku === attachItem.accessorysku && location) {
						return {
							...s,
							attached: [...(s.attached || []), location]
						}
					} 
					return s;
				})
			}

			selected = this.resolveAttachPoints(unit, selected, [sku], detachItem && detachItem.accessorysku ? [detachItem.accessorysku] : []);
		}

		//Ensure the newly selected part appears last in the array to match selecting items in deltagen
		const currPos = selected.findIndex(s => s.accessorysku === sku)
		if(currPos !== selected.length - 1) {
			const move = selected.splice(currPos, 1);
			selected.push(move[0]);
		}
		
		this.dispatchUpdate(
			{
				...this.state,
				modified: new Date().toISOString(),
				selected
			}
		)
	}

	private detachAccessory(sku: string, location?: string) {
		const detachItem = this.state.selected.find(s => s.accessorysku === sku);
		if(!detachItem) {
			throw new Error("Cannot detach an accessory that isn't added");
		}

		if(!detachItem.attached || detachItem.attached.length === 0) { 
			return; //Already not attached
		}

		let freedUpAttachPoints : string[] = []
		if(location) {
			if(!detachItem.attached.includes(location)) {
				return; //Already not attached at the specified location
			}
			freedUpAttachPoints.push(location)
		}
		else {
			freedUpAttachPoints = detachItem.attached;
		}

		const unit = this.getUnit();
		
		this.dispatchUpdate(
			{
				...this.state,
				modified: new Date().toISOString(),
				selected: this.state.selected.map(s => {
					if(detachItem && s.accessorysku === detachItem.accessorysku && s.attached) {
						return {
							...s,
							attached: location ? s.attached.filter(ap => ap !== location) : []
						}
					}
					else if(freedUpAttachPoints.length > 0 && s.accessorysku) {
						const acc = this.getAccessoryItemBySku(unit, s.accessorysku);
						if(acc && acc.validAttachPoints) {
							const aps = acc.validAttachPoints.filter(ap => freedUpAttachPoints.includes(ap) && (!s.attached || !s.attached.includes(ap)));
							if(aps.length > 0) {
								freedUpAttachPoints = freedUpAttachPoints.filter(ap => !aps.includes(ap));
								return {
									...s,
									attached: [...(s.attached || []), ...aps]
								}
							}
						}
					} 
					return s;
				})
			}
		)
	}

	getOptimisationLevel() {
		return this.config?.optimise ? 1 : 0;
	}
}
 
export class SessionV2 implements ISession {
	readonly version: number;
	id: string;
	userId: string;
	server: string;
	brand: string;
	country: string;
	locale: string;
	region: string;
	currency: string;
	zlservice: string;
	authFunc: GetAuthFunc

	constructor(userId: string, server: string, brand: string, country: string, locale: string, region: string, currency: string, zlservice: string = "stage", authFunc: GetAuthFunc = undefined) {
		this.version = 2;
		this.server = server;
		this.userId = userId;
		this.brand = brand;
		this.country = country;
		this.locale = locale.replace("_", "-");
		this.region = region;
		this.currency = currency;
		this.zlservice = zlservice;
		this.authFunc = authFunc
		this.id = uuid();
	}

	async requestBuilds() : Promise<ManagableSaveData[]> {
		if(window.parent !== window) {
			try {
				return (await this.queryParentPage<SaveData[]>("brp_list_builds", "brp_build_list", 150)).map(s => makeManagable(s));
			}
			catch(e) {
				console.error("Failed to query builds from parent page (expected brp_build_list message in response to brp_list_builds), falling back to local storage but that won't work on iOS/OSX. " + e)
			}
		}

		const builds : ManagableSaveData[] = []
		try {
			for(let index = 0; index < window.localStorage.length; ++index) {
				const key = localStorage.key(index);
				if(key?.startsWith("SavedBuild.")) {
					const data = localStorage.getItem(key);
					if(data) {
						builds.push(makeManagable(JSON.parse(data) as SaveData));
					}
				}
			}
		}
		catch {
			console.log("local storage unavailable for saved builds")
		}
		return builds;
	}

	async queryParentPage<T>(request: string, response: string, timeout: number) {
		return await new Promise((resolve: (data: T) => void, reject) => {
			let handle : any = undefined;
			let listener : any = undefined;
			
			handle = setTimeout(() => {
				window.removeEventListener("message", listener);
				reject("Timeout");
			}, timeout)

			listener = (ev:any) => {

				//if(ev.origin !== document.referrer) {
				//	return;
				//}

				let data = undefined;
				try {
					const message = JSON.parse(ev.data);
					if(message.event === response) {
						data = message.params;
					}
					if (data){
						console.log("message:", data)
					}
				}
				catch (ex) {
					console.log("Failed to parse a message", ev);
				}

				if(data) {
					window.removeEventListener("message", listener);
					if(handle !== undefined) {
						clearTimeout(handle);
						resolve(data as T)
					}
				}
			}
			
			window.addEventListener("message", listener);

			//post message to parent frame to request builds
			try {
				postMessage(request);
			}
			catch (ex) {
				reject("Not embedded in an iframe" + ex);
			}
		})
	}
}
